import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import { AnimatePresence, motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import Loader from '../../Core/Components/Loader/Loader';
import {
  saveConfigurationSetting,
  getConfigurationSetting,
  testVersionControlConnection,
  testIssueManagementConnection,
  verifyDBConfig,
} from '../Services/SettingService';
import {
  resolveSectionUiSchema,
  sortSectionEntries,
} from '../settingsUiSchema';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/Modules/Core/Components/shadcn/dialog';

const SURFACE_CLASS =
  'rounded-lg border border-border/40 bg-card/30 shadow-[0_18px_48px_-34px_rgba(15,23,42,0.2)]';

const INNER_PANEL_CLASS = 'rounded-lg border border-border/40 bg-card/20';

const FIELD_PANEL_CLASS = 'rounded-lg border border-border/60 bg-background';

const INPUT_CLASS =
  'h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-[13px] text-foreground shadow-none outline-none transition focus:border-ring/70 focus:ring-[3px] focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50';

const LABEL_CLASS =
  'text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/90';

const mergeClasses = (...classes) => classes.filter(Boolean).join(' ');
const normalizeText = (value = '') =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const cleanTitle = (title = '') =>
  title
    .replace(/^AUTH\s+/i, '')
    .replace(/\s*Configuration$/i, '')
    .trim();

const isIssueManagementSectionTitle = (title = '') =>
  cleanTitle(title).toLowerCase() === 'issue management';

const getStepIcon = (index) => {
  const icons = [
    Icons.Sparkles,
    Icons.SlidersHorizontal,
    Icons.Waypoints,
    Icons.BadgeCheck,
  ];

  return icons[index] || Icons.ChevronRight;
};

const getStatusMeta = ({ totalFields = 0, completedFields = 0 } = {}) => {
  if (!totalFields || !completedFields) {
    return {
      key: 'not-configured',
      label: 'Not Configured',
      icon: Icons.XCircle,
      className:
        'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/15',
    };
  }

  if (completedFields >= totalFields) {
    return {
      key: 'configured',
      label: 'Configured',
      icon: Icons.CheckCircle2,
      className:
        'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/15',
    };
  }

  return {
    key: 'partial',
    label: 'Partial',
    icon: Icons.AlertTriangle,
    className:
      'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/15',
  };
};

const getKeywordIcon = (title = '', fallbackIcon) => {
  const normalized = title.toLowerCase();

  if (normalized.includes('issue') || normalized.includes('ticket')) {
    return Icons.Bug;
  }

  if (
    normalized.includes('version') ||
    normalized.includes('git') ||
    normalized.includes('branch')
  ) {
    return Icons.GitBranch;
  }

  if (normalized.includes('rollout') || normalized.includes('package')) {
    return Icons.Package;
  }

  if (normalized.includes('system')) {
    return Icons.Settings2;
  }

  if (normalized.includes('auth')) {
    return Icons.ShieldCheck;
  }

  if (normalized.includes('database')) {
    return Icons.Database;
  }

  if (normalized.includes('yaml')) {
    return Icons.Files;
  }

  if (normalized.includes('webhook')) {
    return Icons.Send;
  }

  return fallbackIcon;
};

const getOptionIcon = (option = '', fieldName = '') => {
  const normalized = option.toLowerCase();
  const normalizedField = fieldName.toLowerCase();

  if (normalizedField.includes('os platform')) {
    if (normalized.includes('linux')) {
      return Icons.Terminal;
    }

    if (normalized.includes('windows')) {
      return Icons.Monitor;
    }

    if (normalized.includes('mac') || normalized.includes('osx')) {
      return Icons.Laptop;
    }

    return Icons.Server;
  }

  if (normalizedField.includes('download platform')) {
    if (
      normalized.includes('tar') ||
      normalized.includes('zip') ||
      normalized.includes('gzip') ||
      normalized.includes('tgz')
    ) {
      return Icons.Archive;
    }

    if (normalized.includes('rpm') || normalized.includes('deb')) {
      return Icons.Package;
    }

    return Icons.Download;
  }

  if (normalized.includes('bugzilla')) {
    return Icons.Bug;
  }

  if (normalized.includes('jira')) {
    return Icons.Ticket;
  }

  if (normalized.includes('azure')) {
    return Icons.Cloud;
  }

  if (normalized.includes('github')) {
    return Icons.Github;
  }

  if (normalized.includes('gitlab')) {
    return Icons.GitBranch;
  }

  if (normalized.includes('bitbucket')) {
    return Icons.GitFork;
  }

  if (normalized.includes('enterprise')) {
    return Icons.Building2;
  }

  if (normalized.includes('testing') || normalized.includes('test')) {
    return Icons.FlaskConical;
  }

  if (normalized.includes('production') || normalized.includes('prod')) {
    return Icons.Rocket;
  }

  if (normalized.includes('oauth')) {
    return Icons.RefreshCw;
  }

  if (normalized.includes('token')) {
    return Icons.ShieldCheck;
  }

  if (normalized.includes('basic')) {
    return Icons.KeyRound;
  }

  if (normalizedField.includes('version') || /^v\d+/i.test(option)) {
    return Icons.Layers3;
  }

  return getKeywordIcon(option, Icons.Circle);
};

const getProviderBrandKey = (value = '') => {
  const normalized = String(value || '').toLowerCase();

  if (normalized === 'git' || normalized.includes(' git ')) {
    return 'git';
  }

  if (normalized.includes('svn') || normalized.includes('subversion')) {
    return 'svn';
  }

  if (normalized.includes('jira')) {
    return 'jira';
  }

  if (normalized.includes('bugzilla')) {
    return 'bugzilla';
  }

  if (normalized.includes('azure')) {
    return 'azure';
  }

  if (normalized.includes('github')) {
    return 'github';
  }

  if (normalized.includes('bitbucket')) {
    return 'bitbucket';
  }

  if (normalized.includes('gitlab')) {
    return 'gitlab';
  }

  return '';
};

const getProviderOptionDescription = (option = '') => {
  const normalized = String(option || '').toLowerCase();

  if (normalized === 'git' || normalized.includes('git')) {
    return 'Distributed version control';
  }

  if (normalized.includes('svn') || normalized.includes('subversion')) {
    return 'Centralized version control';
  }

  if (normalized.includes('jira')) {
    return 'Atlassian issue tracking';
  }

  if (normalized.includes('bugzilla')) {
    return 'Mozilla bug tracker';
  }

  if (normalized.includes('azure')) {
    return 'Microsoft DevOps services';
  }

  return '';
};

const getVersionControlProviderDescription = (option = '') => {
  const normalized = String(option || '').toLowerCase();

  if (normalized.includes('github')) {
    return "The world's largest code hosting platform.";
  }

  if (normalized.includes('bitbucket')) {
    return "Atlassian's Git solution with Jira integration.";
  }

  if (normalized.includes('azure')) {
    return "Microsoft's end-to-end DevOps platform.";
  }

  if (normalized.includes('gitlab')) {
    return 'Complete DevOps platform, cloud or self-hosted.';
  }

  if (normalized.includes('apache')) {
    return 'Official Apache Subversion server.';
  }

  if (normalized.includes('visualsvn') || normalized.includes('visual svn')) {
    return 'Windows-based Subversion server.';
  }

  return getProviderOptionDescription(option);
};

const getPlatformOptionLabel = (option = '') => {
  const normalized = String(option || '')
    .trim()
    .toLowerCase();

  if (normalized === 'linux') {
    return 'Linux';
  }

  if (normalized === 'windows') {
    return 'Windows';
  }

  if (normalized === 'mac' || normalized === 'macos' || normalized === 'osx') {
    return 'macOS';
  }

  if (normalized === 'tar') {
    return 'TAR';
  }

  if (normalized === 'zip') {
    return 'ZIP';
  }

  if (normalized === 'rpm') {
    return 'RPM';
  }

  if (normalized === 'deb') {
    return 'DEB';
  }

  return option;
};

const getPlatformOptionDescription = (fieldName = '', option = '') => {
  const normalizedField = normalizeText(fieldName);
  const normalizedOption = String(option || '')
    .trim()
    .toLowerCase();

  if (normalizedField.includes('os platform')) {
    if (normalizedOption === 'linux') {
      return 'Linux server and container environments.';
    }

    if (normalizedOption === 'windows') {
      return 'Windows-based rollout and installation targets.';
    }

    if (
      normalizedOption === 'mac' ||
      normalizedOption === 'macos' ||
      normalizedOption === 'osx'
    ) {
      return 'macOS development and workstation targets.';
    }
  }

  if (normalizedField.includes('download platform')) {
    if (normalizedOption === 'tar') {
      return 'Compressed tarball package for Unix-like systems.';
    }

    if (normalizedOption === 'zip') {
      return 'ZIP archive for broad cross-platform distribution.';
    }

    if (normalizedOption === 'rpm') {
      return 'RPM package format for Red Hat-based systems.';
    }

    if (normalizedOption === 'deb') {
      return 'DEB package format for Debian-based systems.';
    }
  }

  return '';
};

const getSystemChoiceCardMeta = (fieldName = '', option = '') => {
  const normalizedField = normalizeText(fieldName);
  const normalizedOption = normalizeText(option);

  if (normalizedField.includes('os platform')) {
    if (normalizedOption === 'linux') {
      return {
        label: 'Linux',
        description: 'Server and container environments',
        tileClassName:
          'border border-[#8f86ff]/18 bg-[#8f86ff]/10 text-[#b2a8ff]',
        iconType: 'linux',
      };
    }

    if (normalizedOption === 'windows') {
      return {
        label: 'Windows',
        description: 'Windows-based rollout targets',
        tileClassName:
          'border border-[#58a6ff]/18 bg-[#58a6ff]/10 text-[#74b9ff]',
        iconType: 'windows',
      };
    }
  }

  if (normalizedField.includes('download platform')) {
    if (normalizedOption === 'tar') {
      return {
        label: 'TAR',
        description: 'Compressed tarball for Unix-like systems',
        tileClassName:
          'border border-[#f1b45a]/18 bg-[#f1b45a]/10 text-[#f2c781]',
        iconType: 'tar',
      };
    }

    if (normalizedOption === 'zip') {
      return {
        label: 'ZIP',
        description: 'Cross-platform archive distribution',
        tileClassName:
          'border border-[#7fcf6a]/18 bg-[#7fcf6a]/10 text-[#a8de85]',
        iconType: 'zip',
      };
    }
  }

  return {
    label: getPlatformOptionLabel(option),
    description: getPlatformOptionDescription(fieldName, option),
    tileClassName: 'border border-primary/15 bg-primary/10 text-primary',
    iconType: 'default',
  };
};

const getProviderDisplayName = (option = '') => {
  const normalized = String(option || '')
    .trim()
    .toLowerCase();

  if (normalized === 'git') {
    return 'Git';
  }

  if (normalized === 'svn' || normalized === 'subversion') {
    return 'SVN';
  }

  if (normalized.includes('github')) {
    return 'GitHub';
  }

  if (normalized.includes('bitbucket')) {
    return 'Bitbucket';
  }

  if (normalized.includes('gitlab')) {
    return 'GitLab';
  }

  if (normalized.includes('azure')) {
    return 'Azure DevOps';
  }

  if (normalized.includes('bugzilla')) {
    return 'Bugzilla';
  }

  if (normalized.includes('jira')) {
    return 'Jira';
  }

  if (normalized.includes('visualsvn') || normalized.includes('visual svn')) {
    return 'VisualSVN';
  }

  if (normalized.includes('apache')) {
    return 'Apache SVN';
  }

  return cleanTitle(option);
};

const getAuthProviderMeta = (authKey = '', fallbackTitle = '') => {
  switch (String(authKey || '').toUpperCase()) {
    case 'AUTH_DATABASE':
      return {
        name: 'Database',
        description:
          'Authenticate users against your internal database credentials.',
        brand: 'database',
      };
    case 'AUTH_AZURE':
      return {
        name: 'Microsoft Azure',
        description:
          'Single sign-on via Azure Active Directory for Microsoft tenants.',
        brand: 'azure',
      };
    case 'AUTH_GOOGLE':
      return {
        name: 'Google',
        description:
          'Let users sign in with their Google Workspace or Gmail accounts.',
        brand: 'google',
      };
    case 'AUTH_OKTA':
      return {
        name: 'Okta',
        description:
          'Enterprise identity provider with advanced security policies.',
        brand: 'okta',
      };
    case 'AUTH_OPENID':
      return {
        name: 'OpenID Connect',
        description:
          'Generic OIDC provider for custom or third-party identity services.',
        brand: 'openid',
      };
    case 'AUTH_LDAP':
      return {
        name: 'LDAP',
        description:
          'Connect to your company directory via LDAP or Active Directory.',
        brand: 'ldap',
      };
    default:
      return {
        name: fallbackTitle || cleanTitle(authKey),
        description: 'Configure authentication settings for this provider.',
        brand: 'default',
      };
  }
};

const getAuthProviderStatusMeta = (enabled) => ({
  label: enabled ? 'Enabled' : 'Not configured',
  icon: enabled ? Icons.Check : Icons.Circle,
  className: enabled
    ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    : 'border-border/60 bg-muted/55 text-muted-foreground',
});

const getIssueManagementApiName = (provider = '') => {
  const normalized = String(provider || '').toLowerCase();

  if (normalized.includes('jira')) {
    return 'Jira REST API';
  }

  if (normalized.includes('bugzilla')) {
    return 'Bugzilla REST API';
  }

  if (normalized.includes('azure')) {
    return 'Azure DevOps API';
  }

  return 'Provider API';
};

const getSectionProviderPrompt = (sectionTitle = '') => {
  const normalized = cleanTitle(sectionTitle).toLowerCase();

  if (normalized === 'issue management') {
    return 'Select the issue tracking system your team uses.';
  }

  if (normalized === 'version control') {
    return 'Select the version control provider your team uses.';
  }

  return `Select the ${normalized || 'service'} provider your team uses.`;
};

const getSectionProviderApiName = (sectionTitle = '', provider = '') => {
  const normalizedSection = cleanTitle(sectionTitle).toLowerCase();
  const normalizedProvider = String(provider || '').toLowerCase();

  if (normalizedSection === 'issue management') {
    return getIssueManagementApiName(provider);
  }

  if (normalizedSection === 'version control') {
    if (normalizedProvider.includes('github')) {
      return 'GitHub REST API';
    }

    if (normalizedProvider.includes('gitlab')) {
      return 'GitLab API';
    }

    if (normalizedProvider.includes('bitbucket')) {
      return 'Bitbucket API';
    }

    if (normalizedProvider.includes('azure')) {
      return 'Azure DevOps API';
    }

    return provider ? `${provider} API` : 'Version Control API';
  }

  return provider
    ? `${provider} API`
    : `${cleanTitle(sectionTitle) || 'Provider'} API`;
};

const VERSION_CONTROL_CATEGORY_OPTIONS = [
  {
    id: 'vcs',
    name: 'Version Control System',
    description: 'Connect a Git or SVN repository for source code tracking.',
    icon: Icons.GitBranch,
  },
  {
    id: 'other',
    name: 'Other Integration',
    description: 'Connect other tools or services to this environment.',
    icon: Icons.LayoutGrid,
  },
];

const DEFAULT_DATE_FORMAT_OPTIONS = [
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'DD-MM-YYYY',
  'DD.MM.YYYY',
];
const GROUP_ROLLOUTS_FIELD_KEY = 'GROUP_ROLLOUTS';
const SYSTEM_GROUP_ROLLOUTS_FIELD = {
  type: 'checkbox',
  name: 'Group Rollouts',
  suggestion_text: 'Enable or disable grouping of rollouts.',
};

const DEFAULT_ROLLOUT_ENVIRONMENTS = [
  '2023',
  '2024',
  'Production',
  'Staging',
];
const ROLLOUT_PACKAGE_ENVIRONMENTS_KEY =
  'MOCA_ENVIRONEMTNS';
const LEGACY_ROLLOUT_PACKAGE_ENVIRONMENTS_KEY =
  'ROLLOUT_PACKAGE_ENVIRONMENT_VERSIONS';

const createRolloutEnvironmentId = (name = '') => {
  const normalized = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `rollout-env-${normalized || 'item'}`;
};

const createRolloutVersionDraft = (value = {}) => ({
  major: String(value?.major || ''),
  minor: String(value?.minor || ''),
});

const getPreferredRolloutPackageEnvironmentId = (environments = []) =>
  environments.find(
    (environment) => environment.name.toLowerCase() === 'production'
  )?.id ||
  environments[0]?.id ||
  '';

const resolveRolloutPackageDefaultEnvironmentId = (
  environments = [],
  defaultId = ''
) =>
  environments.some((environment) => environment.id === defaultId)
    ? defaultId
    : getPreferredRolloutPackageEnvironmentId(environments);

const getDefaultRolloutPackageEnvironments = () =>
  DEFAULT_ROLLOUT_ENVIRONMENTS.map((name) => ({
    id: createRolloutEnvironmentId(name),
    name,
  }));

const getRolloutPackageEnvironments = (value) => {
  if (value === undefined) {
    return getDefaultRolloutPackageEnvironments();
  }

  const normalized = Array.isArray(value)
    ? value.reduce((items, environment, index) => {
        const name = String(environment?.name || '').trim();

        if (!name) {
          return items;
        }

        items.push({
          id:
            String(environment?.id || '').trim() ||
            `${createRolloutEnvironmentId(name)}-${index}`,
          name,
        });

        return items;
      }, [])
    : [];

  if (Array.isArray(value)) {
    return normalized;
  }

  return getDefaultRolloutPackageEnvironments();
};

const getRolloutPackagePersistedEnvironmentEntries = (value = []) =>
  Array.isArray(value)
    ? value.reduce((items, environment, index) => {
        const name = String(
          environment?.name || environment?.environment || ''
        ).trim();

        if (!name) {
          return items;
        }

        items.push({
          id:
            String(environment?.id || '').trim() ||
            `${createRolloutEnvironmentId(name)}-${index}`,
          name,
          isDefault: Boolean(
            environment?.isDefault ??
              environment?.default ??
              environment?.is_default
          ),
          major:
            environment?.majorVersion ??
            environment?.major ??
            environment?.major_version ??
            '',
          minor:
            environment?.minorVersion ??
            environment?.minor ??
            environment?.minor_version ??
            '',
        });

        return items;
      }, [])
    : [];

const normalizeRolloutPackageVersionState = (value = {}) => {
  if (Array.isArray(value)) {
    const persistedEntries = getRolloutPackagePersistedEnvironmentEntries(value);
    const environments = persistedEntries.length
      ? persistedEntries.map(({ id, name }) => ({ id, name }))
      : getRolloutPackageEnvironments(value);
    const defaultId = resolveRolloutPackageDefaultEnvironmentId(
      environments,
      persistedEntries.find((environment) => environment.isDefault)?.id || ''
    );
    const versions = {};
    const drafts = {};

    persistedEntries.forEach((environment) => {
      const versionDraft = createRolloutVersionDraft({
        major: environment.major,
        minor: environment.minor,
      });

      if (versionDraft.major || versionDraft.minor) {
        versions[environment.id] = versionDraft;
        drafts[environment.id] = versionDraft;
      }
    });

    const selectedId = defaultId;

    return {
      environments,
      defaultId,
      selectedId,
      newEnvironment: '',
      versions,
      drafts,
    };
  }

  const environments = getRolloutPackageEnvironments(value?.environments);
  const defaultId = resolveRolloutPackageDefaultEnvironmentId(
    environments,
    String(value?.defaultId || '').trim()
  );
  const selectedId = environments.some(
    (environment) => environment.id === value?.selectedId
  )
    ? value.selectedId
    : defaultId || environments[0]?.id || '';
  const savedVersions =
    value?.versions && typeof value.versions === 'object' ? value.versions : {};
  const draftVersions =
    value?.drafts && typeof value.drafts === 'object' ? value.drafts : {};
  const versions = {};
  const drafts = {};

  environments.forEach((environment) => {
    const savedVersion = createRolloutVersionDraft(
      savedVersions[environment.id] || {}
    );
    const draftVersion = createRolloutVersionDraft(
      draftVersions[environment.id] || savedVersion
    );

    if (savedVersion.major || savedVersion.minor) {
      versions[environment.id] = savedVersion;
    }

    if (
      draftVersion.major ||
      draftVersion.minor ||
      draftVersions[environment.id] ||
      savedVersions[environment.id]
    ) {
      drafts[environment.id] = draftVersion;
    }
  });

  return {
    environments,
    defaultId,
    selectedId,
    newEnvironment: String(value?.newEnvironment || ''),
    versions,
    drafts,
  };
};

const buildPersistedRolloutPackageVersionState = (value = {}) => {
  const normalized = normalizeRolloutPackageVersionState(value);
  const versions = {};

  normalized.environments.forEach((environment) => {
    const draftVersion = normalized.drafts?.[environment.id];
    const sourceVersion =
      draftVersion !== undefined
        ? draftVersion
        : normalized.versions?.[environment.id] || {};
    const nextVersion = createRolloutVersionDraft(sourceVersion);

    if (nextVersion.major || nextVersion.minor) {
      versions[environment.id] = nextVersion;
    }
  });

  return {
    environments: normalized.environments,
    defaultId: normalized.defaultId,
    selectedId: normalized.selectedId,
    newEnvironment: '',
    versions,
    drafts: Object.fromEntries(
      Object.entries(versions).map(([environmentId, version]) => [
        environmentId,
        createRolloutVersionDraft(version),
      ])
    ),
  };
};

const buildRolloutPackageEnvironmentPayload = (value = {}) => {
  const normalized = buildPersistedRolloutPackageVersionState(value);

  return normalized.environments.map((environment) => {
    const version =
      normalized.drafts?.[environment.id] ||
      normalized.versions?.[environment.id] ||
      createRolloutVersionDraft();

    return {
      environment: environment.name,
      isDefault: environment.id === normalized.defaultId,
      majorVersion: String(version.major || ''),
      minorVersion: String(version.minor || ''),
    };
  });
};

const getDefaultRolloutPackageVersionDraft = () => {
  const environments = getDefaultRolloutPackageEnvironments();
  const preferredSelection =
    environments.find(
      (environment) => environment.name.toLowerCase() === 'production'
    ) || environments[0];

  return {
    environments,
    defaultId: preferredSelection?.id || '',
    selectedId: preferredSelection?.id || '',
    newEnvironment: '',
    versions: {},
    drafts: {},
  };
};

const stripUrlProtocol = (value = '') =>
  String(value || '').replace(/^https?:\/\//i, '');

const buildHttpsUrl = (value = '') => {
  const normalized = stripUrlProtocol(value).trim();
  return normalized ? `https://${normalized}` : '';
};

const getInitials = (value = '') => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return 'NA';
  }

  const segments = normalized
    .split(/[@.\s_-]+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (!segments.length) {
    return normalized.slice(0, 2).toUpperCase();
  }

  return segments
    .slice(0, 2)
    .map((segment) => segment[0]?.toUpperCase() || '')
    .join('');
};

const humanizeFieldKey = (fieldKey = '') =>
  String(fieldKey || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const ProviderBrandLogo = ({ option, className = '' }) => {
  const brand = getProviderBrandKey(option);

  if (brand === 'git') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 52 52" className="h-8 w-8" aria-hidden="true">
          <rect width="52" height="52" rx="13" fill="#FFFFFF" />
          <g transform="translate(26 26) rotate(45) translate(-26 -26)">
            <rect x="10" y="10" width="32" height="32" rx="8" fill="#F05133" />
            <circle cx="22" cy="22" r="3.3" fill="#FFFFFF" />
            <circle cx="31" cy="31" r="3.3" fill="#FFFFFF" />
            <circle cx="31" cy="18" r="3.3" fill="#FFFFFF" />
            <path
              d="M22 22h9v9"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 22v-4h9"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      </span>
    );
  }

  if (brand === 'svn') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 52 52" className="h-8 w-8" aria-hidden="true">
          <rect width="52" height="52" rx="13" fill="#FFFFFF" />
          <path d="M10 34 22.5 13h7L17 39h-7Z" fill="#6DA544" />
          <path d="M22.5 13h19L29 39H17Z" fill="#2F6FB6" />
          <path d="M30 18h7.5L26 34h-7.5Z" fill="#FFFFFF" opacity=".72" />
        </svg>
      </span>
    );
  }

  if (brand === 'jira') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 52 52" className="h-8 w-8" aria-hidden="true">
          <defs>
            <linearGradient
              id="jiraTopGradient"
              x1="34"
              y1="22.6"
              x2="22.4"
              y2="34.2"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0052CC" />
              <stop offset="1" stopColor="#2684FF" />
            </linearGradient>
            <linearGradient
              id="jiraBottomGradient"
              x1="18.1"
              y1="29.5"
              x2="29.7"
              y2="17.9"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0052CC" />
              <stop offset="1" stopColor="#2684FF" />
            </linearGradient>
          </defs>
          <rect width="52" height="52" rx="13" fill="#FFFFFF" />
          <path
            d="M26.15 6.5L13.5 19.15c-.4.4-.4 1.05 0 1.45l6.2 6.2 6.45-6.45 6.45 6.45 6.2-6.2c.4-.4.4-1.05 0-1.45L26.15 6.5Z"
            fill="url(#jiraTopGradient)"
          />
          <path
            d="M26.15 27.2 19.7 20.75 13.5 26.95c-.4.4-.4 1.05 0 1.45l12.65 12.65 6.45-6.45Z"
            fill="url(#jiraBottomGradient)"
          />
          <path
            d="M26.15 27.2 32.6 20.75l6.2 6.2c.4.4.4 1.05 0 1.45L26.15 41.05Z"
            fill="#2684FF"
          />
        </svg>
      </span>
    );
  }

  if (brand === 'bugzilla') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 52 52" className="h-8 w-8" aria-hidden="true">
          <rect width="52" height="52" rx="13" fill="#D22A2A" />
          <circle
            cx="26"
            cy="27"
            r="15"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            opacity=".35"
          />
          <circle
            cx="26"
            cy="27"
            r="10"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            opacity=".55"
          />
          <circle cx="26" cy="27" r="5" fill="#fff" opacity=".9" />
          <circle cx="26" cy="27" r="2" fill="#D22A2A" />
          <circle cx="19" cy="11.5" r="3" fill="#fff" opacity=".85" />
          <circle cx="33" cy="11.5" r="3" fill="#fff" opacity=".85" />
          <line
            x1="19"
            y1="14.5"
            x2="21"
            y2="19"
            stroke="#fff"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity=".7"
          />
          <line
            x1="33"
            y1="14.5"
            x2="31"
            y2="19"
            stroke="#fff"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity=".7"
          />
        </svg>
      </span>
    );
  }

  if (brand === 'azure') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 52 52" className="h-8 w-8" aria-hidden="true">
          <rect width="52" height="52" rx="13" fill="#FFFFFF" />
          <path d="M8 36.5 17.5 12 30 24.5 22 28.5Z" fill="#0078D4" />
          <path d="M17.5 12 37 8l7 8L22 28.5Z" fill="#0078D4" />
          <path d="M44 16v22L8 36.5 22 28.5Z" fill="#1B9BE4" />
        </svg>
      </span>
    );
  }

  if (brand === 'github') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 24 24" className="h-8 w-8" aria-hidden="true">
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.15-1.11-1.46-1.11-1.46-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.08.63-1.33-2.22-.25-4.56-1.11-4.56-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02A9.56 9.56 0 0112 6.8c.85.004 1.7.115 2.5.337 1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10.01 10.01 0 0022 12c0-5.52-4.48-10-10-10z"
          />
        </svg>
      </span>
    );
  }

  if (brand === 'bitbucket') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 24 24" className="h-8 w-8" aria-hidden="true">
          <rect width="24" height="24" rx="5" fill="#0052CC" />
          <path
            d="M4 6.5l2.2 11h11.6L20 6.5H4zm9 7.8H11l-.6-3.3h3.2L13 14.3z"
            fill="white"
          />
        </svg>
      </span>
    );
  }

  if (brand === 'gitlab') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg bg-transparent',
          className
        )}
      >
        <svg viewBox="0 0 24 24" className="h-8 w-8" aria-hidden="true">
          <rect width="24" height="24" rx="5" fill="#FC6D26" />
          <path d="M12 18l3-9H9l3 9z" fill="white" />
          <path d="M12 18L9 9 6.5 13l1 2 4.5 3z" fill="white" opacity=".7" />
          <path d="M12 18l3-9 2.5 4-1 2-4.5 3z" fill="white" opacity=".7" />
        </svg>
      </span>
    );
  }

  return (
    <span
      className={mergeClasses(
        'inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary',
        className
      )}
    >
      {React.createElement(getOptionIcon(option), { size: 18 })}
    </span>
  );
};

const AuthProviderLogo = ({ authKey = '', title = '', className = '' }) => {
  const { brand } = getAuthProviderMeta(authKey, title);

  if (brand === 'database') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-[#EEEDFE]',
          className
        )}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#7F77DD"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
          <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" />
        </svg>
      </span>
    );
  }

  if (brand === 'azure') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-[#E6F1FB]',
          className
        )}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 96 96"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id="authAzureGradientOne"
              x1="3.398"
              x2="25.958"
              y1="35.16"
              y2="95.325"
              gradientTransform="translate(-.008 .003)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#114a8b" />
              <stop offset="1" stopColor="#0669bc" />
            </linearGradient>
            <linearGradient
              id="authAzureGradientTwo"
              x1="41.557"
              x2="52.469"
              y1="53.175"
              y2="56.706"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopOpacity=".3" />
              <stop offset=".071" stopOpacity=".2" />
              <stop offset=".321" stopOpacity=".1" />
              <stop offset=".623" stopOpacity=".05" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="authAzureGradientThree"
              x1="34.063"
              x2="66.016"
              y1="3.175"
              y2="82.044"
              gradientTransform="translate(-.008 .003)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#3ccbf4" />
              <stop offset="1" stopColor="#2892df" />
            </linearGradient>
          </defs>
          <path
            fill="url(#authAzureGradientOne)"
            d="M33.338 6.544h26.038L33.149 82.075a4.15 4.15 0 0 1-3.933 2.825H8.038a4.15 4.15 0 0 1-3.928-5.479L26.402 9.368a4.15 4.15 0 0 1 3.933-2.825z"
          />
          <path
            fill="#0078d4"
            d="M71.175 60.261H29.971a1.91 1.91 0 0 0-1.305 3.308l26.532 24.764a4.17 4.17 0 0 0 2.846 1.121h23.38z"
          />
          <path
            fill="url(#authAzureGradientTwo)"
            d="M33.338 6.544a4.12 4.12 0 0 0-3.943 2.879L4.153 79.368a4.14 4.14 0 0 0 3.908 5.532h20.787a4.44 4.44 0 0 0 3.41-2.9l5.014-14.777 17.91 16.705a4.24 4.24 0 0 0 2.666.972H81.24L71.024 60.261l-29.781.007L59.47 6.544z"
          />
          <path
            fill="url(#authAzureGradientThree)"
            d="M69.617 9.368a4.14 4.14 0 0 0-3.927-2.825H33.648a4.14 4.14 0 0 1 3.927 2.825l22.293 70.054a4.15 4.15 0 0 1-3.928 5.478h32.042a4.15 4.15 0 0 0 3.928-5.478z"
          />
        </svg>
      </span>
    );
  }

  if (brand === 'google') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl border border-[#e8e8e8] bg-[#fafafa]',
          className
        )}
      >
        <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
          />
          <path
            fill="#34A853"
            d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
          />
          <path
            fill="#FBBC05"
            d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
          />
          <path
            fill="#EA4335"
            d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
          />
        </svg>
      </span>
    );
  }

  if (brand === 'okta') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-[#FFF1F0]',
          className
        )}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="100" cy="100" r="100" fill="#007DC1" />
          <circle cx="100" cy="100" r="54" fill="white" />
          <circle cx="100" cy="100" r="27" fill="#007DC1" />
        </svg>
      </span>
    );
  }

  if (brand === 'openid') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-[#EAF3DE]',
          className
        )}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#3B6D11"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10" />
          <path d="M12 6v6l4 2" />
          <path d="M18 15l2 2 4-4" />
        </svg>
      </span>
    );
  }

  if (brand === 'ldap') {
    return (
      <span
        className={mergeClasses(
          'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-[#FAEEDA]',
          className
        )}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#854F0B"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
          <path d="M7 8h10M7 11h6" />
        </svg>
      </span>
    );
  }

  return (
    <span
      className={mergeClasses(
        'inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-primary/10 text-primary',
        className
      )}
    >
      <Icons.ShieldCheck size={20} />
    </span>
  );
};

const SystemChoiceCardIcon = ({ type = 'default' }) => {
  if (type === 'linux') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <circle cx="12" cy="17" r=".5" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (type === 'windows') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M3 5.6L10.5 4.5V11.5H3zM11.5 4.35L21 3V11.5H11.5zM3 12.5H10.5V19.5L3 18.4zM11.5 12.5H21V21L11.5 19.65z"
        />
      </svg>
    );
  }

  if (type === 'tar') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    );
  }

  if (type === 'zip') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
        <path d="M9 3h2v2H9zM11 5h2v2H11zM9 7h2v2H9z" />
      </svg>
    );
  }

  return <Icons.CircleHelp size={24} />;
};

const ActionButton = ({
  children,
  variant = 'secondary',
  className = '',
  type = 'button',
  ...props
}) => {
  const variantClass =
    {
      primary:
        'border-transparent bg-primary text-primary-foreground hover:bg-primary/90',
      ghost:
        'border-transparent bg-transparent text-foreground hover:bg-accent',
      subtle: 'border-border/60 bg-muted/55 text-foreground hover:bg-muted',
      secondary:
        'border-border/60 bg-background text-foreground hover:bg-accent',
    }[variant] || 'border-border/60 bg-background text-foreground';

  return (
    <button
      type={type}
      className={mergeClasses(
        'inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border px-3.5 py-2 text-[13px] font-medium shadow-none transition focus:outline-none focus:ring-[3px] focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50',
        variantClass,
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

const StatusBadge = ({ status }) => {
  const StatusIcon = status.icon;

  return (
    <span
      className={mergeClasses(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em]',
        status.className
      )}
    >
      <StatusIcon size={14} />
      {status.label}
    </span>
  );
};

const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => {
      if (!disabled) {
        onChange(!checked);
      }
    }}
    className={mergeClasses(
      'relative inline-flex h-6 w-11 items-center rounded-full border-2 border-transparent transition focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-50',
      checked ? 'bg-primary' : 'bg-muted'
    )}
  >
    <span
      className={mergeClasses(
        'inline-block h-5 w-5 rounded-full bg-background shadow transition-transform',
        checked ? 'translate-x-5' : 'translate-x-0'
      )}
    />
  </button>
);

const TextInput = React.forwardRef(({ className = '', ...props }, ref) => (
  <input
    ref={ref}
    className={mergeClasses(INPUT_CLASS, className)}
    {...props}
  />
));
TextInput.displayName = 'TextInput';

const formatCountLabel = (count, singularLabel, pluralLabel = `${singularLabel}s`) =>
  `${count} ${count === 1 ? singularLabel : pluralLabel}`;

const buildYamlLoadOrderSummary = (entries = []) => {
  const countLabel = formatCountLabel(entries.length, 'reference');

  if (!entries.length) {
    return {
      badges: [countLabel],
      collapsedSummary:
        'No YAML references yet. Expand to add references and arrange processing order.',
    };
  }

  const previewEntries = entries.slice(0, 2);
  const remainderCount = entries.length - previewEntries.length;

  return {
    badges: [countLabel],
    collapsedSummary:
      remainderCount > 0
        ? `${previewEntries.join(', ')} +${remainderCount} more`
        : previewEntries.join(', '),
  };
};

const buildEnvironmentSectionSummary = (draft = {}) => {
  const environments = Array.isArray(draft?.environments)
    ? draft.environments
    : [];
  const selectedEnvironment =
    environments.find((environment) => environment.id === draft?.selectedId) ||
    environments[0] ||
    null;
  const configuredCount = environments.reduce((count, environment) => {
    const version = createRolloutVersionDraft(
      draft?.drafts?.[environment.id] || draft?.versions?.[environment.id] || {}
    );

    return version.major || version.minor ? count + 1 : count;
  }, 0);
  const badges = [formatCountLabel(environments.length, 'environment')];

  if (environments.length > 0) {
    badges.push(`${configuredCount}/${environments.length} versioned`);
  }

  if (!selectedEnvironment) {
    return {
      badges,
      collapsedSummary:
        'No environments added yet. Expand to add environments and assign versions.',
    };
  }

  const selectedVersion = createRolloutVersionDraft(
    draft?.drafts?.[selectedEnvironment.id] ||
      draft?.versions?.[selectedEnvironment.id] ||
      {}
  );
  const versionSummary =
    selectedVersion.major || selectedVersion.minor
      ? `${selectedEnvironment.name} v${selectedVersion.major || '?'}.${selectedVersion.minor || '?'}`
      : `${selectedEnvironment.name} selected`;

  return {
    badges,
    collapsedSummary: versionSummary,
  };
};

const CollapsibleSettingsSection = ({
  title,
  description,
  collapsedSummary,
  badges = [],
  icon: IconComponent = Icons.ChevronRight,
  isExpanded = false,
  onToggle,
  children,
}) => {
  const contentId = React.useId();
  const [isDarkTheme, setIsDarkTheme] = React.useState(() =>
    typeof document !== 'undefined'
      ? document.documentElement.classList.contains('dark')
      : false
  );

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const updateThemeState = () => {
      setIsDarkTheme(document.documentElement.classList.contains('dark'));
    };

    const observer = new MutationObserver(updateThemeState);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    updateThemeState();

    return () => observer.disconnect();
  }, []);

  const surfaceStyle = {
    backgroundColor: isDarkTheme ? '#211c1b' : '#f7f4f1',
    borderColor: isDarkTheme
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(17, 24, 39, 0.08)',
  };
  const subtleSurfaceStyle = {
    backgroundColor: isDarkTheme
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(255, 255, 255, 0.72)',
    borderColor: isDarkTheme
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(17, 24, 39, 0.1)',
  };
  const contentSurfaceStyle = {
    backgroundColor: isDarkTheme
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.26)',
    borderColor: isDarkTheme
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(17, 24, 39, 0.08)',
  };

  return (
    <section
      className="overflow-hidden rounded-lg border shadow-none"
      style={surfaceStyle}
    >
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={onToggle}
        className="flex w-full items-start gap-4 px-4 py-4 text-left transition hover:bg-black/[0.025] bg-card/20 focus:outline-none focus:ring-[3px] focus:ring-ring/15  sm:px-5 sm:py-5"
      >
        <span className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/12">
          <IconComponent size={18} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/90">
              {title}
            </h3>
            {badges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center rounded-lg border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                style={subtleSurfaceStyle}
              >
                {badge}
              </span>
            ))}
          </div>

          <p className="mt-1.5 text-[12px] leading-5 text-muted-foreground">
            {isExpanded ? description : collapsedSummary || description}
          </p>
        </div>

        <span
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
          style={subtleSurfaceStyle}
        >
          {isExpanded ? 'Collapse' : 'Expand'}
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="inline-flex"
          >
            <Icons.ChevronDown size={15} />
          </motion.span>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isExpanded ? (
          <motion.div
            key="content"
            id={contentId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div
              className="border-t px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-5"
              style={contentSurfaceStyle}
            >
              {children}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  );
};

const PasswordInput = ({
  value,
  onChange,
  inputClassName = '',
  buttonClassName = '',
  ...props
}) => {
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="relative">
      <TextInput
        {...props}
        value={value || ''}
        onChange={onChange}
        type={visible ? 'text' : 'password'}
        className={mergeClasses('pr-12', inputClassName)}
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className={mergeClasses(
          'absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-foreground',
          buttonClassName
        )}
      >
        {visible ? <Icons.EyeOff size={16} /> : <Icons.Eye size={16} />}
      </button>
    </div>
  );
};

const SuggestionInput = ({
  id,
  name,
  placeholder,
  value,
  onChange,
  suggestions = [],
}) => {
  const listId = React.useId();

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <TextInput
          id={id}
          name={name}
          placeholder={placeholder}
          value={value || ''}
          onChange={onChange}
          list={suggestions.length ? listId : undefined}
          className={suggestions.length ? 'pr-11' : ''}
        />
        {suggestions.length ? (
          <span className="pointer-events-none absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-muted/70 text-muted-foreground">
            <Icons.ChevronsUpDown size={15} />
          </span>
        ) : null}
      </div>
      {suggestions.length ? (
        <datalist id={listId}>
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      ) : null}
    </div>
  );
};

const SettingsSkeleton = () => (
  <div className="tw grid gap-4" aria-hidden="true">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className={mergeClasses(
            SURFACE_CLASS,
            'grid min-h-[14rem] gap-3 p-4 animate-pulse'
          )}
        >
          <div className="flex items-center justify-end gap-2">
            <span className="h-6 w-24 rounded-full bg-muted" />
          </div>
          <div className="grid grid-cols-[auto_1fr] items-center gap-3">
            <span className="h-12 w-12 rounded-xl bg-muted" />
            <span className="h-6 w-40 rounded-full bg-muted" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="h-4 w-28 rounded-full bg-muted" />
            <span className="h-4 w-10 rounded-full bg-muted" />
          </div>
          <span className="h-2 rounded-full bg-muted" />
          <div className="mt-auto flex gap-2">
            <span className="h-8 flex-1 rounded-full bg-muted" />
            <span className="h-8 flex-1 rounded-full bg-muted" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Setting = () => {
  const [loading, setLoading] = React.useState(false);
  const [configuration, setConfiguration] = React.useState({});
  const [selectedSectionKey, setSelectedSectionKey] = React.useState('');
  const [isWizardView, setIsWizardView] = React.useState(false);
  const [sectionStepIndex, setSectionStepIndex] = React.useState({});
  const [envObj, setEnvObj] = React.useState({});
  const [savedEnvObj, setSavedEnvObj] = React.useState({});
  const [authMode, setAuthMode] = React.useState({});
  const [dbVerified, setDBVerified] = React.useState(true);
  const [updateConfiguration, setUpdateConfiguration] = React.useState({});
  const [yamlOrder, setYamlOrder] = React.useState([]);
  const [yamlNewToken, setYamlNewToken] = React.useState('');
  const [dragIndex, setDragIndex] = React.useState(null);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);
  const [issueProviderDrafts, setIssueProviderDrafts] = React.useState({});
  const [sectionEditMode, setSectionEditMode] = React.useState({});
  const [expandedCompactSections, setExpandedCompactSections] = React.useState(
    {}
  );
  const [
    versionControlCategorySelections,
    setVersionControlCategorySelections,
  ] = React.useState({});
  const [rolloutPackageVersionDrafts, setRolloutPackageVersionDrafts] =
    React.useState({});
  const [deleteEnvironmentDialog, setDeleteEnvironmentDialog] = React.useState({
    open: false,
    sectionKey: '',
    environmentId: '',
    environmentName: '',
  });
  const [sectionSidebarCollapsed, setSectionSidebarCollapsed] =
    React.useState(true);
  const [authProviderConfigView, setAuthProviderConfigView] = React.useState(
    {}
  );
  const [isTestingConnection, setIsTestingConnection] = React.useState(false);
  const [feedbackDialog, setFeedbackDialog] = React.useState({
    open: false,
    tone: 'error',
    title: 'Something went wrong',
    message: '',
  });

  const isCompactSectionExpanded = React.useCallback(
    (sectionKey) => Boolean(expandedCompactSections[sectionKey]),
    [expandedCompactSections]
  );

  const toggleCompactSection = React.useCallback((sectionKey) => {
    setExpandedCompactSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  }, []);

  const tryParseJsonString = React.useCallback((value) => {
    const normalized = String(value ?? '').trim();

    if (
      !normalized ||
      (!normalized.startsWith('{') &&
        !normalized.startsWith('[') &&
        !normalized.startsWith('"'))
    ) {
      return null;
    }

    try {
      return JSON.parse(normalized);
    } catch (_error) {
      return null;
    }
  }, []);

  const collectErrorMessages = React.useCallback((value) => {
    if (value === null || value === undefined) {
      return [];
    }

    if (value instanceof Error) {
      return collectErrorMessages(value.message);
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const normalized = String(value).trim();

      if (!normalized) {
        return [];
      }

      const parsedJson = tryParseJsonString(normalized);

      if (parsedJson && parsedJson !== value) {
        const parsedMessages = collectErrorMessages(parsedJson);

        if (parsedMessages.length > 0) {
          return parsedMessages;
        }
      }

      return [normalized];
    }

    if (Array.isArray(value)) {
      return value.flatMap((item) => collectErrorMessages(item));
    }

    if (typeof value !== 'object') {
      return [];
    }

    const preferredMessages = [
      value?.status?.description,
      value?.status?.message,
      value?.payload?.description,
      value?.payload?.message,
      value?.error?.description,
      value?.error?.message,
      value?.errorMessage,
      value?.description,
      value?.message,
    ].flatMap((item) => collectErrorMessages(item));

    if (preferredMessages.length > 0) {
      return preferredMessages;
    }

    if (value?.errors && typeof value.errors === 'object') {
      return Object.values(value.errors).flatMap((item) =>
        collectErrorMessages(item)
      );
    }

    return [];
  }, [tryParseJsonString]);

  const getReadableErrorTitle = React.useCallback((error) => {
    if (error && typeof error === 'object') {
      const titleCandidates = [
        error?.status?.message,
        error?.error?.message,
        error?.payload?.message,
      ]
        .flatMap((value) => collectErrorMessages(value))
        .filter(Boolean);

      if (titleCandidates.length > 0) {
        return titleCandidates[0];
      }
    }

    if (typeof error === 'string') {
      const parsedJson = tryParseJsonString(error);

      if (parsedJson) {
        return getReadableErrorTitle(parsedJson);
      }
    }

    return 'Something went wrong';
  }, [collectErrorMessages, tryParseJsonString]);

  const getReadableErrorMessage = React.useCallback(
    (error, fallback = 'Something went wrong.') => {
      if (error && typeof error === 'object') {
        const detailedMessageSources = [
          error?.status?.description,
          error?.payload?.description,
          error?.error?.description,
          error?.errors,
          error?.description,
          error?.errorMessage,
          error?.status?.message,
          error?.payload?.message,
          error?.error?.message,
          error?.message,
        ];

        for (const source of detailedMessageSources) {
          const extracted = [...new Set(collectErrorMessages(source))].filter(
            Boolean
          );

          if (extracted.length > 0) {
            return extracted.slice(0, 3).join(' ');
          }
        }
      }

      if (typeof error === 'string') {
        const parsedJson = tryParseJsonString(error);

        if (parsedJson) {
          return getReadableErrorMessage(parsedJson, fallback);
        }
      }

      const messages = [...new Set(collectErrorMessages(error))].filter(Boolean);

      if (!messages.length) {
        return fallback;
      }

      return messages.slice(0, 3).join(' ');
    },
    [collectErrorMessages, tryParseJsonString]
  );

  const showDialogMessage = React.useCallback(
    ({ tone = 'error', title, message }) => {
      setFeedbackDialog({
        open: true,
        tone,
        title,
        message,
      });
    },
    []
  );

  const showError = (error) => {
    showDialogMessage({
      tone: 'error',
      title: getReadableErrorTitle(error),
      message: getReadableErrorMessage(error),
    });
  };

  const showSuccess = React.useCallback(
    (title, message) => {
      showDialogMessage({
        tone: 'success',
        title,
        message,
      });
    },
    [showDialogMessage]
  );

  const handleInput = (event) => {
    setFieldValue(event.target.name, event.target.value);
  };

  const ensureBuiltInSystemSettings = (data = {}) => {
    const systemSectionEntry = Object.entries(data || {}).find(
      ([sectionKey, sectionValue]) =>
        normalizeText(
          resolveSectionUiSchema(sectionKey, sectionValue)?.title ||
            sectionValue?.Properties?.Name ||
            sectionKey
        ) === 'system'
    );

    if (!systemSectionEntry) {
      return data || {};
    }

    const [systemSectionKey, systemSection] = systemSectionEntry;
    const hasGroupRolloutsField = Object.keys(systemSection || {}).some(
      (fieldKey) =>
        fieldKey !== 'Properties' &&
        isGroupRolloutsField(fieldKey, systemSection[fieldKey])
    );

    if (hasGroupRolloutsField) {
      return data || {};
    }

    return {
      ...(data || {}),
      [systemSectionKey]: {
        ...systemSection,
        [GROUP_ROLLOUTS_FIELD_KEY]: SYSTEM_GROUP_ROLLOUTS_FIELD,
      },
    };
  };

  const hydrateConfigurationState = (data, configData) => {
    const nextAuthMode = {};
    const nextEnvState = { ...(configData || {}) };

    Object.keys(data || {}).forEach((sectionKey) => {
      const section = data[sectionKey];
      const hasSubsections = Boolean(section?.Properties?.subsection);

      if (hasSubsections) {
        Object.keys(section).forEach((subSectionKey) => {
          if (subSectionKey === 'Properties') {
            return;
          }

          nextAuthMode[subSectionKey] = configData[subSectionKey] === '1';

          Object.keys(section[subSectionKey]).forEach((fieldKey) => {
            if (section[subSectionKey][fieldKey].type === 'file') {
              nextEnvState[fieldKey] = {
                file: '',
                image: configData[fieldKey],
              };
            }
          });
        });

        return;
      }

      Object.keys(section).forEach((fieldKey) => {
        if (
          fieldKey !== 'Properties' &&
          isGroupRolloutsField(fieldKey, section[fieldKey]) &&
          nextEnvState[fieldKey] === undefined
        ) {
          nextEnvState[fieldKey] = 'false';
        }

        if (fieldKey !== 'Properties' && section[fieldKey].type === 'file') {
          nextEnvState[fieldKey] = {
            file: '',
            image: configData[fieldKey],
          };
        }
      });
    });

    return { nextAuthMode, nextEnvState };
  };

  const fetchConfiguration = async () => {
    setLoading(true);

    try {
      const response = await getConfigurationSetting();
      const normalizedConfiguration = ensureBuiltInSystemSettings(
        response.data || {}
      );
      const { nextAuthMode, nextEnvState } = hydrateConfigurationState(
        normalizedConfiguration,
        response.config_data
      );

      setAuthMode(nextAuthMode);
      setEnvObj(nextEnvState);
      setSavedEnvObj(nextEnvState);
      setUpdateConfiguration({});
      setConfiguration(normalizedConfiguration);

      const firstSectionKey = Object.keys(normalizedConfiguration || {})[0];
      if (firstSectionKey) {
        setSelectedSectionKey((prev) => prev || firstSectionKey);
      }

      const cfgOrderRaw = response.config_data?.REFS_YAML_LOAD_ORDER;
      if (cfgOrderRaw) {
        try {
          const parsed = JSON.parse(cfgOrderRaw);
          setYamlOrder(Array.isArray(parsed) ? parsed : []);
        } catch (error) {
          setYamlOrder([]);
        }
      } else {
        setYamlOrder([]);
      }

      const rolloutPackageSectionKey = Object.entries(
        normalizedConfiguration || {}
      ).find(
        ([sectionKey, sectionValue]) =>
          resolveSectionUiSchema(sectionKey, sectionValue)?.kind ===
          'rollout-package-split'
      )?.[0];
      const rolloutPackageStateRaw =
        response.config_data?.[ROLLOUT_PACKAGE_ENVIRONMENTS_KEY] ||
        response.config_data?.[LEGACY_ROLLOUT_PACKAGE_ENVIRONMENTS_KEY];

      if (rolloutPackageSectionKey) {
        let nextRolloutPackageState = getDefaultRolloutPackageVersionDraft();

        if (rolloutPackageStateRaw) {
          try {
            nextRolloutPackageState = normalizeRolloutPackageVersionState(
              JSON.parse(rolloutPackageStateRaw)
            );
          } catch (_error) {
            nextRolloutPackageState = getDefaultRolloutPackageVersionDraft();
          }
        }

        setRolloutPackageVersionDrafts({
          [rolloutPackageSectionKey]: nextRolloutPackageState,
        });
      } else {
        setRolloutPackageVersionDrafts({});
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (event) => {
    event?.preventDefault?.();

    if (!dbVerified) {
      showError('Please verify Database Configuration');
      return;
    }

    setLoading(true);

    try {
      const serializedYamlOrder = JSON.stringify(yamlOrder || []);
      const isRolloutPackageSave =
        selectedSectionEntry?.uiKind === 'rollout-package-split';
      const rolloutPackageSectionKey = isRolloutPackageSave
        ? selectedSectionEntry?.key || 'rollout-package'
        : '';
      let rolloutPackageState = null;
      let rolloutPackageEnvironmentPayload = null;
      let serializedRolloutPackageState =
        envObj[ROLLOUT_PACKAGE_ENVIRONMENTS_KEY] ||
        envObj[LEGACY_ROLLOUT_PACKAGE_ENVIRONMENTS_KEY] ||
        '';
      const rolloutFieldOverrides = {};

      if (isRolloutPackageSave) {
        rolloutPackageState = buildPersistedRolloutPackageVersionState(
          rolloutPackageVersionDrafts[rolloutPackageSectionKey] ||
            getDefaultRolloutPackageVersionDraft()
        );
        rolloutPackageEnvironmentPayload =
          buildRolloutPackageEnvironmentPayload(rolloutPackageState);

        for (const environment of rolloutPackageState.environments) {
          const version = rolloutPackageState.versions?.[environment.id] || {};
          const hasAnyVersion = Boolean(version.major || version.minor);

          if (!hasAnyVersion) {
            continue;
          }

          if (!version.major || !version.minor) {
            showError(
              `${environment.name} needs both major and minor versions before saving.`
            );
            return;
          }

          const majorValidation = getSemanticVersionValidation(
            version.major,
            'mcs-component'
          );
          const minorValidation = getSemanticVersionValidation(
            version.minor,
            'rpcommon'
          );

          if (!majorValidation.valid) {
            showError(`${environment.name}: ${majorValidation.message}`);
            return;
          }

          if (!minorValidation.valid) {
            showError(`${environment.name}: ${minorValidation.message}`);
            return;
          }
        }

        serializedRolloutPackageState = JSON.stringify(
          rolloutPackageEnvironmentPayload
        );

        const defaultRolloutEnvironment =
          rolloutPackageState.environments.find(
            (environment) => environment.id === rolloutPackageState.defaultId
          ) ||
          rolloutPackageState.environments.find(
            (environment) => environment.id === rolloutPackageState.selectedId
          ) ||
          rolloutPackageState.environments[0];
        const defaultRolloutVersion = defaultRolloutEnvironment
          ? rolloutPackageState.versions?.[defaultRolloutEnvironment.id] ||
            createRolloutVersionDraft()
          : createRolloutVersionDraft();
        const rolloutFields = getSectionVisibleFields(selectedSection);
        const environmentField = rolloutFields.find(({ key, field }) =>
          isEnvironmentField(key, field)
        );
        const majorVersionField = rolloutFields.find(({ key, field }) =>
          isMajorVersionField(key, field)
        );
        const minorVersionField = rolloutFields.find(({ key, field }) =>
          isMinorVersionField(key, field)
        );

        if (environmentField?.key) {
          rolloutFieldOverrides[environmentField.key] =
            defaultRolloutEnvironment?.name || '';
        }

        if (majorVersionField?.key) {
          rolloutFieldOverrides[majorVersionField.key] =
            defaultRolloutVersion.major || '';
        }

        if (minorVersionField?.key) {
          rolloutFieldOverrides[minorVersionField.key] =
            defaultRolloutVersion.minor || '';
        }
      }

      const {
        [LEGACY_ROLLOUT_PACKAGE_ENVIRONMENTS_KEY]: _legacyRolloutPackageState,
        ...envStateWithoutLegacyRolloutPackage
      } = envObj;
      const nextSavedEnvState = {
        ...envStateWithoutLegacyRolloutPackage,
        REFS_YAML_LOAD_ORDER: serializedYamlOrder,
        ...(serializedRolloutPackageState
          ? {
              [ROLLOUT_PACKAGE_ENVIRONMENTS_KEY]:
                serializedRolloutPackageState,
            }
          : {}),
        ...rolloutFieldOverrides,
      };

      await saveConfigurationSetting({
        envVarArray: {
          ...updateConfiguration,
          REFS_YAML_LOAD_ORDER: serializedYamlOrder,
          ...(serializedRolloutPackageState
            ? {
                [ROLLOUT_PACKAGE_ENVIRONMENTS_KEY]:
                  serializedRolloutPackageState,
                [LEGACY_ROLLOUT_PACKAGE_ENVIRONMENTS_KEY]: '',
              }
            : {}),
          ...rolloutFieldOverrides,
        },
        type: 'rolloutconfig',
      });

      if (isRolloutPackageSave && rolloutPackageState) {
        setRolloutPackageVersionDrafts((current) => ({
          ...current,
          [rolloutPackageSectionKey]: rolloutPackageState,
        }));
      }

      setSavedEnvObj(nextSavedEnvState);
      setUpdateConfiguration({});

      if (selectedSectionEntry?.key) {
        setAuthProviderConfigView((prevState) => ({
          ...prevState,
          [selectedSectionEntry.key]: false,
        }));

        if (isGuidedWizardEntry(selectedSectionEntry)) {
          setSectionEditMode((prevState) => ({
            ...prevState,
            [selectedSectionEntry.key]: false,
          }));
          setIsWizardView(true);
        }
      }

      showSuccess(
        'Configuration saved',
        'Your changes have been saved successfully.'
      );
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const nextValue = {
        file,
        image: reader.result,
      };

      setUpdateConfiguration((prevState) => ({
        ...prevState,
        [event.target.name]: nextValue,
      }));

      setEnvObj((prevState) => ({
        ...prevState,
        [event.target.name]: nextValue,
      }));
    };

    reader.readAsDataURL(file);
  };

  const addYamlToken = () => {
    const token = (yamlNewToken || '').trim();

    if (!token || yamlOrder.includes(token)) {
      setYamlNewToken('');
      return;
    }

    setYamlOrder((prev) => [...prev, token]);
    setYamlNewToken('');
  };

  const deleteYamlToken = (index) => {
    setYamlOrder((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const onDragStart = (index) => {
    setDragIndex(index);
  };

  const onDragOver = (index, event) => {
    event.preventDefault();
    setDragOverIndex(index);
  };

  const onDragLeave = () => {
    setDragOverIndex(null);
  };

  const onDrop = (index) => {
    if (dragIndex === null) {
      return;
    }

    setYamlOrder((prev) => {
      const nextItems = [...prev];
      const [moved] = nextItems.splice(dragIndex, 1);
      let targetIndex = index;

      if (dragIndex < index) {
        targetIndex = index - 1;
      }

      nextItems.splice(Math.max(targetIndex, 0), 0, moved);
      return nextItems;
    });

    setDragIndex(null);
    setDragOverIndex(null);
  };

  const onDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const normalizeVisibleValues = (visibleIf) => {
    if (Array.isArray(visibleIf)) {
      return visibleIf.map((value) => String(value).trim()).filter(Boolean);
    }

    return String(visibleIf || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  };

  const normalizeDependencyValue = (value) => normalizeText(value || '');

  const matchesDependencyValue = (expectedValue, actualValue) => {
    const normalizedExpected = normalizeDependencyValue(expectedValue);
    const normalizedActual = normalizeDependencyValue(actualValue);

    if (!normalizedExpected || !normalizedActual) {
      return false;
    }

    return (
      normalizedExpected === normalizedActual ||
      normalizedActual.includes(normalizedExpected) ||
      normalizedExpected.includes(normalizedActual)
    );
  };

  const isFieldVisible = (field) => {
    if (!field) {
      return false;
    }

    if (!field.depend_on || !field.visible_if) {
      return true;
    }

    const visibleValues = normalizeVisibleValues(field.visible_if);
    const dependentValue = envObj[field.depend_on];

    return visibleValues.some((visibleValue) =>
      matchesDependencyValue(visibleValue, dependentValue)
    );
  };

  const isFieldConfigured = (fieldKey, field) => {
    if (!field) {
      return false;
    }

    if (field.type === 'yamlorder') {
      return yamlOrder.length > 0;
    }

    const value = envObj[fieldKey];

    if (field.type === 'file') {
      return Boolean(value?.image);
    }

    if (field.type === 'checkbox') {
      return (
        value === true ||
        value === false ||
        value === 'true' ||
        value === 'false'
      );
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return value !== undefined && value !== null && String(value).trim() !== '';
  };

  const getFieldRequestValue = (fieldKey, field) => {
    if (!field) {
      return '';
    }

    if (field.type === 'yamlorder') {
      return JSON.stringify(yamlOrder || []);
    }

    const value = envObj[fieldKey];

    if (field.type === 'file') {
      return value?.image || '';
    }

    if (value === undefined || value === null) {
      return '';
    }

    return value;
  };

  const getFieldSnapshotValue = (source, fieldKey, field) => {
    if (!field) {
      return '';
    }

    const value = source?.[fieldKey];

    if (field.type === 'file') {
      return value?.image || '';
    }

    if (field.type === 'yamlorder') {
      return String(value || '');
    }

    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }

    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value ?? '';
  };

  const hasSubsections = (section) => Boolean(section?.Properties?.subsection);

  const getAuthIcon = (authMethod) => {
    const iconMap = {
      AUTH_DATABASE: Icons.Database,
      AUTH_AZURE: Icons.Cloud,
      AUTH_GOOGLE: Icons.Mail,
      AUTH_OKTA: Icons.Shield,
      AUTH_OPENID: Icons.BadgeCheck,
      AUTH_LDAP: Icons.Users,
    };

    return iconMap[authMethod] || Icons.ShieldCheck;
  };

  const getSectionIcon = (title, iconName) =>
    getKeywordIcon(title, Icons[iconName] || Icons.Settings2 || Icons.Settings);

  const toggleAuthMethod = (authKey, nextValue) => {
    setAuthMode((prevState) => ({
      ...prevState,
      [authKey]: nextValue,
    }));

    setUpdateConfiguration((prevState) => ({
      ...prevState,
      [authKey]: nextValue ? '1' : '0',
    }));

    setEnvObj((prevState) => ({
      ...prevState,
      [authKey]: nextValue ? '1' : '0',
    }));
  };

  const getFieldMeta = (fieldKey, field) =>
    normalizeText(`${fieldKey} ${field?.name || ''}`);

  const matchesFieldPattern = (fieldKey, field, patterns) => {
    const fieldMeta = getFieldMeta(fieldKey, field);
    return patterns.some((pattern) => fieldMeta.includes(pattern));
  };

  const matchesCompactFieldPattern = (fieldKey, field, patterns) => {
    const compactFieldMeta = String(`${fieldKey} ${field?.name || ''}`)
      .replace(/[\s_-]+/g, '')
      .toLowerCase();

    return patterns.some((pattern) =>
      compactFieldMeta.includes(
        String(pattern || '')
          .replace(/[\s_-]+/g, '')
          .toLowerCase()
      )
    );
  };

  const getRawFieldOptions = (field) =>
    String(field?.options || '')
      .split(',')
      .map((option) => normalizeText(option))
      .filter(Boolean);

  const isProviderField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['provider']) &&
    !matchesFieldPattern(fieldKey, field, [
      'type',
      'version',
      'auth provider',
      'url',
      'uri',
      'host',
      'port',
      'endpoint',
      'server',
      'connection',
    ]);

  const isVersionField = (fieldKey, field) => {
    const fieldMeta = getFieldMeta(fieldKey, field);
    const fieldNameMeta = normalizeText(field?.name || '');
    const sanitizedMeta = fieldMeta
      .replace(/\bversion control\b/g, '')
      .replace(/\bissue management\b/g, '')
      .trim();
    const options = getRawFieldOptions(field);
    const hasVersionLikeOptions =
      options.length > 0 &&
      options.every(
        (option) =>
          /^v\d+(\.\d+)*$/i.test(option) ||
          ['latest', 'version', 'release', 'stable', 'current'].includes(option)
      );
    const explicitVersionName =
      /\bapi version\b/.test(fieldMeta) ||
      /\brelease\b/.test(fieldNameMeta || sanitizedMeta) ||
      /\btag\b/.test(fieldNameMeta || sanitizedMeta) ||
      /\bversion\b/.test(fieldNameMeta || sanitizedMeta);

    return explicitVersionName || hasVersionLikeOptions;
  };

  const isApiVersionField = (fieldKey, field) => {
    const fieldMeta = getFieldMeta(fieldKey, field);
    const options = getRawFieldOptions(field);

    return (
      /\bapi version\b/.test(fieldMeta) ||
      (options.length > 0 &&
        options.every((option) => /^v\d+(\.\d+)*$/i.test(option)))
    );
  };

  const isConnectionField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, [
      'uri',
      'url',
      'host',
      'port',
      'endpoint',
      'database',
      'sid',
      'connection',
      'server',
    ]);

  const isProjectOrAuthField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, [
      'project',
      'category',
      'auth',
      'username',
      'password',
      'token',
      'oauth',
      'client',
      'secret',
      'tenant',
      'scope',
    ]);

  const isCredentialField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, [
      'username',
      'password',
      'token',
      'secret',
      'client',
    ]);

  const isUrlField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['uri', 'url', 'endpoint']);

  const isCategoryField = (fieldKey, field) =>
    getChoiceOptions(field).length > 0 &&
    matchesFieldPattern(fieldKey, field, ['category']);

  const isPlatformVisualChoiceField = (fieldKey, field) =>
    getChoiceOptions(field).length > 0 &&
    matchesFieldPattern(fieldKey, field, ['os platform', 'download platform']);

  const isOsPlatformField = (fieldKey, field) =>
    getChoiceOptions(field).length > 0 &&
    (matchesFieldPattern(fieldKey, field, ['os platform']) ||
      matchesCompactFieldPattern(fieldKey, field, ['os platform']));

  const isDownloadPlatformField = (fieldKey, field) =>
    getChoiceOptions(field).length > 0 &&
    (matchesFieldPattern(fieldKey, field, ['download platform']) ||
      matchesCompactFieldPattern(fieldKey, field, ['download platform']));

  const hasDateFormatOptions = (field) => {
    const options = getChoiceOptions(field);

    if (!options.length) {
      return false;
    }

    return options.every((option) => {
      const normalizedOption = String(option || '')
        .trim()
        .toUpperCase();

      return (
        /^(M{1,4}|D{1,4}|Y{2,4})([\/\-.](M{1,4}|D{1,4}|Y{2,4})){2}$/.test(
          normalizedOption
        ) || ['ISO8601', 'RFC3339'].includes(normalizedOption)
      );
    });
  };

  const hasExplicitDateFormatIdentity = (fieldKey, field) => {
    const normalizedKey = String(fieldKey || '')
      .trim()
      .toUpperCase();
    const normalizedName = normalizeText(
      field?.name || field?.label || field?.title || ''
    );

    return (
      [
        'DATE_FORMAT',
        'DATEFORMAT',
        'DATE_FROMAT',
        'DATEFROMAT',
        'FORMAT_DATE',
        'DATE_PATTERN',
      ].includes(normalizedKey) ||
      [
        'date format',
        'date fromat',
        'dateformat',
        'date pattern',
      ].includes(normalizedName)
    );
  };

  const isDateFormatField = (fieldKey, field) =>
    (field?.type === 'select' ||
      field?.type === 'radio' ||
      getChoiceOptions(field).length > 0 ||
      hasDateFormatOptions(field) ||
      hasExplicitDateFormatIdentity(fieldKey, field)) &&
    (hasExplicitDateFormatIdentity(fieldKey, field) ||
      matchesFieldPattern(fieldKey, field, [
        'date format',
        'date fromat',
        'date',
      ]) ||
      matchesCompactFieldPattern(fieldKey, field, [
        'date format',
        'date fromat',
        'date',
      ]) ||
      hasDateFormatOptions(field));

  const isReleaseVersionField = (fieldKey, field) =>
    fieldKey === 'RELEASE_NOTE_FILE' ||
    ((getChoiceOptions(field).length > 0 || field?.type === 'checkbox') &&
      (matchesFieldPattern(fieldKey, field, [
        'release version',
        'release note file',
        'api version',
      ]) ||
        matchesCompactFieldPattern(fieldKey, field, [
          'release version',
          'release note file',
          'api version',
        ])));

  const isGroupRolloutsField = (fieldKey, field) => {
    const binaryOptionCount = String(field?.options || '')
      .split(',')
      .map((option) => option.trim())
      .filter(Boolean).length;

    return (
      (field?.type === 'checkbox' || binaryOptionCount === 2) &&
      (String(fieldKey || '').trim().toUpperCase() === GROUP_ROLLOUTS_FIELD_KEY ||
        matchesFieldPattern(fieldKey, field, [
          'group rollouts',
          'group rollout',
          'rollout grouping',
        ]) ||
        matchesCompactFieldPattern(fieldKey, field, [
          'group rollouts',
          'group rollout',
          'rollout grouping',
        ]))
    );
  };

  const isEnvironmentField = (fieldKey, field) =>
    (field?.type === 'select' ||
      field?.type === 'radio' ||
      field?.type === 'input' ||
      getChoiceOptions(field).length > 0) &&
    (matchesFieldPattern(fieldKey, field, ['environment']) ||
      matchesCompactFieldPattern(fieldKey, field, [
        'environment',
        'env',
      ])) &&
    !isReleaseVersionField(fieldKey, field) &&
    !isVersionField(fieldKey, field);

  const isMajorVersionField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['major version']) ||
    matchesCompactFieldPattern(fieldKey, field, [
      'major version',
      'majorver',
      'majorversion',
    ]);

  const isMinorVersionField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['minor version']) ||
    matchesCompactFieldPattern(fieldKey, field, [
      'minor version',
      'minorver',
      'minorversion',
    ]);

  const SEMANTIC_VERSION_PATTERN = /^\d+(?:\.(?:\d+|max))+$/i;

  const getSemanticVersionValidation = (value, label = 'Version') => {
    const normalized = String(value ?? '').trim();

    if (!normalized) {
      return {
        valid: false,
        tone: 'neutral',
        message: `${label} is required. Use a value like 12.2 or 12.max.max.`,
      };
    }

    if (SEMANTIC_VERSION_PATTERN.test(normalized)) {
      return {
        valid: true,
        tone: 'success',
        message: 'Format looks good.',
      };
    }

    return {
      valid: false,
      tone: 'error',
      message: 'Use dot-separated values like 12.2 or 12.max.max.',
    };
  };

  const getNextSemanticVersionSuggestion = (value, fallback = '1.0') => {
    const normalized = String(value ?? '').trim();

    if (!SEMANTIC_VERSION_PATTERN.test(normalized)) {
      return fallback;
    }

    const segments = normalized.split('.');
    const lastIndex = segments.length - 1;
    const nextLast = Number.parseInt(segments[lastIndex], 10);

    if (!Number.isFinite(nextLast)) {
      return fallback;
    }

    segments[lastIndex] = String(nextLast + 1);
    return segments.join('.');
  };

  const isRolloutNameField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['rollout name', 'package name']) ||
    matchesCompactFieldPattern(fieldKey, field, [
      'rollout name',
      'package name',
    ]);

  const isYamlOrderField = (fieldKey, field) =>
    field?.type === 'yamlorder' || fieldKey === 'REFS_YAML_LOAD_ORDER';

  const isAuthTypeField = (fieldKey, field) =>
    getChoiceOptions(field).length > 0 &&
    matchesFieldPattern(fieldKey, field, ['auth']) &&
    !matchesFieldPattern(fieldKey, field, [
      'provider',
      'username',
      'password',
      'token',
      'secret',
      'client',
      'tenant',
      'scope',
    ]);

  const isUsernameField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['username', 'email', 'user']) &&
    !getChoiceOptions(field).length;

  const isPasswordLikeField = (fieldKey, field) =>
    field?.type === 'password' ||
    (matchesFieldPattern(fieldKey, field, [
      'password',
      'token',
      'secret',
      'api key',
      'apikey',
    ]) &&
      !getChoiceOptions(field).length);

  const isSyncIntervalField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['sync interval', 'interval']) &&
    !getChoiceOptions(field).length;

  const isWorkspaceField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['workspace', 'org', 'organization']);

  const isBranchField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['default branch', 'branch']);

  const isMultiFilePathField = (fieldKey, field) =>
    matchesFieldPattern(fieldKey, field, ['multi file', 'multi-file', 'path']);

  const isProviderTypeField = (fieldKey, field) =>
    (matchesFieldPattern(fieldKey, field, ['provider type']) ||
      Boolean(field?.dependent_key)) &&
    (field?.type === 'select' || field?.type === 'radio') &&
    !isVersionField(fieldKey, field);

  const getFieldLabel = (fieldKey, field) => {
    if (isEnvironmentField(fieldKey, field)) {
      return 'Environment';
    }

    if (isMajorVersionField(fieldKey, field)) {
      return 'Major Version';
    }

    if (isMinorVersionField(fieldKey, field)) {
      return 'Minor Version';
    }

    if (isProviderField(fieldKey, field)) {
      return 'Provider';
    }

    if (isUrlField(fieldKey, field)) {
      return 'Base URI';
    }

    if (isVersionField(fieldKey, field)) {
      return 'API Version';
    }

    if (isAuthTypeField(fieldKey, field)) {
      return 'Authentication Type';
    }

    if (isProviderTypeField(fieldKey, field)) {
      return 'Provider Type';
    }

    if (isUsernameField(fieldKey, field)) {
      return 'Username';
    }

    if (isCategoryField(fieldKey, field)) {
      return 'Project Category';
    }

    if (isSyncIntervalField(fieldKey, field)) {
      return 'Sync Interval';
    }

    if (isGroupRolloutsField(fieldKey, field)) {
      return 'Group Rollouts';
    }

    if (field?.type === 'yamlorder' || fieldKey === 'REFS_YAML_LOAD_ORDER') {
      return 'YAML Load Order';
    }

    return field?.name || humanizeFieldKey(fieldKey);
  };

  const getFieldHelperText = (fieldKey, field, fallback = '') => {
    if (isEnvironmentField(fieldKey, field)) {
      return 'Choose the target environment before configuring versions.';
    }

    if (isMajorVersionField(fieldKey, field)) {
      return 'Use a dot-separated version such as 12.2, 15.0, or 12.max.max.';
    }

    if (isMinorVersionField(fieldKey, field)) {
      return 'Use a dot-separated version such as 9.0, 0.5, or 12.max.max.';
    }

    if (field?.type === 'yamlorder' || fieldKey === 'REFS_YAML_LOAD_ORDER') {
      return 'Add YAML references and drag them into the order they should be processed.';
    }

    if (isUrlField(fieldKey, field)) {
      return field?.suggestion_text || 'Enter the base URL for this service.';
    }

    if (isGroupRolloutsField(fieldKey, field)) {
      return (
        field?.suggestion_text || 'Enable or disable grouping of rollouts.'
      );
    }

    return field?.suggestion_text || fallback;
  };

  const getSelectOptions = (item) =>
    (item?.options || '')
      .split(',')
      .map((option) => option.trim())
      .filter(Boolean);

  const getDependentSelectOptions = (item) => {
    if (!item) {
      return [];
    }

    const dependentOptions = [];

    (item?.options || '').split(',').forEach((option) => {
      if (!option || !option.includes('|')) {
        return;
      }

      const [provider, values] = option.split('|');
      if (provider && values) {
        dependentOptions.push({
          provider,
          values: values.split('.').filter(Boolean),
        });
      }
    });

    const dependentValue = envObj[item?.dependent_key];
    const matchedOption = dependentOptions.find(({ provider }) =>
      matchesDependencyValue(provider, dependentValue)
    );

    return matchedOption?.values || [];
  };

  const getChoiceOptions = (item) => {
    if (!item) {
      return [];
    }

    if (item.type === 'select' && item.dependent_key) {
      return getDependentSelectOptions(item);
    }

    if (item.type === 'select' || item.type === 'radio') {
      return getSelectOptions(item);
    }

    return [];
  };

  const getDateFormatPresetOptions = (field) => {
    const optionMap = new Map();

    [...getChoiceOptions(field), ...DEFAULT_DATE_FORMAT_OPTIONS].forEach(
      (option) => {
        const normalizedOption = String(option || '').trim();

        if (!normalizedOption) {
          return;
        }

        const normalizedKey = normalizeText(normalizedOption);

        if (!optionMap.has(normalizedKey)) {
          optionMap.set(normalizedKey, normalizedOption);
        }
      }
    );

    return Array.from(optionMap.values());
  };

  const getDefaultUrlForProvider = (providerValue = '') => {
    const normalized = String(providerValue || '').toLowerCase();

    if (normalized.includes('bugzilla')) {
      return 'https://bugzilla.your-instance.com';
    }

    if (normalized.includes('jira')) {
      return 'https://your-domain.atlassian.net';
    }

    if (normalized.includes('azure')) {
      return 'https://dev.azure.com/your-organization';
    }

    if (normalized.includes('github')) {
      return 'https://api.github.com';
    }

    if (normalized.includes('gitlab')) {
      return 'https://gitlab.com/api';
    }

    if (normalized.includes('bitbucket')) {
      return 'https://api.bitbucket.org';
    }

    return '';
  };

  const getProviderValueForSection = (section) => {
    if (!section) {
      return '';
    }

    const providerFields = [];

    if (hasSubsections(section)) {
      Object.keys(section).forEach((subSectionKey) => {
        if (subSectionKey === 'Properties') {
          return;
        }

        Object.keys(section[subSectionKey] || {}).forEach((fieldKey) => {
          if (
            fieldKey !== 'Properties' &&
            isProviderField(fieldKey, section[subSectionKey][fieldKey])
          ) {
            providerFields.push(fieldKey);
          }
        });
      });
    } else {
      Object.keys(section).forEach((fieldKey) => {
        if (
          fieldKey !== 'Properties' &&
          isProviderField(fieldKey, section[fieldKey])
        ) {
          providerFields.push(fieldKey);
        }
      });
    }

    const providerFieldKey = providerFields.find((fieldKey) => {
      const value = envObj[fieldKey];
      return typeof value === 'string' && value.trim().length > 0;
    });

    return providerFieldKey ? envObj[providerFieldKey] || '' : '';
  };

  const getSectionFieldKeys = (section) => {
    if (!section) {
      return [];
    }

    if (hasSubsections(section)) {
      return Object.keys(section).flatMap((subSectionKey) => {
        if (subSectionKey === 'Properties') {
          return [];
        }

        return Object.keys(section[subSectionKey] || {}).filter(
          (fieldKey) => fieldKey !== 'Properties'
        );
      });
    }

    return Object.keys(section).filter((fieldKey) => fieldKey !== 'Properties');
  };

  const getIssueManagementDraftSnapshot = (section, source = envObj) => {
    const snapshot = {};

    getSectionFieldKeys(section).forEach((fieldKey) => {
      const field = hasSubsections(section) ? null : section?.[fieldKey];

      if (field && isProviderField(fieldKey, field)) {
        return;
      }

      snapshot[fieldKey] = source[fieldKey] ?? '';
    });

    return snapshot;
  };

  const getUrlValidation = (value) => {
    if (!value || !String(value).trim()) {
      return null;
    }

    try {
      const parsed = new URL(value);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return {
          valid: true,
          message: 'Valid URL',
        };
      }
    } catch (error) {
      return {
        valid: false,
        message: 'Invalid format',
      };
    }

    return {
      valid: false,
      message: 'Invalid format',
    };
  };

  const getIssueManagementAuthOptionMeta = (option = '') => {
    const normalized = String(option || '').toLowerCase();

    if (normalized.includes('oauth')) {
      return {
        icon: Icons.UserRound,
        iconClassName:
          'bg-emerald-500/12 text-emerald-600 dark:text-emerald-300',
      };
    }

    if (normalized.includes('basic')) {
      return {
        icon: Icons.Lock,
        iconClassName: 'bg-primary/12 text-primary',
      };
    }

    if (normalized.includes('key') || normalized.includes('token')) {
      return {
        icon: Icons.KeyRound,
        iconClassName: 'bg-amber-500/12 text-amber-600 dark:text-amber-300',
      };
    }

    return {
      icon: Icons.Circle,
      iconClassName: 'bg-muted text-muted-foreground',
    };
  };

  const getIssueManagementReviewLabel = (fieldKey, field) => {
    if (isProviderField(fieldKey, field)) {
      return 'Provider';
    }

    if (isUrlField(fieldKey, field)) {
      return 'Base URI';
    }

    if (isVersionField(fieldKey, field)) {
      return 'API Version';
    }

    if (isAuthTypeField(fieldKey, field)) {
      return 'Auth Type';
    }

    if (isUsernameField(fieldKey, field)) {
      return 'Username';
    }

    if (isCategoryField(fieldKey, field)) {
      return 'Category';
    }

    if (isSyncIntervalField(fieldKey, field)) {
      return 'Sync Interval';
    }

    return getFieldLabel(fieldKey, field);
  };

  const buildIssueManagementReviewItems = (fields = []) => {
    const orderedFields = [...fields].sort((left, right) => {
      const getRank = ({ key, field }) => {
        if (isProviderField(key, field)) return 1;
        if (isUrlField(key, field)) return 2;
        if (isVersionField(key, field)) return 3;
        if (isAuthTypeField(key, field)) return 4;
        if (isUsernameField(key, field)) return 5;
        if (isCategoryField(key, field)) return 6;
        if (isSyncIntervalField(key, field)) return 7;
        return 20;
      };

      return getRank(left) - getRank(right);
    });

    return orderedFields
      .filter(({ key, field }) => !isPasswordLikeField(key, field))
      .map(({ key, field }) => ({
        key,
        label: getIssueManagementReviewLabel(key, field),
        value: isProviderField(key, field)
          ? getProviderDisplayName(envObj[key] || '')
          : isUsernameField(key, field)
            ? envObj[key] || 'Not configured'
            : isUrlField(key, field)
              ? stripUrlProtocol(envObj[key] || '')
              : isSyncIntervalField(key, field)
                ? `${envObj[key] || '0'} minutes`
                : formatReviewValue(key, field),
        badge: isVersionField(key, field) || isAuthTypeField(key, field),
        providerBrand: isProviderField(key, field),
        mono: isUrlField(key, field) || isUsernameField(key, field),
      }));
  };

  const getSyncIntervalMeta = (fieldKey) => {
    const rawValue = Number.parseInt(envObj[fieldKey], 10);
    const value = Number.isFinite(rawValue) ? rawValue : 60;
    const min = 5;
    const max = Math.max(300, Math.ceil(value / 15) * 15);
    const step = value >= 60 ? 15 : 5;
    const progress =
      max === min
        ? 0
        : Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));

    return { value, min, max, step, progress };
  };

  const getProviderDraftStorageKey = (sectionKey, providerValue) =>
    `${sectionKey || 'section'}::${String(providerValue || '')
      .trim()
      .toLowerCase()}`;

  const setFieldValue = (fieldName, value) => {
    if (fieldName.includes('DB_')) {
      setDBVerified(false);
    }

    const isStyledConnectionSelection =
      (selectedSectionEntry?.uiKind === 'issue-management' ||
        selectedSectionEntry?.uiKind === 'version-control-wizard') &&
      selectedSection &&
      !hasSubsections(selectedSection) &&
      isProviderField(fieldName, selectedSection[fieldName]);

    if (isStyledConnectionSelection) {
      const previousProvider = getProviderValueForSection(selectedSection);
      const providerFieldKeys = getSectionFieldKeys(selectedSection).filter(
        (key) => key !== fieldName
      );

      if (previousProvider && previousProvider !== value) {
        const previousDraft = {};

        providerFieldKeys.forEach((key) => {
          previousDraft[key] = envObj[key] ?? '';
        });

        setIssueProviderDrafts((prevState) => ({
          ...prevState,
          [getProviderDraftStorageKey(
            selectedSectionEntry?.key,
            previousProvider
          )]: previousDraft,
        }));
      }

      const nextDraft =
        previousProvider !== value
          ? issueProviderDrafts[
              getProviderDraftStorageKey(selectedSectionEntry?.key, value)
            ] || {}
          : getIssueManagementDraftSnapshot(selectedSection);

      setUpdateConfiguration((prevState) => {
        const nextState = {
          ...prevState,
          [fieldName]: value,
        };

        providerFieldKeys.forEach((key) => {
          nextState[key] = nextDraft[key] ?? '';
        });

        return nextState;
      });

      setEnvObj((prevState) => {
        const nextState = {
          ...prevState,
          [fieldName]: value,
        };

        providerFieldKeys.forEach((key) => {
          nextState[key] = nextDraft[key] ?? '';
        });

        return nextState;
      });

      return;
    }

    setUpdateConfiguration((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));

    setEnvObj((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const handleConfirmEnvironmentDelete = React.useCallback(() => {
    if (!deleteEnvironmentDialog.sectionKey || !deleteEnvironmentDialog.environmentId) {
      setDeleteEnvironmentDialog({
        open: false,
        sectionKey: '',
        environmentId: '',
        environmentName: '',
      });
      return;
    }

    setRolloutPackageVersionDrafts((current) => {
      const sectionKey = deleteEnvironmentDialog.sectionKey;
      const currentDraft =
        current[sectionKey] || getDefaultRolloutPackageVersionDraft();
      const remainingEnvironments = currentDraft.environments.filter(
        (environment) => environment.id !== deleteEnvironmentDialog.environmentId
      );
      const nextSelectedEnvironment =
        currentDraft.selectedId === deleteEnvironmentDialog.environmentId
          ? remainingEnvironments[0]?.id || ''
          : currentDraft.selectedId;

      const nextVersions = { ...currentDraft.versions };
      delete nextVersions[deleteEnvironmentDialog.environmentId];

      const nextDrafts = { ...currentDraft.drafts };
      delete nextDrafts[deleteEnvironmentDialog.environmentId];

      return {
        ...current,
        [sectionKey]: {
          ...currentDraft,
          environments: remainingEnvironments,
          selectedId: nextSelectedEnvironment,
          versions: nextVersions,
          drafts: nextDrafts,
        },
      };
    });

    showSuccess(
      'Environment deleted',
      `${deleteEnvironmentDialog.environmentName} has been removed from the rollout package configuration.`
    );
    setDeleteEnvironmentDialog({
      open: false,
      sectionKey: '',
      environmentId: '',
      environmentName: '',
    });
  }, [deleteEnvironmentDialog, showSuccess]);

  const renderChoiceCards = ({
    options,
    value,
    onSelect,
    fieldName = '',
    columns = 'md:grid-cols-3',
    size = 'default',
    layout = 'default',
    getDescription = getProviderOptionDescription,
    getLabel = getProviderDisplayName,
  }) => (
    <div className={mergeClasses('grid grid-cols-1 gap-2', columns)}>
      {options.map((option) => {
        const isActive = value === option;
        const OptionIcon = getOptionIcon(option, fieldName);
        const isBrandProviderLayout = layout === 'brand-provider';
        const isImageCardLayout = layout === 'image-card';
        const isVisualChoiceLayout = layout === 'visual-choice';

        return (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={mergeClasses(
              'relative text-left transition duration-200 focus:outline-none focus:ring-[3px] focus:ring-ring/20',
              isBrandProviderLayout
                ? 'flex min-h-[170px] flex-col items-center justify-center gap-4 rounded-lg border border-border/60 bg-background/80 px-5 py-6 text-center'
                : isImageCardLayout
                  ? 'flex min-h-[170px] flex-col items-center justify-center gap-4 rounded-lg border border-border/60 bg-background/80 px-5 py-6 text-center'
                  : isVisualChoiceLayout
                    ? 'flex min-h-[150px] flex-col items-center justify-center gap-4 rounded-lg border border-border/60 bg-background/80 px-5 py-5 text-center'
                    : size === 'compact'
                      ? 'flex min-h-10 items-center gap-3 rounded-full border border-border/50 px-3.5 py-2'
                      : 'flex min-h-[76px] items-center gap-3 rounded-lg border border-border/40 bg-muted/20 px-4 py-3.5',
              isActive && isBrandProviderLayout
                ? 'border-primary bg-primary/10'
                : isActive && isImageCardLayout
                  ? 'border-primary bg-primary/10'
                  : isActive && isVisualChoiceLayout
                    ? 'border-primary bg-primary/10'
                    : '',
              isActive &&
                !isBrandProviderLayout &&
                !isImageCardLayout &&
                !isVisualChoiceLayout
                ? 'bg-primary/8 text-foreground shadow-[0_12px_30px_-24px_rgba(15,23,42,0.55)]'
                : '',
              !isActive && isBrandProviderLayout
                ? 'hover:-translate-y-0.5 hover:border-primary/35'
                : !isActive && isImageCardLayout
                  ? 'hover:-translate-y-0.5 hover:border-primary/35'
                  : !isActive && isVisualChoiceLayout
                    ? 'hover:-translate-y-0.5 hover:border-primary/30'
                    : '',
              !isActive &&
                !isBrandProviderLayout &&
                !isImageCardLayout &&
                !isVisualChoiceLayout
                ? 'text-foreground hover:-translate-y-0.5 hover:border-primary/20 hover:bg-muted/30 hover:shadow-[0_12px_30px_-24px_rgba(15,23,42,0.45)]'
                : '',
              isActive ? 'border-primary/45' : ''
            )}
          >
            {isBrandProviderLayout ? (
              <>
                <ProviderBrandLogo
                  option={option}
                  className="h-[52px] w-[52px] rounded-lg [&>svg]:h-[52px] [&>svg]:w-[52px]"
                />
                <span className="min-w-0 space-y-1">
                  <span className="block text-[13px] font-semibold leading-5 text-foreground">
                    {getLabel(option)}
                  </span>
                  {getDescription(option) ? (
                    <span className="block text-[11px] leading-5 text-muted-foreground">
                      {getDescription(option)}
                    </span>
                  ) : null}
                </span>
              </>
            ) : isImageCardLayout ? (
              <>
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-primary shadow-[0_14px_30px_-22px_rgba(15,23,42,0.55)]">
                  <OptionIcon size={30} />
                </span>
                <span className="min-w-0 space-y-1">
                  <span className="block text-[13px] font-semibold leading-5 text-foreground">
                    {getLabel(option)}
                  </span>
                  {getDescription(option) ? (
                    <span className="block text-[11px] leading-5 text-muted-foreground">
                      {getDescription(option)}
                    </span>
                  ) : null}
                </span>
              </>
            ) : isVisualChoiceLayout ? (
              <>
                <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <OptionIcon size={24} />
                </span>
                <span className="min-w-0 space-y-1">
                  <span className="block text-[13px] font-semibold leading-5 text-foreground">
                    {option}
                  </span>
                </span>
              </>
            ) : (
              <>
                <span
                  className={mergeClasses(
                    'inline-flex h-10 w-10 items-center justify-center rounded-2xl transition duration-200',
                    isActive
                      ? 'bg-primary/15 text-primary'
                      : 'bg-background/90 text-primary'
                  )}
                >
                  <OptionIcon size={18} />
                </span>
                <span className="min-w-0 flex-1 whitespace-normal pr-2 text-[13px] font-medium leading-5">
                  {option}
                </span>
              </>
            )}
            {isActive ? (
              <span
                className={mergeClasses(
                  'inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm',
                  isBrandProviderLayout || isImageCardLayout
                    ? 'absolute right-3 top-3'
                    : ''
                )}
              >
                <Icons.Check size={14} />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  const renderElement = (item, itemName) => {
    if (!isFieldVisible(item)) {
      return null;
    }

    const options = getChoiceOptions(item);
    const isVersionControlStyledSection =
      selectedSectionEntry?.uiKind === 'version-control-wizard';
    const isStyledConnectionWizardSection =
      selectedSectionEntry?.uiKind === 'issue-management' ||
      isVersionControlStyledSection;
    const isCardChoiceField =
      options.length > 0 &&
      (isProviderField(itemName, item) || isProjectOrAuthField(itemName, item));
    const isVersionChoiceField =
      options.length > 0 && isVersionField(itemName, item);
    const isPlatformChoiceField = isPlatformVisualChoiceField(itemName, item);

    if (item.type === 'input') {
      if (isUrlField(itemName, item)) {
        const providerDefaultUrl = getDefaultUrlForProvider(
          getProviderValueForSection(selectedSectionEntry?.section)
        );
        const validation = getUrlValidation(envObj[itemName]);

        return (
          <div className="space-y-1.5">
            <TextInput
              id={itemName}
              name={itemName}
              placeholder="https://your-instance.com"
              value={envObj[itemName] || ''}
              onChange={handleInput}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-[12px]">
              <span className="text-muted-foreground">
                Hint: https://your-instance.com
              </span>
              {providerDefaultUrl ? (
                <button
                  type="button"
                  onClick={() => setFieldValue(itemName, providerDefaultUrl)}
                  className="text-primary transition hover:text-primary/80"
                >
                  Use default{' '}
                  {getProviderValueForSection(selectedSectionEntry?.section) ||
                    'provider'}{' '}
                  URL
                </button>
              ) : null}
            </div>
            {validation ? (
              <div
                className={mergeClasses(
                  'text-[12px] font-medium',
                  validation.valid
                    ? 'text-emerald-600 dark:text-emerald-300'
                    : 'text-red-600 dark:text-red-300'
                )}
              >
                {validation.valid ? 'OK' : 'Check'}: {validation.message}
              </div>
            ) : null}
          </div>
        );
      }

      return (
        <SuggestionInput
          id={itemName}
          name={itemName}
          placeholder={itemName}
          value={envObj[itemName]}
          onChange={handleInput}
          suggestions={
            item?.suggestion_value?.length > 0 ? item.suggestion_value : []
          }
        />
      );
    }

    if (item.type === 'password') {
      return (
        <PasswordInput
          id={itemName}
          name={itemName}
          placeholder={itemName}
          value={envObj[itemName]}
          onChange={handleInput}
        />
      );
    }

    if (item.type === 'select') {
      if (isPlatformChoiceField) {
        return renderChoiceCards({
          options,
          value: envObj[itemName] || '',
          fieldName: itemName,
          onSelect: (option) => setFieldValue(itemName, option),
          columns: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
          layout: 'image-card',
          getLabel: getPlatformOptionLabel,
          getDescription: (option) =>
            getPlatformOptionDescription(itemName, option),
        });
      }

      if (isCardChoiceField) {
        return renderChoiceCards({
          options,
          value: envObj[itemName] || '',
          fieldName: itemName,
          onSelect: (option) => setFieldValue(itemName, option),
          columns: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
          layout:
            isStyledConnectionWizardSection && isProviderField(itemName, item)
              ? 'brand-provider'
              : 'default',
        });
      }

      if (isVersionChoiceField) {
        return renderChoiceCards({
          options,
          value: envObj[itemName] || '',
          fieldName: itemName,
          onSelect: (option) => setFieldValue(itemName, option),
          columns: isVersionControlStyledSection
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
          size: isVersionControlStyledSection ? 'default' : 'compact',
        });
      }

      return (
        <select
          className={INPUT_CLASS}
          name={itemName}
          onChange={handleInput}
          value={envObj[itemName] || ''}
        >
          <option value="" disabled>
            Select option
          </option>
          {options.map((listItem) => (
              <option value={listItem} key={listItem}>
                {listItem}
              </option>
            ))}
        </select>
      );
    }

    if (item.type === 'file') {
      return (
        <div className="space-y-2">
          <input
            className={INPUT_CLASS}
            name={itemName}
            type="file"
            onChange={handleImageChange}
          />
          <div
            className={mergeClasses(
              INNER_PANEL_CLASS,
              'flex min-h-40 items-center justify-center overflow-hidden border-dashed p-4'
            )}
          >
            {envObj[itemName]?.image ? (
              <img
                src={envObj[itemName].image?.replace(/['"]+/g, '')}
                alt={itemName}
                className="max-h-56 w-full object-contain"
              />
            ) : (
              <div className="text-center text-[12px] text-muted-foreground">
                Upload a file to preview it here.
              </div>
            )}
          </div>
        </div>
      );
    }

    if (item.type === 'radio') {
      if (isPlatformChoiceField) {
        return renderChoiceCards({
          options,
          value: envObj[itemName] || '',
          fieldName: itemName,
          onSelect: (option) => setFieldValue(itemName, option),
          columns: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
          layout: 'image-card',
          getLabel: getPlatformOptionLabel,
          getDescription: (option) =>
            getPlatformOptionDescription(itemName, option),
        });
      }

      if (isCardChoiceField) {
        return renderChoiceCards({
          options,
          value: envObj[itemName] || '',
          fieldName: itemName,
          onSelect: (option) => setFieldValue(itemName, option),
          layout:
            isStyledConnectionWizardSection && isProviderField(itemName, item)
              ? 'brand-provider'
              : 'default',
        });
      }

      if (isVersionChoiceField) {
        return renderChoiceCards({
          options,
          value: envObj[itemName] || '',
          fieldName: itemName,
          onSelect: (option) => setFieldValue(itemName, option),
          columns: isVersionControlStyledSection
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
          size: isVersionControlStyledSection ? 'default' : 'compact',
        });
      }

      if (options.length === 2) {
        return (
          <div className="grid grid-cols-2 gap-2">
            {options.map((option) => {
              const isActive = envObj[itemName] === option;

              return (
                <button
                  key={option}
                  type="button"
                  onClick={() =>
                    handleInput({
                      target: { name: itemName, value: option },
                    })
                  }
                  className={mergeClasses(
                    'min-h-11 rounded-xl border px-3 text-[13px] font-medium transition',
                    isActive
                      ? 'border-primary/40 bg-primary/10 text-foreground'
                      : 'border-border/70 bg-background text-muted-foreground hover:bg-accent'
                  )}
                >
                  {option}
                </button>
              );
            })}
          </div>
        );
      }

      return (
        <div className="flex flex-wrap gap-3">
          {options.map((option, index) => (
            <label
              key={index}
              className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border/70 bg-background px-3 py-2 text-[13px] text-foreground"
            >
              <input
                type="radio"
                name={itemName}
                checked={envObj[itemName] === option}
                onChange={() =>
                  handleInput({
                    target: { name: itemName, value: option },
                  })
                }
                className="h-4 w-4 accent-[oklch(var(--primary))]"
              />
              {option}
            </label>
          ))}
        </div>
      );
    }

    if (item.type === 'checkbox') {
      return (
        <label className="inline-flex items-center gap-3 rounded-xl border border-border/70 bg-background px-3 py-3 text-[13px] text-foreground">
          <input
            type="checkbox"
            name={itemName}
            checked={envObj[itemName] === 'true' || envObj[itemName] === true}
            onChange={(event) =>
              handleInput({
                target: {
                  name: itemName,
                  value: event.target.checked ? 'true' : 'false',
                },
              })
            }
            className="h-4 w-4 accent-[oklch(var(--primary))]"
          />
          <span>{getFieldLabel(itemName, item)}</span>
        </label>
      );
    }

    if (item.type === 'yamlorder') {
      const yamlSectionKey = `${
        selectedSectionEntry?.key || selectedSectionKey || 'settings'
      }::${itemName || 'yaml-order'}`;
      const yamlSummary = buildYamlLoadOrderSummary(yamlOrder);

      return (
        <CollapsibleSettingsSection
          title={getFieldLabel(itemName, item)}
          description={getFieldHelperText(itemName, item)}
          collapsedSummary={yamlSummary.collapsedSummary}
          badges={yamlSummary.badges}
          icon={Icons.Files}
          isExpanded={isCompactSectionExpanded(yamlSectionKey)}
          onToggle={() => toggleCompactSection(yamlSectionKey)}
        >
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-foreground">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icons.ListTree size={17} />
              </span>
              <div className="space-y-0.5">
                <div>Manage reference order</div>
                <p className="text-[12px] font-normal text-muted-foreground">
                  Drag items to set processing priority.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
              <TextInput
                placeholder="Add YAML reference"
                value={yamlNewToken}
                onChange={(event) => setYamlNewToken(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    addYamlToken();
                  }
                }}
                className="h-11 border-border/35 bg-transparent"
              />
              <ActionButton
                onClick={addYamlToken}
                variant="primary"
                className="min-h-11 min-w-[168px] justify-center rounded-lg px-5"
              >
                <Icons.Plus size={15} />
                Add Reference
              </ActionButton>
            </div>

            <div className="rounded-lg border border-border/25 bg-muted/10 p-3 sm:p-4">
              {yamlOrder.length > 0 ? (
                <ul className="themed-slim-scrollbar grid max-h-[320px] gap-3 overflow-y-auto pr-2 sm:max-h-[420px]">
                  {yamlOrder.map((token, index) => (
                    <li
                      key={`${token}-${index}`}
                      className={mergeClasses(
                        'flex items-start gap-4 rounded-lg border border-border/30 bg-background/75 p-4 transition sm:items-center dark:bg-background/35',
                        dragOverIndex === index
                          ? 'border-primary/35 bg-primary/[0.04]'
                          : '',
                        dragIndex === index ? 'scale-[0.99] opacity-75' : ''
                      )}
                      draggable
                      onDragStart={() => onDragStart(index)}
                      onDragOver={(event) => onDragOver(index, event)}
                      onDragLeave={onDragLeave}
                      onDrop={() => onDrop(index)}
                      onDragEnd={onDragEnd}
                    >
                      <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-primary/10 px-2 text-[12px] font-semibold text-primary">
                        {index + 1}
                      </span>
                      <Icons.GripVertical
                        size={16}
                        className="mt-1 shrink-0 cursor-grab text-muted-foreground sm:mt-0"
                      />
                      <span className="min-w-0 flex-1 whitespace-normal break-words text-[13px] font-medium leading-6 text-foreground">
                        {token}
                      </span>
                      <ActionButton
                        onClick={() => deleteYamlToken(index)}
                        variant="ghost"
                        className="min-h-10 shrink-0 rounded-lg px-3.5 text-muted-foreground hover:bg-accent/40 hover:text-foreground"
                      >
                        <Icons.Trash2 size={14} />
                        Remove
                      </ActionButton>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-lg border border-dashed border-border/30 bg-background/55 px-4 py-8 text-center dark:bg-background/15">
                  <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icons.ListTree size={20} />
                  </div>
                  <p className="mt-3 text-[14px] font-medium text-foreground">
                    No YAML references added yet
                  </p>
                  <p className="mt-1 text-[12px] leading-5 text-muted-foreground">
                    Add references above, then drag them into the order your
                    rollout should use.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CollapsibleSettingsSection>
      );
    }

    return null;
  };

  const getSectionStats = (section, sectionKind = '') => {
    const toBooleanFlag = (value) => {
      if (typeof value === 'boolean') {
        return value;
      }

      const normalized = String(value ?? '')
        .trim()
        .toLowerCase();

      if (!normalized) {
        return null;
      }

      if (['true', '1', 'yes', 'y'].includes(normalized)) {
        return true;
      }

      if (['false', '0', 'no', 'n'].includes(normalized)) {
        return false;
      }

      return null;
    };

    const getExplicitRequiredState = (field) => {
      if (!field) {
        return null;
      }

      const requiredCandidates = [
        field.required,
        field.is_required,
        field.Required,
        field.mandatory,
        field.Mandatory,
        field.isMandatory,
      ];

      for (const candidate of requiredCandidates) {
        const parsed = toBooleanFlag(candidate);
        if (parsed !== null) {
          return parsed;
        }
      }

      const optionalCandidates = [
        field.optional,
        field.is_optional,
        field.Optional,
        field.isOptional,
      ];

      for (const candidate of optionalCandidates) {
        const parsed = toBooleanFlag(candidate);
        if (parsed !== null) {
          return !parsed;
        }
      }

      return null;
    };

    const isSectionFieldRequired = (sectionKind, fieldKey, field) => {
      const explicitRequired = getExplicitRequiredState(field);

      if (explicitRequired !== null) {
        return explicitRequired;
      }

      if (
        sectionKind === 'version-control-wizard' &&
        (
          isProviderTypeField(fieldKey, field) ||
          isVersionField(fieldKey, field) ||
          isWorkspaceField(fieldKey, field)
        )
      ) {
        return false;
      }

      if (sectionKind === 'issue-management' && isSyncIntervalField(fieldKey, field)) {
        return false;
      }

      return true;
    };

    let totalFields = 0;
    let completedFields = 0;

    if (!section) {
      return { totalFields: 0, completedFields: 0, progress: 0 };
    }

    if (hasSubsections(section) && sectionKind === 'auth-subsections') {
      Object.keys(section).forEach((subSectionKey) => {
        if (subSectionKey === 'Properties') {
          return;
        }

        const isEnabled = Boolean(authMode[subSectionKey]);

        if (!isEnabled) {
          return;
        }

        totalFields += 1;
        completedFields += 1;

        Object.keys(section[subSectionKey]).forEach((fieldKey) => {
          if (fieldKey === 'Properties') {
            return;
          }

          const field = section[subSectionKey][fieldKey];

          if (
            !isFieldVisible(field) ||
            !isSectionFieldRequired('auth-subsections', fieldKey, field)
          ) {
            return;
          }

          totalFields += 1;
          if (isFieldConfigured(fieldKey, field)) {
            completedFields += 1;
          }
        });
      });
    } else if (hasSubsections(section)) {
      Object.keys(section).forEach((subSectionKey) => {
        if (subSectionKey === 'Properties') {
          return;
        }

        Object.keys(section[subSectionKey]).forEach((fieldKey) => {
          if (fieldKey === 'Properties') {
            return;
          }

          const field = section[subSectionKey][fieldKey];

          if (!isFieldVisible(field) || !isSectionFieldRequired(sectionKind, fieldKey, field)) {
            return;
          }

          totalFields += 1;
          if (isFieldConfigured(fieldKey, field)) {
            completedFields += 1;
          }
        });
      });
    } else {
      Object.keys(section).forEach((fieldKey) => {
        if (fieldKey === 'Properties') {
          return;
        }

        const field = section[fieldKey];

        if (!isFieldVisible(field) || !isSectionFieldRequired(sectionKind, fieldKey, field)) {
          return;
        }

        totalFields += 1;
        if (isFieldConfigured(fieldKey, field)) {
          completedFields += 1;
        }
      });
    }

    return {
      totalFields,
      completedFields,
      progress: totalFields
        ? Math.round((completedFields / totalFields) * 100)
        : 0,
    };
  };

  const getAuthenticationMethodStats = (section) => {
    if (!section || !hasSubsections(section)) {
      return {
        totalMethods: 0,
        configuredMethods: 0,
        progress: 0,
      };
    }

    const authMethodKeys = Object.keys(section).filter(
      (subSectionKey) => subSectionKey !== 'Properties'
    );

    const configuredMethods = authMethodKeys.filter((subSectionKey) => {
      if (!authMode[subSectionKey]) {
        return false;
      }

      const methodStats = getSectionStats(section[subSectionKey], 'auth-provider');

      return methodStats.totalFields
        ? methodStats.completedFields >= methodStats.totalFields
        : true;
    }).length;

    return {
      totalMethods: authMethodKeys.length,
      configuredMethods,
      progress: authMethodKeys.length
        ? Math.round((configuredMethods / authMethodKeys.length) * 100)
        : 0,
    };
  };

const getAuthenticationStatusMeta = ({
  configuredMethods = 0,
} = {}) => {
    if (!configuredMethods) {
      return {
        key: 'not-configured',
        label: 'Not Configured',
        icon: Icons.XCircle,
        className:
          'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/15',
      };
    }

    return {
      key: 'configured-auth-methods',
      label: `${configuredMethods} ${
        configuredMethods === 1 ? 'Method' : 'Methods'
      }`,
      icon: Icons.ShieldCheck,
      className:
        'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/15',
  };
};

const getSectionOverviewDescription = (entry) => {
  const normalizedTitle = cleanTitle(
    entry?.fullTitle || entry?.title || ''
  ).toLowerCase();

  if (entry?.uiKind === 'issue-management') {
    return 'Bug tracking & task management';
  }

  if (entry?.uiKind === 'version-control-wizard') {
    return 'Source code & branch management';
  }

  if (entry?.uiKind === 'rollout-package-split') {
    return 'Package creation & rollout delivery';
  }

  if (entry?.uiKind === 'auth-subsections') {
    return 'Login providers & access management';
  }

  if (normalizedTitle.includes('database')) {
    return 'Connection, schema & credential setup';
  }

  if (normalizedTitle.includes('system')) {
    return 'Environment, platforms & runtime settings';
  }

  if (normalizedTitle.includes('yaml')) {
    return 'Reference file mapping & sync rules';
  }

  if (normalizedTitle.includes('webhook')) {
    return 'Outbound events & delivery endpoints';
  }

  return `Manage ${normalizedTitle || 'configuration'} settings`;
};

const getSectionOverviewStatusClassName = (statusKey = '') =>
  ({
    configured:
      'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    'configured-auth-methods':
      'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    partial:
      'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
    'not-configured':
      'border-rose-500/20 bg-rose-500/10 text-rose-700 dark:text-rose-300',
  })[statusKey] ||
  'border-border/60 bg-card text-muted-foreground';

const SETTINGS_OVERVIEW_ACTION_BASE_CLASS =
  'inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 text-[11.5px] font-medium shadow-none transition focus:outline-none focus:ring-[3px] focus:ring-ring/20';

const SETTINGS_OVERVIEW_VIEW_ACTION_CLASS = mergeClasses(
  SETTINGS_OVERVIEW_ACTION_BASE_CLASS,
  'border-border/45 bg-transparent text-foreground/78 hover:border-border/70 hover:bg-accent/60 hover:text-foreground'
);

const SETTINGS_OVERVIEW_EDIT_ACTION_CLASS = mergeClasses(
  SETTINGS_OVERVIEW_ACTION_BASE_CLASS,
  'border-primary/18 bg-primary/8 text-primary hover:border-primary/32 hover:bg-primary/12'
);

const getSectionDisplayState = (entry) => {
  if (entry?.uiKind === 'auth-subsections') {
    const authMethodStats = getAuthenticationMethodStats(entry.section);

    return {
      statusMeta: getAuthenticationStatusMeta(authMethodStats),
      summaryLabel: `${authMethodStats.configuredMethods}/${authMethodStats.totalMethods} login methods configured`,
      progress: authMethodStats.progress,
      completedCount: authMethodStats.configuredMethods,
      totalCount: authMethodStats.totalMethods,
    };
  }

  return {
    statusMeta: getStatusMeta(entry?.stats),
    summaryLabel: `${entry?.stats?.completedFields || 0}/${
      entry?.stats?.totalFields || 0
    } configured`,
    progress: entry?.stats?.progress || 0,
    completedCount: entry?.stats?.completedFields || 0,
    totalCount: entry?.stats?.totalFields || 0,
  };
};

  const getSectionSteps = (sectionEntry) => {
    const sectionKey = sectionEntry?.key;
    const section = sectionEntry?.section;
    const sectionKind = sectionEntry?.uiKind;
    const sectionTitle =
      sectionEntry?.fullTitle || sectionEntry?.title || sectionKey;

    if (!section || !sectionKey) {
      return [];
    }

    const isIssueManagementSection = sectionKind === 'issue-management';
    const isVersionControlSection = sectionKind === 'version-control-wizard';
    const isStyledConnectionSection =
      isIssueManagementSection || isVersionControlSection;
    const isAuthSection = sectionKind === 'auth-subsections';
    const isFlatSection = sectionKind === 'generic-flat';

    if (isAuthSection && hasSubsections(section)) {
      const providerSteps = Object.keys(section)
        .filter((subSectionKey) => subSectionKey !== 'Properties')
        .map((subSectionKey) => {
          const fields = Object.keys(section[subSectionKey])
            .filter(
              (fieldKey) =>
                fieldKey !== 'Properties' &&
                isFieldVisible(section[subSectionKey][fieldKey])
            )
            .map((fieldKey) => ({
              key: fieldKey,
              field: section[subSectionKey][fieldKey],
            }));

          const completedCount = fields.filter(({ key, field }) =>
            isFieldConfigured(key, field)
          ).length;

          return {
            key: `${sectionKey}-${subSectionKey}`,
            sectionType: 'subsection',
            authKey: subSectionKey,
            title: cleanTitle(
              section[subSectionKey]?.Properties?.Name || subSectionKey
            ),
            icon: getAuthIcon(subSectionKey),
            fields,
            enabled: Boolean(authMode[subSectionKey]),
            completedCount,
          };
        });

      const reviewFields = providerSteps.flatMap((step) => step.fields);

      return [
        ...providerSteps,
        {
          key: `${sectionKey}-review`,
          sectionType: 'review',
          title: 'Review & Save',
          icon: Icons.BadgeCheck,
          fields: reviewFields,
          providerKeys: providerSteps.map((step) => step.authKey),
          completedCount: reviewFields.filter(({ key, field }) =>
            isFieldConfigured(key, field)
          ).length,
        },
      ];
    }

    const visibleFields = Object.keys(section)
      .filter(
        (fieldKey) =>
          fieldKey !== 'Properties' && isFieldVisible(section[fieldKey])
      )
      .map((fieldKey) => ({
        key: fieldKey,
        field: section[fieldKey],
      }));

    if (!visibleFields.length) {
      return [
        {
          key: `${sectionKey}-empty`,
          sectionType: 'field-group',
          title: 'Overview',
          icon: Icons.LayoutTemplate,
          fields: [],
          completedCount: 0,
        },
      ];
    }

    const buckets = {
      provider: [],
      providerType: [],
      version: [],
      connection: [],
      authProject: [],
      additional: [],
    };

    visibleFields.forEach((fieldEntry) => {
      if (isProviderField(fieldEntry.key, fieldEntry.field)) {
        buckets.provider.push(fieldEntry);
        return;
      }

      if (
        isVersionControlSection &&
        isProviderTypeField(fieldEntry.key, fieldEntry.field)
      ) {
        buckets.providerType.push(fieldEntry);
        return;
      }

      if (isVersionField(fieldEntry.key, fieldEntry.field)) {
        buckets.version.push(fieldEntry);
        return;
      }

      if (isConnectionField(fieldEntry.key, fieldEntry.field)) {
        buckets.connection.push(fieldEntry);
        return;
      }

      if (isProjectOrAuthField(fieldEntry.key, fieldEntry.field)) {
        buckets.authProject.push(fieldEntry);
        return;
      }

      buckets.additional.push(fieldEntry);
    });

    const stepBlueprints = isIssueManagementSection
      ? [
          {
            key: 'provider',
            title: 'Choose a provider',
            description: getSectionProviderPrompt(sectionTitle),
            icon: Icons.Bug,
            fields: buckets.provider,
            stepRole: 'provider',
          },
          {
            key: 'connection',
            title: 'Connection details',
            description: `Specify where and how to connect to ${
              getProviderValueForSection(section) || 'the selected provider'
            }.`,
            icon: Icons.Link2,
            fields: [...buckets.connection, ...buckets.version],
            stepRole: 'connection',
          },
          {
            key: 'auth-project',
            title: 'Configure connection',
            description: 'Set up authentication and sync preferences.',
            icon: Icons.KeyRound,
            fields: [...buckets.authProject, ...buckets.additional],
            stepRole: 'details',
          },
        ]
      : isVersionControlSection
        ? [
            {
              key: 'vcs-type',
              title: 'VCS Type',
              description: 'Select the version control system your team uses.',
              displayTitle: 'Choose version control type',
              displayDescription:
                'Select the version control system your team uses.',
              icon: Icons.Layers3,
              fields: buckets.provider,
              stepRole: 'provider',
            },
            {
              key: 'provider',
              title: 'Provider',
              description: `Based on your ${
                getProviderDisplayName(getProviderValueForSection(section)) ||
                'VCS type'
              } selection, choose your hosting provider.`,
              icon: Icons.Bug,
              fields: buckets.providerType,
              stepRole: 'provider-type',
              optional: true,
            },
            {
              key: 'api-version',
              title: 'API Version',
              description: `Choose the API version for your ${
                getProviderDisplayName(
                  envObj[buckets.providerType[0]?.key] || ''
                ) || 'selected provider'
              } integration.`,
              icon: Icons.BadgeInfo,
              fields: buckets.version,
              stepRole: 'api-version',
              optional: true,
            },
            {
              key: 'configuration',
              title: 'Configuration',
              description: 'Enter your credentials and repository details.',
              icon: Icons.KeyRound,
              fields: [
                ...buckets.connection,
                ...buckets.authProject,
                ...buckets.additional,
              ],
              stepRole: 'details',
            },
          ]
        : isFlatSection
          ? [
              {
                key: 'configuration',
                title: 'Configuration',
                description: `Manage the ${sectionTitle.toLowerCase()} settings in one place.`,
                icon: Icons.SlidersHorizontal,
                fields: visibleFields,
                stepRole: 'details',
              },
            ]
          : [
              {
                key: 'provider',
                title: 'Choose Provider',
                description: 'Choose the service or provider for this setup.',
                icon: Icons.Bug,
                fields: buckets.provider,
                stepRole: 'provider',
              },
              {
                key: 'version',
                title: 'Version Selection',
                description: 'Pick the version or release target.',
                icon: Icons.Layers3,
                fields: buckets.version,
                stepRole: 'version',
              },
              {
                key: 'connection',
                title: 'Connection Setup',
                description: 'Add the connection, server, or endpoint values.',
                icon: Icons.Link2,
                fields: buckets.connection,
                stepRole: 'connection',
              },
              {
                key: 'auth-project',
                title: 'Project + Auth Config',
                description:
                  'Fill in project, authentication, and advanced options.',
                icon: Icons.KeyRound,
                fields: [...buckets.authProject, ...buckets.additional],
                stepRole: 'details',
              },
            ].filter((step) => step.fields.length > 0);

    const steps = stepBlueprints.map((step, index) => ({
      key: `${sectionKey}-${step.key}`,
      sectionType: 'field-group',
      title: step.title,
      description: step.description,
      displayTitle: step.displayTitle,
      displayDescription: step.displayDescription,
      icon: step.icon || getStepIcon(index),
      fields: step.fields,
      stepRole: step.stepRole,
      optional: Boolean(step.optional),
      completedCount: step.fields.filter(({ key, field }) =>
        isFieldConfigured(key, field)
      ).length,
    }));

    steps.push({
      key: `${sectionKey}-review`,
      sectionType: 'review',
      title: isIssueManagementSection ? 'Review & save' : 'Review & Save',
      description: isStyledConnectionSection
        ? 'Confirm your settings before saving.'
        : 'Double-check everything before saving this section.',
      icon: Icons.BadgeCheck,
      fields: visibleFields,
      stepRole: 'review',
      completedCount: visibleFields.filter(({ key, field }) =>
        isFieldConfigured(key, field)
      ).length,
    });

    return steps;
  };

  const formatReviewValue = (fieldKey, field) => {
    const value = envObj[fieldKey];

    if (field?.type === 'file') {
      return value?.image ? 'File uploaded' : 'Not configured';
    }

    if (field?.type === 'yamlorder') {
      return yamlOrder.length
        ? `${yamlOrder.length} reference${yamlOrder.length === 1 ? '' : 's'}`
        : 'Not configured';
    }

    if (field?.type === 'checkbox') {
      return value === 'true' || value === true ? 'Enabled' : 'Disabled';
    }

    if (isCredentialField(fieldKey, field)) {
      return value ? 'Hidden' : 'Not configured';
    }

    if (value && typeof value === 'object') {
      return 'Configured';
    }

    return value && String(value).trim() ? String(value) : 'Not configured';
  };

  const getIssueManagementStepFields = (fields = []) => {
    const groups = {
      provider: null,
      version: null,
      url: null,
      category: null,
      authType: null,
      username: null,
      password: null,
      sync: null,
      remainder: [],
    };

    fields.forEach((fieldEntry) => {
      const { key, field } = fieldEntry;

      if (!groups.provider && isProviderField(key, field)) {
        groups.provider = fieldEntry;
        return;
      }

      if (!groups.version && isVersionField(key, field)) {
        groups.version = fieldEntry;
        return;
      }

      if (!groups.url && isUrlField(key, field)) {
        groups.url = fieldEntry;
        return;
      }

      if (!groups.category && isCategoryField(key, field)) {
        groups.category = fieldEntry;
        return;
      }

      if (!groups.authType && isAuthTypeField(key, field)) {
        groups.authType = fieldEntry;
        return;
      }

      if (
        !groups.category &&
        getChoiceOptions(field).length > 0 &&
        !isProviderField(key, field) &&
        !isVersionField(key, field) &&
        !isAuthTypeField(key, field)
      ) {
        groups.category = fieldEntry;
        return;
      }

      if (!groups.username && isUsernameField(key, field)) {
        groups.username = fieldEntry;
        return;
      }

      if (!groups.password && isPasswordLikeField(key, field)) {
        groups.password = fieldEntry;
        return;
      }

      if (!groups.sync && isSyncIntervalField(key, field)) {
        groups.sync = fieldEntry;
        return;
      }

      groups.remainder.push(fieldEntry);
    });

    return groups;
  };

  const getVersionControlStepFields = (fields = []) => {
    const groups = {
      provider: null,
      providerType: null,
      version: null,
      url: null,
      remainder: [],
    };

    fields.forEach((fieldEntry) => {
      const { key, field } = fieldEntry;

      if (!groups.provider && isProviderField(key, field)) {
        groups.provider = fieldEntry;
        return;
      }

      if (!groups.providerType && isProviderTypeField(key, field)) {
        groups.providerType = fieldEntry;
        return;
      }

      if (!groups.version && isApiVersionField(key, field)) {
        groups.version = fieldEntry;
        return;
      }

      if (!groups.version && isVersionField(key, field)) {
        groups.version = fieldEntry;
        return;
      }

      if (!groups.url && isUrlField(key, field)) {
        groups.url = fieldEntry;
        return;
      }

      groups.remainder.push(fieldEntry);
    });

    if (!groups.provider) {
      groups.provider =
        fields.find(({ field }) => {
          const options = getChoiceOptions(field).map((option) =>
            String(option || '')
              .trim()
              .toLowerCase()
          );

          return options.includes('git') && options.includes('svn');
        }) || null;
    }

    if (!groups.providerType) {
      groups.providerType =
        fields.find(({ key, field }) => {
          if (groups.provider && key === groups.provider.key) {
            return false;
          }

          const options = getChoiceOptions(field).map((option) =>
            String(option || '')
              .trim()
              .toLowerCase()
          );

          return (
            options.length > 0 &&
            ['github', 'bitbucket', 'gitlab', 'azure'].some((option) =>
              options.some((value) => value.includes(option))
            )
          );
        }) || null;
    }

    return groups;
  };

  const getVersionControlApiVersionOptions = (providerValue = '', field) => {
    const providedOptions = getChoiceOptions(field);

    if (providedOptions.length) {
      return providedOptions.map((option, index) => ({
        value: option,
        label: option,
        description: index === 0 ? 'Recommended option.' : 'Supported version.',
        recommended: index === 0,
      }));
    }

    const normalized = String(providerValue || '').toLowerCase();

    if (normalized.includes('github')) {
      return [
        {
          value: 'latest',
          label: 'Latest (v4 GraphQL)',
          description: 'Recommended. Full feature support.',
          recommended: true,
        },
        {
          value: 'v3',
          label: 'REST v3',
          description: 'Legacy REST API. Widely supported.',
          recommended: false,
        },
      ];
    }

    if (normalized.includes('bitbucket')) {
      return [
        {
          value: 'latest',
          label: 'Latest (v2)',
          description: 'Recommended. Current stable API.',
          recommended: true,
        },
        {
          value: 'v1',
          label: 'v1 (Legacy)',
          description: 'Deprecated. Use only if required.',
          recommended: false,
        },
      ];
    }

    if (normalized.includes('azure')) {
      return [
        {
          value: 'latest',
          label: 'Latest (2024-01)',
          description: 'Most current Azure DevOps API.',
          recommended: true,
        },
        {
          value: 'v6',
          label: '6.0',
          description: 'Stable release, broad support.',
          recommended: false,
        },
      ];
    }

    return [
      {
        value: 'latest',
        label: 'Latest',
        description: 'Recommended. Current stable API.',
        recommended: true,
      },
    ];
  };

  const getVersionControlProviderTypeOptions = (providerValue = '', field) => {
    const providedOptions = getChoiceOptions(field);

    if (providedOptions.length) {
      return providedOptions;
    }

    const normalized = String(providerValue || '').toLowerCase();

    if (normalized.includes('git')) {
      return ['github', 'bitbucket', 'azure', 'gitlab'];
    }

    if (normalized.includes('svn')) {
      return ['apache svn', 'visualsvn'];
    }

    return [];
  };

  const getSectionVisibleFields = (section) => {
    if (!section || hasSubsections(section)) {
      return [];
    }

    return Object.keys(section)
      .filter(
        (fieldKey) =>
          fieldKey !== 'Properties' && isFieldVisible(section[fieldKey])
      )
      .map((fieldKey) => ({
        key: fieldKey,
        field: section[fieldKey],
      }));
  };

  const isDatabaseSection = (sectionEntry) =>
    normalizeText(sectionEntry?.fullTitle || sectionEntry?.title || '') ===
    'database configuration';

  const canTestSectionConnection = (sectionEntry) =>
    Boolean(
      sectionEntry &&
        (
          sectionEntry.uiKind === 'issue-management' ||
          sectionEntry.uiKind === 'version-control-wizard' ||
          isDatabaseSection(sectionEntry)
        )
    );

  const getSectionConnectionPayload = (sectionEntry = selectedSectionEntry) => {
    const section = sectionEntry?.section;
    const visibleFields = getSectionVisibleFields(section);
    const envVarArray = {};

    visibleFields.forEach(({ key, field }) => {
      envVarArray[key] = getFieldRequestValue(key, field);
    });

    if (isDatabaseSection(sectionEntry)) {
      return {
        data: envVarArray,
      };
    }

    return {
      type: 'rolloutconfig',
      sectionKey: sectionEntry?.key || '',
      sectionTitle: sectionEntry?.fullTitle || sectionEntry?.title || '',
      sectionKind: sectionEntry?.uiKind || '',
      provider: getProviderValueForSection(section) || '',
      envVarArray,
    };
  };

  const hasUnsavedSectionChanges = (sectionEntry = selectedSectionEntry) => {
    const section = sectionEntry?.section;

    if (!section) {
      return false;
    }

    return getSectionVisibleFields(section).some(({ key, field }) => {
      const currentValue = getFieldSnapshotValue(envObj, key, field);
      const savedValue = getFieldSnapshotValue(savedEnvObj, key, field);

      return currentValue !== savedValue;
    });
  };

  const getIssueManagementConnectionPayload = (
    sectionEntry = selectedSectionEntry
  ) => {
    const basePayload = getSectionConnectionPayload(sectionEntry);

    if (!hasUnsavedSectionChanges(sectionEntry)) {
      const { envVarArray, ...payloadWithoutEnvVarArray } = basePayload;
      return payloadWithoutEnvVarArray;
    }

    return basePayload;
  };

  const getConnectionTestSuccessMessage = (response) =>
    response?.payload?.message ||
    response?.message ||
    response?.status?.message ||
    'The current configuration connected successfully.';

  const getConnectionTestErrorMessage = (response) =>
    getReadableErrorMessage(response, 'Unable to test the current connection.');

  const isConnectionTestSuccessful = (response) =>
    response?.isSuccess === true ||
    response?.success === true ||
    response?.status === true ||
    response?.status?.code === 0;

  const handleTestConnection = async (sectionEntry = selectedSectionEntry) => {
    if (!canTestSectionConnection(sectionEntry)) {
      return;
    }

    setIsTestingConnection(true);

    try {
      const payload =
        sectionEntry?.uiKind === 'issue-management'
          ? getIssueManagementConnectionPayload(sectionEntry)
          : getSectionConnectionPayload(sectionEntry);
      const response = isDatabaseSection(sectionEntry)
        ? await verifyDBConfig(payload)
        : sectionEntry?.uiKind === 'issue-management'
          ? await testIssueManagementConnection(payload)
          : await testVersionControlConnection(payload);
      const isSuccess = isConnectionTestSuccessful(response);

      if (isDatabaseSection(sectionEntry)) {
        setDBVerified(Boolean(isSuccess));
      }

      if (isSuccess) {
        showSuccess(
          'Connection test successful',
          getConnectionTestSuccessMessage(response)
        );
        return;
      }

      showError(getConnectionTestErrorMessage(response));
    } catch (error) {
      showError(error);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const getVersionControlSelectionState = (section) => {
    const entries = getSectionVisibleFields(section);
    const grouped = getVersionControlStepFields(entries);
    const versionControlProviderValue =
      envObj.VERSION_CONTROL_PROVIDER ||
      (grouped.provider ? envObj[grouped.provider.key] || '' : '');

    return {
      entries,
      grouped,
      providerValue: versionControlProviderValue,
      providerTypeValue: grouped.providerType
        ? envObj[grouped.providerType.key] || ''
        : '',
      versionValue: grouped.version ? envObj[grouped.version.key] || '' : '',
      urlValue: grouped.url ? envObj[grouped.url.key] || '' : '',
    };
  };

  const getVersionControlReviewLabel = (fieldKey, field) => {
    if (isProviderField(fieldKey, field)) {
      return 'VCS Type';
    }

    if (isProviderTypeField(fieldKey, field)) {
      return 'Provider';
    }

    if (isApiVersionField(fieldKey, field) || isVersionField(fieldKey, field)) {
      return 'API Version';
    }

    if (isUrlField(fieldKey, field)) {
      return 'Base URI';
    }

    if (isUsernameField(fieldKey, field)) {
      return 'Username';
    }

    if (isWorkspaceField(fieldKey, field)) {
      return 'Workspace / Org';
    }

    if (isBranchField(fieldKey, field)) {
      return 'Default Branch';
    }

    if (isMultiFilePathField(fieldKey, field)) {
      return 'Multi-file path';
    }

    return getFieldLabel(fieldKey, field);
  };

  const buildVersionControlReviewItems = (fields = []) =>
    [...fields]
      .sort((left, right) => {
        const getRank = ({ key, field }) => {
          if (isProviderField(key, field)) return 2;
          if (isProviderTypeField(key, field)) return 3;
          if (isApiVersionField(key, field) || isVersionField(key, field))
            return 4;
          if (isUrlField(key, field)) return 5;
          if (isUsernameField(key, field)) return 6;
          if (isWorkspaceField(key, field)) return 7;
          if (isBranchField(key, field)) return 8;
          if (isMultiFilePathField(key, field)) return 9;
          return 20;
        };

        return getRank(left) - getRank(right);
      })
      .filter(({ key, field }) => !isPasswordLikeField(key, field))
      .map(({ key, field }) => ({
        key,
        label: getVersionControlReviewLabel(key, field),
        value:
          isProviderField(key, field) || isProviderTypeField(key, field)
            ? getProviderDisplayName(envObj[key] || '')
            : isUrlField(key, field)
              ? stripUrlProtocol(envObj[key] || '')
              : formatReviewValue(key, field),
        badge:
          isApiVersionField(key, field) || isVersionField(key, field),
        providerBrand:
          isProviderField(key, field) || isProviderTypeField(key, field),
        mono:
          isUrlField(key, field) ||
          isUsernameField(key, field) ||
          isBranchField(key, field) ||
          isMultiFilePathField(key, field),
      }));

  const renderDefaultReviewStep = (step) => {
    const providerReviewItems = (step.providerKeys || []).map(
      (providerKey) => ({
        label: cleanTitle(providerKey.replace(/_/g, ' ')),
        value: authMode[providerKey] ? 'Enabled' : 'Disabled',
      })
    );

    const fieldReviewItems = step.fields.map(({ key, field }) => ({
      label: getFieldLabel(key, field),
      value: formatReviewValue(key, field),
    }));

    const reviewItems = [...providerReviewItems, ...fieldReviewItems];

    return (
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
        {reviewItems.map((item) => (
          <div
            key={item.label}
            className={mergeClasses(FIELD_PANEL_CLASS, 'space-y-1.5 p-4')}
          >
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {item.label}
            </span>
            <strong className="block text-[13px] font-semibold leading-6 text-foreground">
              {item.value}
            </strong>
          </div>
        ))}
      </div>
    );
  };

  const renderDefaultFieldGroup = (fields, step) => {
    if (step?.sectionType === 'review') {
      return renderDefaultReviewStep(step);
    }

    if (!fields.length) {
      const currentProviderValue = getProviderValueForSection(selectedSection);
      const isProviderLockedStep =
        step?.stepRole === 'connection' && !currentProviderValue;

      return (
        <div
          className={mergeClasses(
            FIELD_PANEL_CLASS,
            'grid gap-3 p-4 sm:grid-cols-[auto_1fr]'
          )}
        >
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            {isProviderLockedStep ? (
              <Icons.Lock size={18} />
            ) : (
              <Icons.Sparkles size={18} />
            )}
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {isProviderLockedStep
                ? 'Choose a provider first'
                : 'Nothing here yet'}
            </h3>
            <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
              {isProviderLockedStep
                ? 'Select a provider in the previous step to unlock URL and connection details.'
                : 'A previous choice will unlock fields for this step.'}
            </p>
          </div>
        </div>
      );
    }

    const compactCredentialLayout =
      fields.length <= 2 &&
      fields.every(({ key, field }) => isCredentialField(key, field));
    const isVersionControlVersionStep =
      selectedSectionEntry?.uiKind === 'version-control-wizard' &&
      step?.stepRole === 'version';
    const isRolloutPackageSection = normalizeText(
      selectedSectionEntry?.fullTitle || selectedSectionEntry?.title || ''
    ).includes('rollout package');
    const isUnboxedFieldLayout = Boolean(step?.unboxedFields);
    const isSelectionOnlyStep =
      fields.length > 0 &&
      fields.every(({ key, field }) => {
        const hasOptions = getChoiceOptions(field).length > 0;

        return (
          hasOptions &&
          (isProviderField(key, field) ||
            isVersionField(key, field) ||
            isProjectOrAuthField(key, field))
        );
      });

    return (
      <div
        className={mergeClasses(
          'grid grid-cols-1 gap-3',
          isRolloutPackageSection
            ? 'xl:grid-cols-1'
            : isVersionControlVersionStep
              ? 'lg:grid-cols-2'
              : isSelectionOnlyStep
                ? 'xl:grid-cols-1'
                : compactCredentialLayout
                  ? 'lg:grid-cols-2'
                  : 'xl:grid-cols-2 2xl:grid-cols-3'
        )}
      >
        {fields.map(({ key, field }) => {
          const isYamlOrderField = field?.type === 'yamlorder';
          const isChoiceField =
            getChoiceOptions(field).length > 0 &&
            (isProviderField(key, field) ||
              isVersionField(key, field) ||
              isProjectOrAuthField(key, field));
          const hideLabel =
            selectedSectionEntry?.uiKind === 'issue-management' &&
            (isProviderField(key, field) || isVersionField(key, field));
          const shouldWrapChoiceField =
            isChoiceField &&
            selectedSectionEntry?.uiKind === 'version-control-wizard';

          return (
            <div
              key={key}
              className={
                isYamlOrderField
                  ? 'space-y-0'
                  : shouldWrapChoiceField
                  ? isUnboxedFieldLayout
                    ? 'space-y-3'
                    : mergeClasses(FIELD_PANEL_CLASS, 'space-y-3 p-4')
                  : isChoiceField
                    ? 'space-y-2'
                    : isUnboxedFieldLayout
                      ? 'space-y-1.5'
                      : mergeClasses(FIELD_PANEL_CLASS, 'space-y-1.5 p-4')
              }
            >
              {!hideLabel ? (
                !isYamlOrderField ? (
                <label
                  htmlFor={key}
                  className={
                    isChoiceField
                      ? 'text-[12px] font-medium text-muted-foreground'
                      : LABEL_CLASS
                  }
                >
                  {getFieldLabel(key, field)}
                </label>
                ) : null
              ) : null}
              {renderElement(field, key)}
              {!isYamlOrderField && getFieldHelperText(key, field) ? (
                <div className="pt-0.5 text-[12px] leading-5 text-muted-foreground">
                  {getFieldHelperText(key, field)}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderIssueManagementReviewStep = (step) => {
    const reviewItems = buildIssueManagementReviewItems(step.fields);

    return (
      <div className="space-y-4">
        <div className="max-w-[540px] overflow-hidden rounded-lg border border-border/60 bg-background/80">
          {reviewItems.map((item, index) => (
            <div
              key={item.key}
              className={mergeClasses(
                'flex items-center justify-between gap-4 px-4 py-3',
                index < reviewItems.length - 1
                  ? 'border-b border-border/60'
                  : ''
              )}
            >
              <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {item.label}
              </span>
              <span
                className={mergeClasses(
                  'text-right text-[12px] font-medium text-foreground',
                  item.mono ? 'font-mono text-primary' : ''
                )}
              >
                {item.badge ? (
                  <span className="inline-flex rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                    {item.value}
                  </span>
                ) : (
                  item.value
                )}
              </span>
            </div>
          ))}
        </div>

        <div className="flex max-w-[540px] items-center gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-emerald-700 dark:text-emerald-300">
          <Icons.BadgeCheck size={16} />
          <span className="text-[12px] font-medium">
            All settings look good. Click Save to finish setup.
          </span>
        </div>
      </div>
    );
  };

  const renderVersionControlReviewStep = (step) => {
    const selectionState = getVersionControlSelectionState(selectedSection);
    const reviewItems = buildVersionControlReviewItems(step.fields);

    const isReadyToSave = Boolean(selectionState.providerValue);

    return (
      <div className="space-y-4">
        <div className="max-w-[720px] overflow-hidden rounded-lg border border-border/60 bg-background/80">
          {reviewItems.map((item, index) => (
            <div
              key={item.key}
              className={mergeClasses(
                'flex items-center justify-between gap-4 px-4 py-3',
                index < reviewItems.length - 1
                  ? 'border-b border-border/60'
                  : ''
              )}
            >
              <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {item.label}
              </span>
              <span
                className={mergeClasses(
                  'text-right text-[12px] font-medium text-foreground',
                  item.mono ? 'font-mono' : ''
                )}
              >
                {item.badge ? (
                  <span className="inline-flex rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                    {item.value}
                  </span>
                ) : (
                  item.value
                )}
              </span>
            </div>
          ))}
        </div>

        <div
          className={mergeClasses(
            'flex max-w-[720px] items-center gap-2 rounded-lg px-4 py-3 text-[12px] font-medium',
            isReadyToSave
              ? 'border border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
              : 'border border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          )}
        >
          {isReadyToSave ? (
            <Icons.BadgeCheck size={16} />
          ) : (
            <Icons.AlertCircle size={16} />
          )}
          <span>
            {isReadyToSave
              ? 'All settings look good. Click Save to finish setup.'
              : 'Review the configuration values before saving.'}
          </span>
        </div>
      </div>
    );
  };

  const renderIssueManagementFieldGroup = (fields, step) => {
    if (step?.sectionType === 'review') {
      return renderIssueManagementReviewStep(step);
    }

    if (
      !fields.length &&
      selectedSectionEntry?.uiKind !== 'version-control-wizard'
    ) {
      return renderDefaultFieldGroup(fields, step);
    }

    if (
      selectedSectionEntry?.uiKind === 'version-control-wizard' &&
      step?.stepRole === 'category'
    ) {
      return (
        <div className="rounded-lg border border-border/60 bg-background/80 p-5">
          <div className="space-y-3">
            <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Project Category
            </span>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {VERSION_CONTROL_CATEGORY_OPTIONS.map((option) => {
                const OptionIcon = option.icon;
                const isSelected = currentVersionControlCategory === option.id;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setVersionControlCategorySelections((prevState) => ({
                        ...prevState,
                        [selectedSectionEntry.key]: option.id,
                      }));
                      window.setTimeout(() => {
                        setCurrentSectionStep(activeStepIndex + 1);
                      }, 0);
                    }}
                    className={mergeClasses(
                      'relative flex min-h-[150px] flex-col items-start gap-4 rounded-lg border bg-background/80 px-5 py-5 text-left transition duration-200 focus:outline-none focus:ring-[3px] focus:ring-ring/20',
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-border/60 hover:-translate-y-0.5 hover:border-primary/30'
                    )}
                  >
                    <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <OptionIcon size={24} />
                    </span>
                    <span className="space-y-1">
                      <span className="block text-[13px] font-semibold text-foreground">
                        {option.name}
                      </span>
                      <span className="block text-[11px] leading-5 text-muted-foreground">
                        {option.description}
                      </span>
                    </span>
                    {isSelected ? (
                      <span className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                        <Icons.Check size={14} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    if (
      selectedSectionEntry?.uiKind === 'version-control-wizard' &&
      step?.stepRole === 'provider'
    ) {
      const selectionState = getVersionControlSelectionState(selectedSection);
      const providerField = selectionState.grouped.provider;
      const providerOptions = ['git', 'svn'];

      return (
        <div className="rounded-lg border border-border/60 bg-background/80 p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {providerField ? (
              providerOptions.map((option) => {
                const isSelected = (envObj[providerField.key] || '') === option;

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setFieldValue(providerField.key, option);
                    }}
                    className={mergeClasses(
                      'relative flex min-h-[168px] flex-col items-start justify-start gap-5 rounded-lg border bg-background px-6 py-5 text-left transition duration-200 focus:outline-none focus:ring-[3px] focus:ring-[#ff5b35]/15',
                      isSelected
                        ? 'border-[2px] border-[#ff5b35] shadow-[0_10px_28px_-20px_rgba(255,91,53,0.55)]'
                        : 'border-border/60 hover:border-[#ff5b35]/45'
                    )}
                  >
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#eeedfe] text-foreground">
                      <ProviderBrandLogo
                        option={option}
                        className="h-11 w-11 rounded-lg [&>svg]:h-11 [&>svg]:w-11"
                      />
                    </span>

                    <span className="space-y-1.5">
                      <span className="block text-[15px] font-semibold text-foreground">
                        {option === 'svn'
                          ? 'SVN (Subversion)'
                          : getProviderDisplayName(option)}
                      </span>
                      <span className="block max-w-[30ch] text-[12px] leading-6 text-muted-foreground">
                        {option === 'git'
                          ? 'Distributed version control. Used by GitHub, Bitbucket, GitLab, and Azure DevOps.'
                          : 'Centralized version control. Best for large binaries and strict access control.'}
                      </span>
                    </span>

                    {isSelected ? (
                      <span className="absolute right-4 top-4 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#ff5b35] text-white shadow-sm">
                        <Icons.Check size={14} />
                      </span>
                    ) : null}
                  </button>
                );
              })
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 bg-card/20 px-4 py-3 text-[13px] text-muted-foreground">
                Version provider configuration is unavailable.
              </div>
            )}
          </div>
        </div>
      );
    }

    if (
      selectedSectionEntry?.uiKind === 'version-control-wizard' &&
      step?.stepRole === 'provider-type'
    ) {
      const selectionState = getVersionControlSelectionState(selectedSection);
      const providerTypeField = selectionState.grouped.providerType;
      const providerOptions = getVersionControlProviderTypeOptions(
        selectionState.providerValue,
        providerTypeField?.field
      );
      const selectedVcs = selectionState.providerValue;

      return (
        <div className="rounded-lg border border-border/60 bg-background/80 p-5">
          <div className="space-y-3">
            <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Provider
            </span>
            {providerTypeField ? (
              renderChoiceCards({
                options: providerOptions,
                value: envObj[providerTypeField.key] || '',
                fieldName: providerTypeField.key,
                onSelect: (option) => {
                  setFieldValue(providerTypeField.key, option);
                },
                columns: 'grid-cols-1 sm:grid-cols-2',
                layout: 'brand-provider',
                getDescription: getVersionControlProviderDescription,
              })
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 bg-card/20 px-4 py-3 text-[13px] text-muted-foreground">
                Choose a {selectedVcs || 'version control'} provider to
                continue.
              </div>
            )}
          </div>
        </div>
      );
    }

    if (
      selectedSectionEntry?.uiKind === 'version-control-wizard' &&
      step?.stepRole === 'api-version'
    ) {
      const selectionState = getVersionControlSelectionState(selectedSection);
      const versionField = selectionState.grouped.version;
      const providerTypeValue = selectionState.providerTypeValue;
      const versionOptions = getVersionControlApiVersionOptions(
        providerTypeValue,
        versionField?.field
      );

      return (
        <div className="rounded-lg border border-border/60 bg-background/80 p-5">
          <div className="space-y-3">
            {providerTypeValue ? (
              <div className="space-y-2">
                {versionOptions.map((option) => {
                  const isSelected =
                    (envObj[versionField?.key] || '') === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        if (versionField) {
                          setFieldValue(versionField.key, option.value);
                        }
                      }}
                      className={mergeClasses(
                        'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition',
                        isSelected
                          ? 'border-primary bg-primary/10'
                          : 'border-border/60 bg-background/80 hover:border-primary/25'
                      )}
                    >
                      <span
                        className={mergeClasses(
                          'inline-flex h-5 w-5 items-center justify-center rounded-full border',
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border/70'
                        )}
                      >
                        {isSelected ? (
                          <span className="h-2 w-2 rounded-full bg-current" />
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold text-foreground">
                          {option.label}
                        </span>
                        <span className="block text-[11px] text-muted-foreground">
                          {option.description}
                        </span>
                      </span>
                      {option.recommended ? (
                        <span className="inline-flex rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                          Recommended
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 bg-card/20 px-4 py-3 text-[13px] text-muted-foreground">
                Select the provider to continue to API version.
              </div>
            )}
          </div>
        </div>
      );
    }

    if (
      selectedSectionEntry?.uiKind === 'version-control-wizard' &&
      step?.stepRole === 'details'
    ) {
      const selectionState = getVersionControlSelectionState(selectedSection);
      const { grouped } = selectionState;
      const providerTypeValue = selectionState.providerTypeValue;
      const providerDefaultUrl = getDefaultUrlForProvider(providerTypeValue);
      const validation = grouped.url
        ? getUrlValidation(envObj[grouped.url.key])
        : null;
      const workspaceField = grouped.remainder.find(({ key, field }) =>
        isWorkspaceField(key, field)
      );
      const branchField = grouped.remainder.find(({ key, field }) =>
        isBranchField(key, field)
      );
      const multiFilePathField = grouped.remainder.find(({ key, field }) =>
        isMultiFilePathField(key, field)
      );
      const usernameField = grouped.remainder.find(({ key, field }) =>
        isUsernameField(key, field)
      );
      const passwordField = grouped.remainder.find(({ key, field }) =>
        isPasswordLikeField(key, field)
      );
      const remainingFields = grouped.remainder.filter(
        ({ key, field }) => {
          const fieldLabel = getFieldLabel(key, field);

          return (
            !isProviderField(key, field) &&
            !isProviderTypeField(key, field) &&
            !isApiVersionField(key, field) &&
            !isVersionField(key, field) &&
            fieldLabel !== 'Provider' &&
            fieldLabel !== 'Provider Type' &&
            fieldLabel !== 'API Version' &&
            !isWorkspaceField(key, field) &&
            !isBranchField(key, field) &&
            !isMultiFilePathField(key, field) &&
            !isUsernameField(key, field) &&
            !isPasswordLikeField(key, field)
          );
        }
      );

      return (
        <div className="rounded-lg border border-border/60 bg-background/80 p-5">
          <div className="space-y-5">
            {grouped.url ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label
                    htmlFor={grouped.url.key}
                    className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                  >
                    Base URI
                  </label>
                </div>
                <TextInput
                  id={grouped.url.key}
                  name={grouped.url.key}
                  value={envObj[grouped.url.key] || ''}
                  onChange={handleInput}
                  className="font-mono"
                  placeholder={
                    providerDefaultUrl || 'https://your-instance.com'
                  }
                />
                <div className="flex flex-wrap items-center gap-3">
                
                  <span className="text-[12px] text-muted-foreground">
                    {getFieldHelperText(
                      grouped.url.key,
                      grouped.url.field,
                      `Enter the base URL of your ${providerTypeValue || 'provider'} instance.`
                    )}
                  </span>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {usernameField ? (
                <div className="space-y-3">
                  <label
                    htmlFor={usernameField.key}
                    className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                  >
                    Username
                  </label>
                  <TextInput
                    id={usernameField.key}
                    name={usernameField.key}
                    value={envObj[usernameField.key] || ''}
                    onChange={handleInput}
                    placeholder="your-username"
                  />
                  <p className="text-[12px] leading-5 text-muted-foreground">
                    {getFieldHelperText(
                      usernameField.key,
                      usernameField.field,
                      'Enter the username to authenticate with the Version Control Provider APIs'
                    )}
                  </p>
                </div>
              ) : null}

              {passwordField ? (
                <div className="space-y-3">
                  <label
                    htmlFor={passwordField.key}
                    className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                  >
                    Password / Token
                  </label>
                  <PasswordInput
                    id={passwordField.key}
                    name={passwordField.key}
                    value={envObj[passwordField.key] || ''}
                    onChange={handleInput}
                  />
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {workspaceField ? (
                <div className="space-y-3">
                  <label
                    htmlFor={workspaceField.key}
                    className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                  >
                    Workspace / Org
                  </label>
                  <TextInput
                    id={workspaceField.key}
                    name={workspaceField.key}
                    value={envObj[workspaceField.key] || ''}
                    onChange={handleInput}
                    placeholder="your-workspace"
                  />
                  <p className="text-[12px] leading-5 text-muted-foreground">
                    Workspace or org name for your repositories.
                  </p>
                </div>
              ) : null}

              {branchField ? (
                <div className="space-y-3">
                  <label
                    htmlFor={branchField.key}
                    className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                  >
                    Default Branch
                  </label>
                  <TextInput
                    id={branchField.key}
                    name={branchField.key}
                    value={envObj[branchField.key] || ''}
                    onChange={handleInput}
                    placeholder="main"
                  />
                  <p className="text-[12px] leading-5 text-muted-foreground">
                    Default branch to track.
                  </p>
                </div>
              ) : null}
            </div>

            {multiFilePathField ? (
              <div className="space-y-3">
                <label
                  htmlFor={multiFilePathField.key}
                  className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                >
                  Multi-file path
                </label>
                <TextInput
                  id={multiFilePathField.key}
                  name={multiFilePathField.key}
                  value={envObj[multiFilePathField.key] || ''}
                  onChange={handleInput}
                  placeholder="e.g. RPWEB"
                />
                <p className="text-[12px] leading-5 text-muted-foreground">
                  Name to create and manage multiple files separately.
                </p>
              </div>
            ) : null}

            {remainingFields.length ? (
              <div className="grid grid-cols-1 gap-5 border-t border-border/60 pt-5 xl:grid-cols-2">
                {remainingFields.map(({ key, field }) => (
                  <div key={key} className="space-y-3">
                    <label
                      htmlFor={key}
                      className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                    >
                      {getFieldLabel(key, field)}
                    </label>
                    {renderElement(field, key)}
                    {getFieldHelperText(key, field) ? (
                      <p className="text-[12px] leading-5 text-muted-foreground">
                        {getFieldHelperText(key, field)}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    const groupedFields = getIssueManagementStepFields(fields);

    if (step?.stepRole === 'provider' && groupedFields.provider) {
      return (
        <div className="space-y-4">
          {renderElement(
            groupedFields.provider.field,
            groupedFields.provider.key
          )}
          {groupedFields.remainder.length
            ? renderDefaultFieldGroup(groupedFields.remainder, step)
            : null}
        </div>
      );
    }

    if (step?.stepRole === 'connection') {
      const versionField = groupedFields.version;
      const urlField = groupedFields.url;
      const versionOptions = versionField
        ? getChoiceOptions(versionField.field)
        : [];
      const selectedVersion = versionField
        ? envObj[versionField.key] || versionOptions[0] || ''
        : '';
      const currentProvider = getProviderValueForSection(selectedSection);
      const providerDefaultUrl = getDefaultUrlForProvider(currentProvider);
      const validation = urlField
        ? getUrlValidation(envObj[urlField.key])
        : null;

      return (
        <div className="space-y-4">
          {versionField ? (
            <div className="flex flex-col gap-4 rounded-lg border border-border/60 bg-background/80 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icons.LayoutGrid size={18} />
                </span>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    API Version
                  </div>
                  <div className="text-[15px] font-semibold text-foreground">
                    {getSectionProviderApiName(
                      selectedSectionEntry?.fullTitle,
                      currentProvider
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex overflow-hidden rounded-full border border-border/60 bg-card">
                  {versionOptions.map((option) => {
                    const isSelected = selectedVersion === option;

                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFieldValue(versionField.key, option)}
                        className={mergeClasses(
                          'px-4 py-2 text-[12px] font-semibold transition',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                        )}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
                <span className="inline-flex items-center gap-2 text-[12px] font-medium text-emerald-600 dark:text-emerald-300">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Compatible
                </span>
              </div>
            </div>
          ) : null}

          {urlField ? (
            <div className="space-y-3 rounded-lg border border-border/60 bg-background/80 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Base URI
                </span>
                {providerDefaultUrl ? (
                  <button
                    type="button"
                    onClick={() =>
                      setFieldValue(urlField.key, providerDefaultUrl)
                    }
                    className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-medium text-primary transition hover:bg-primary/15"
                  >
                    Use default URL
                  </button>
                ) : null}
              </div>

              <div className="flex overflow-hidden rounded-lg border border-border/60 bg-card">
                <span className="inline-flex h-10 items-center border-r border-border/60 bg-muted px-3 font-mono text-[12px] text-muted-foreground">
                  https://
                </span>
                <input
                  id={urlField.key}
                  type="text"
                  value={stripUrlProtocol(envObj[urlField.key] || '')}
                  onChange={(event) =>
                    setFieldValue(
                      urlField.key,
                      buildHttpsUrl(event.target.value)
                    )
                  }
                  className="h-10 flex-1 bg-transparent px-3 font-mono text-[13px] text-foreground outline-none"
                  placeholder="your-instance.com/path"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3">
               

                <span className="text-[12px] text-muted-foreground lg:ml-auto">
                  {getFieldHelperText(
                    urlField.key,
                    urlField.field,
                    `Enter the base URL of your ${currentProvider || 'provider'} instance`
                  )}
                </span>
              </div>
            </div>
          ) : null}

          {groupedFields.remainder.length
            ? renderDefaultFieldGroup(groupedFields.remainder, step)
            : null}
        </div>
      );
    }

    if (step?.stepRole === 'details') {
      const categoryField = groupedFields.category;
      const authTypeField = groupedFields.authType;
      const usernameField = groupedFields.username;
      const passwordField = groupedFields.password;
      const syncField = groupedFields.sync;
      const syncMeta = syncField ? getSyncIntervalMeta(syncField.key) : null;
      const remainingFields = groupedFields.remainder.filter(
        ({ key, field }) =>
          !isCategoryField(key, field) &&
          !isAuthTypeField(key, field) &&
          !isUsernameField(key, field) &&
          !isPasswordLikeField(key, field) &&
          !isSyncIntervalField(key, field)
      );

      return (
        <div className="rounded-lg border border-border/60 bg-background/80 p-5">
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {categoryField ? (
                <div className="space-y-3">
                  <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Project Category
                  </span>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {getChoiceOptions(categoryField.field).map((option) => {
                      const isSelected = envObj[categoryField.key] === option;

                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() =>
                            setFieldValue(categoryField.key, option)
                          }
                          className={mergeClasses(
                            'rounded-[10px] border px-3 py-2 text-[12px] font-medium transition',
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border/60 bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground'
                          )}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {authTypeField ? (
                <div className="space-y-3">
                  <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Authentication Type
                  </span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {getChoiceOptions(authTypeField.field).map((option) => {
                      const isSelected = envObj[authTypeField.key] === option;
                      const optionMeta =
                        getIssueManagementAuthOptionMeta(option);
                      const OptionIcon = optionMeta.icon;

                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() =>
                            setFieldValue(authTypeField.key, option)
                          }
                          className={mergeClasses(
                            'flex flex-col items-center gap-2 rounded-[12px] border px-3 py-3 text-center transition',
                            isSelected
                              ? 'border-primary bg-primary/10'
                              : 'border-border/60 bg-card hover:border-primary/30'
                          )}
                        >
                          <span
                            className={mergeClasses(
                              'inline-flex h-8 w-8 items-center justify-center rounded-[9px]',
                              optionMeta.iconClassName
                            )}
                          >
                            <OptionIcon size={14} />
                          </span>
                          <span
                            className={mergeClasses(
                              'text-[12px] font-medium',
                              isSelected ? 'text-primary' : 'text-foreground'
                            )}
                          >
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {usernameField ? (
                <div className="space-y-3">
                  <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Username
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] border border-primary/20 bg-primary/10 text-[12px] font-semibold text-primary">
                      {getInitials(envObj[usernameField.key] || '')}
                    </span>
                    <TextInput
                      id={usernameField.key}
                      name={usernameField.key}
                      value={envObj[usernameField.key] || ''}
                      onChange={handleInput}
                      className="font-mono"
                    />
                  </div>
                </div>
              ) : null}

              {passwordField ? (
                <div className="space-y-3">
                  <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    {getFieldLabel(passwordField.key, passwordField.field)}
                  </span>
                  <PasswordInput
                    id={passwordField.key}
                    name={passwordField.key}
                    value={envObj[passwordField.key] || ''}
                    onChange={handleInput}
                    inputClassName="font-mono"
                  />
                </div>
              ) : null}
            </div>

            {syncField && syncMeta ? (
              <div className="space-y-3 border-t border-border/60 pt-5">
                <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Sync Interval
                </span>
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        syncField.key,
                        String(
                          Math.max(syncMeta.min, syncMeta.value - syncMeta.step)
                        )
                      )
                    }
                    className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] border border-border/60 bg-card text-foreground transition hover:border-primary/30"
                  >
                    <Icons.Minus size={14} />
                  </button>
                  <span className="min-w-10 text-center text-[20px] font-semibold text-foreground">
                    {syncMeta.value}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        syncField.key,
                        String(
                          Math.min(syncMeta.max, syncMeta.value + syncMeta.step)
                        )
                      )
                    }
                    className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] border border-border/60 bg-card text-foreground transition hover:border-primary/30"
                  >
                    <Icons.Plus size={14} />
                  </button>
                  <span className="text-[12px] text-muted-foreground">
                    minutes
                  </span>
                  <div className="h-1.5 min-w-[180px] flex-1 overflow-hidden rounded-full bg-border/60">
                    <span
                      className="block h-full rounded-full bg-primary transition-[width]"
                      style={{ width: `${syncMeta.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {remainingFields.length ? (
              <div className="grid grid-cols-1 gap-5 border-t border-border/60 pt-5 xl:grid-cols-2">
                {remainingFields.map(({ key, field }) => (
                  <div key={key} className="space-y-3">
                    <label
                      htmlFor={key}
                      className="block text-[11px] font-medium uppercase tracking-[0.05em] text-muted-foreground"
                    >
                      {getFieldLabel(key, field)}
                    </label>
                    {renderElement(field, key)}
                    {getFieldHelperText(key, field) ? (
                      <p className="text-[12px] leading-5 text-muted-foreground">
                        {getFieldHelperText(key, field)}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    return renderDefaultFieldGroup(fields, step);
  };

  const renderReviewStep = (step) =>
    selectedSectionEntry?.uiKind === 'version-control-wizard'
      ? renderVersionControlReviewStep(step)
      : isStyledConnectionWizard
        ? renderIssueManagementReviewStep(step)
        : renderDefaultReviewStep(step);

  const renderFieldGroup = (fields, step) => {
    if (step?.sectionType === 'review') {
      return renderReviewStep(step);
    }

    if (isStyledConnectionWizard) {
      return renderIssueManagementFieldGroup(fields, step);
    }

    return renderDefaultFieldGroup(fields, step);
  };

  const renderEnvironmentVersionHierarchyLayout = () => {
    const fields = getSectionVisibleFields(selectedSection);
    const draftSectionKey = selectedSectionEntry?.key || 'rollout-package';
    const environmentsSectionKey = `${draftSectionKey}::environments`;
    const rolloutPackageDraft =
      rolloutPackageVersionDrafts[draftSectionKey] ||
      getDefaultRolloutPackageVersionDraft();
    const environmentField = fields.find(({ key, field }) =>
      isEnvironmentField(key, field)
    );
    const majorVersionField = fields.find(({ key, field }) =>
      isMajorVersionField(key, field)
    );
    const minorVersionField = fields.find(({ key, field }) =>
      isMinorVersionField(key, field)
    );
    const remainingFields = fields.filter(
      ({ key, field }) =>
        !isEnvironmentField(key, field) &&
        !isMajorVersionField(key, field) &&
        !isMinorVersionField(key, field)
    );
    const hasSchemaBackedHierarchy = Boolean(
      environmentField && majorVersionField && minorVersionField
    );
    const updateRolloutPackageDraft = (updater) => {
      setRolloutPackageVersionDrafts((current) => ({
        ...current,
        [draftSectionKey]:
          typeof updater === 'function'
            ? updater(current[draftSectionKey] || getDefaultRolloutPackageVersionDraft())
            : {
                ...(current[draftSectionKey] || getDefaultRolloutPackageVersionDraft()),
                ...updater,
              },
      }));
    };
    const environments = rolloutPackageDraft.environments || [];
    const defaultEnvironmentId = resolveRolloutPackageDefaultEnvironmentId(
      environments,
      rolloutPackageDraft.defaultId
    );
    const selectedEnvironmentId =
      rolloutPackageDraft.selectedId ||
      defaultEnvironmentId ||
      environments[0]?.id ||
      '';
    const selectedEnvironment =
      environments.find((environment) => environment.id === selectedEnvironmentId) ||
      environments[0] ||
      null;
    const selectedSavedVersion = selectedEnvironment
      ? rolloutPackageDraft.versions?.[selectedEnvironment.id] || {}
      : {};
    const selectedDraftVersion = selectedEnvironment
      ? rolloutPackageDraft.drafts?.[selectedEnvironment.id] ||
        createRolloutVersionDraft(selectedSavedVersion)
      : createRolloutVersionDraft();
    const majorVersionValue = String(selectedDraftVersion.major || '').trim();
    const minorVersionValue = String(selectedDraftVersion.minor || '').trim();
    const majorVersionValidation = getSemanticVersionValidation(
      majorVersionValue,
      'mcs-component'
    );
    const minorVersionValidation = getSemanticVersionValidation(
      minorVersionValue,
      'rpcommon'
    );
    const previewValue =
      selectedEnvironment && (majorVersionValue || minorVersionValue)
        ? `${selectedEnvironment.name} v${majorVersionValue || '?'}.${minorVersionValue || '?'}`
        : 'Enter values above to preview';
    const environmentSectionSummary =
      buildEnvironmentSectionSummary(rolloutPackageDraft);

    const syncSchemaBackedFields = (environmentName, versionDraft) => {
      if (!hasSchemaBackedHierarchy) {
        return;
      }

      if (environmentField?.key) {
        setFieldValue(environmentField.key, environmentName);
      }

      if (majorVersionField?.key) {
        setFieldValue(majorVersionField.key, versionDraft.major);
      }

      if (minorVersionField?.key) {
        setFieldValue(minorVersionField.key, versionDraft.minor);
      }
    };
    const handleSelectEnvironment = (environmentId) => {
      const selectedEnvironmentRecord = environments.find(
        (environment) => environment.id === environmentId
      );
      const nextVersionDraft =
        rolloutPackageDraft.drafts?.[environmentId] ||
        createRolloutVersionDraft(rolloutPackageDraft.versions?.[environmentId]);

      updateRolloutPackageDraft((current) => {
        const savedVersion = current.versions?.[environmentId] || {};

        return {
          ...current,
          selectedId: environmentId,
          drafts: {
            ...current.drafts,
            [environmentId]:
              current.drafts?.[environmentId] ||
              createRolloutVersionDraft(savedVersion),
            },
        };
      });

      if (selectedEnvironmentRecord) {
        syncSchemaBackedFields(
          selectedEnvironmentRecord.name,
          nextVersionDraft
        );
      }
    };

    const handleSetDefaultEnvironment = (environmentId) => {
      const defaultEnvironmentRecord = environments.find(
        (environment) => environment.id === environmentId
      );
      const nextVersionDraft =
        rolloutPackageDraft.drafts?.[environmentId] ||
        createRolloutVersionDraft(rolloutPackageDraft.versions?.[environmentId]);

      updateRolloutPackageDraft((current) => {
        const savedVersion = current.versions?.[environmentId] || {};

        return {
          ...current,
          defaultId: environmentId,
          selectedId: environmentId,
          drafts: {
            ...current.drafts,
            [environmentId]:
              current.drafts?.[environmentId] ||
              createRolloutVersionDraft(savedVersion),
          },
        };
      });

      if (defaultEnvironmentRecord) {
        syncSchemaBackedFields(defaultEnvironmentRecord.name, nextVersionDraft);
      }
    };

    const handleAddEnvironment = () => {
      const nextEnvironmentName = String(
        rolloutPackageDraft.newEnvironment || ''
      ).trim();

      if (!nextEnvironmentName) {
        return;
      }

      const existingEnvironment = environments.find(
        (environment) =>
          environment.name.toLowerCase() === nextEnvironmentName.toLowerCase()
      );

      if (existingEnvironment) {
        handleSelectEnvironment(existingEnvironment.id);
        updateRolloutPackageDraft({ newEnvironment: '' });
        return;
      }

      const nextEnvironmentId = `${createRolloutEnvironmentId(
        nextEnvironmentName
      )}-${Date.now().toString(36)}`;

      updateRolloutPackageDraft((current) => ({
        ...current,
        environments: [
          ...current.environments,
          {
            id: nextEnvironmentId,
            name: nextEnvironmentName,
          },
        ],
        defaultId: current.defaultId || nextEnvironmentId,
        selectedId: nextEnvironmentId,
        newEnvironment: '',
        drafts: {
          ...current.drafts,
          [nextEnvironmentId]: createRolloutVersionDraft(),
        },
      }));

      showSuccess(
        'Environment added',
        `${nextEnvironmentName} is ready for version configuration.`
      );
    };

    const updateSelectedVersionDraft = (field, nextValue) => {
      if (!selectedEnvironment) {
        return;
      }

      const nextSelectedVersionDraft = {
        ...selectedDraftVersion,
        [field]: nextValue,
      };

      updateRolloutPackageDraft((current) => {
        const currentVersionDraft =
          current.drafts?.[selectedEnvironment.id] ||
          createRolloutVersionDraft(current.versions?.[selectedEnvironment.id]);

        return {
          ...current,
          drafts: {
            ...current.drafts,
            [selectedEnvironment.id]: {
              ...currentVersionDraft,
              [field]: nextValue,
            },
          },
        };
      });

      syncSchemaBackedFields(
        selectedEnvironment.name,
        createRolloutVersionDraft(nextSelectedVersionDraft)
      );
    };

    return (
      <div className="space-y-7 px-3 pt-4">
        {remainingFields.length ? (
          <div className="space-y-4">
            <div>
              <h4 className="text-[15px] font-semibold text-foreground">
                Additional settings
              </h4>
              <p className="mt-1 text-[12px] leading-5 text-muted-foreground">
                Configure any related rollout package fields here.
              </p>
            </div>
            {renderDefaultFieldGroup(remainingFields, {
              stepRole: 'details',
            })}
            <div className="h-px bg-border/35" />
          </div>
        ) : null}

        <CollapsibleSettingsSection
          title="Environments"
          description="Add environments, choose a default, then configure major and minor versions for each one."
          collapsedSummary={environmentSectionSummary.collapsedSummary}
          badges={environmentSectionSummary.badges}
          icon={Icons.Server}
          isExpanded={isCompactSectionExpanded(environmentsSectionKey)}
          onToggle={() => toggleCompactSection(environmentsSectionKey)}
        >
          <div className="space-y-5">
            <div className="grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_auto]">
              <TextInput
                value={rolloutPackageDraft.newEnvironment || ''}
                onChange={(event) =>
                  updateRolloutPackageDraft({
                    newEnvironment: event.target.value,
                  })
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleAddEnvironment();
                  }
                }}
                placeholder="Environment name (e.g. QA, Dev, UAT...)"
                className="h-11 border-border/25 bg-transparent text-[14px]"
              />
              <ActionButton
                type="button"
                variant="secondary"
                className="min-h-11 rounded-lg border-border/25 bg-transparent px-4 text-[14px] hover:bg-muted/20"
                onClick={handleAddEnvironment}
              >
                <Icons.Plus size={15} />
                Add environment
              </ActionButton>
            </div>

            {environments.length ? (
              <div className="flex flex-wrap gap-2">
                {environments.map((environment) => {
                  const isActive = selectedEnvironment?.id === environment.id;
                  const isDefault = defaultEnvironmentId === environment.id;
                  const savedVersion =
                    rolloutPackageDraft.versions?.[environment.id] || {};
                  const hasDraftVersion = Object.prototype.hasOwnProperty.call(
                    rolloutPackageDraft.drafts || {},
                    environment.id
                  );
                  const displayVersion = hasDraftVersion
                    ? createRolloutVersionDraft(
                        rolloutPackageDraft.drafts?.[environment.id]
                      )
                    : createRolloutVersionDraft(savedVersion);
                  const hasVersion = Boolean(
                    displayVersion.major || displayVersion.minor
                  );

                  return (
                    <div
                      key={environment.id}
                      onClick={() => handleSelectEnvironment(environment.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSelectEnvironment(environment.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className={mergeClasses(
                        'group relative w-[150px] rounded-lg border px-3 py-2.5 text-left transition-colors sm:w-[160px]',
                        isActive
                          ? 'border-primary/35 bg-primary/[0.03]'
                          : 'border-border/20 hover:border-primary/20 hover:bg-muted/[0.08]'
                      )}
                    >
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteEnvironmentDialog({
                            open: true,
                            sectionKey: draftSectionKey,
                            environmentId: environment.id,
                            environmentName: environment.name,
                          });
                        }}
                        className="absolute right-2.5 top-2.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground opacity-0 transition hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                        aria-label={`Delete ${environment.name}`}
                      >
                        <Icons.X size={11} />
                      </button>

                      <div className="text-[8px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        Environment
                      </div>
                      <div className="mt-1.5 pr-8 text-[14px] font-semibold text-foreground">
                        {environment.name}
                      </div>
                      <div className="mt-2.5 space-y-2">
                        {isDefault ? (
                          <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200">
                            <Icons.Star size={11} className="fill-current" />
                            Default
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSetDefaultEnvironment(environment.id);
                            }}
                            className="inline-flex items-center gap-1.5 rounded-full border border-border/30 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground transition hover:border-primary/30 hover:bg-primary/[0.05] hover:text-primary"
                            aria-label={`Set ${environment.name} as default environment`}
                          >
                            <Icons.Star size={11} />
                            Set default
                          </button>
                        )}
                        {hasVersion ? (
                          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                            <Icons.CheckCircle2
                              size={12}
                              className="text-emerald-500"
                            />
                            <span>{`v${displayVersion.major || '?'}.${displayVersion.minor || '?'}`}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/25 px-5 py-10 text-center">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/8 text-primary">
                  <Icons.Server size={20} />
                </div>
                <p className="mt-4 text-[14px] font-medium text-foreground">
                  Add an environment to get started
                </p>
                <p className="mt-2 text-[12px] leading-5 text-muted-foreground">
                  Create environments like Production, QA, or Staging and
                  configure versions for each one separately.
                </p>
              </div>
            )}

            {selectedEnvironment ? (
              <section className="space-y-6 border-t border-border/25 pt-6">
                <div className="px-1 sm:px-2">
                  <div className="flex items-start gap-3">
                    <span className="mt-2 inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
                    <div>
                      <h3 className="text-[1.05rem] font-semibold text-foreground">
                        {selectedEnvironment.name}
                      </h3>
                      <p className="mt-1 text-[13px] leading-6 text-muted-foreground">
                        Configure version numbers below.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-5 px-1 sm:px-2 lg:grid-cols-2">
                  <div className="space-y-3">
                    <label
                      htmlFor={`${draftSectionKey}-major-version`}
                      className="text-[13px] font-medium text-foreground"
                    >
                      mcs-component
                    </label>
                    <TextInput
                      id={`${draftSectionKey}-major-version`}
                      value={majorVersionValue}
                      onChange={(event) =>
                        updateSelectedVersionDraft('major', event.target.value)
                      }
                      placeholder="e.g. 12.2 or 12.max.max"
                      inputMode="text"
                      className="h-12 border-border/25 bg-transparent font-mono"
                    />
                    <p
                      className={mergeClasses(
                        'text-[12px] leading-5',
                        majorVersionValidation.valid
                          ? 'text-emerald-600 dark:text-emerald-300'
                          : majorVersionValue
                            ? 'text-rose-600 dark:text-rose-300'
                            : 'text-muted-foreground'
                      )}
                    >
                      {majorVersionValue
                        ? majorVersionValidation.message
                        : 'Use a dot-separated value such as 12.2 or 12.max.max.'}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label
                      htmlFor={`${draftSectionKey}-minor-version`}
                      className="text-[13px] font-medium text-foreground"
                    >
                      rpcommon
                    </label>
                    <TextInput
                      id={`${draftSectionKey}-minor-version`}
                      value={minorVersionValue}
                      onChange={(event) =>
                        updateSelectedVersionDraft('minor', event.target.value)
                      }
                      placeholder="e.g. 9.0 or 12.max.max"
                      inputMode="text"
                      className="h-12 border-border/25 bg-transparent font-mono"
                    />
                    <p
                      className={mergeClasses(
                        'text-[12px] leading-5',
                        minorVersionValidation.valid
                          ? 'text-emerald-600 dark:text-emerald-300'
                          : minorVersionValue
                            ? 'text-rose-600 dark:text-rose-300'
                            : 'text-muted-foreground'
                      )}
                    >
                      {minorVersionValue
                        ? minorVersionValidation.message
                        : 'Use a dot-separated value such as 9.0 or 12.max.max.'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-border/25 px-1 pt-5 sm:px-2">
                  <div className="text-[12px] leading-5 text-muted-foreground">
                    {selectedEnvironment &&
                    (majorVersionValue || minorVersionValue) ? (
                      <>
                        Preview:{' '}
                        <span className="font-mono text-foreground">
                          {previewValue}
                        </span>
                      </>
                    ) : (
                      previewValue
                    )}
                  </div>
                </div>
              </section>
            ) : null}
          </div>
        </CollapsibleSettingsSection>

        <div className="flex justify-end border-t border-border/25 pt-6">
          <ActionButton
            type="button"
            variant="primary"
            className="rounded-xl px-5"
            onClick={saveConfig}
          >
            <Icons.Save size={15} />
            Save Configuration
          </ActionButton>
        </div>
      </div>
    );
  };

  const isGuidedWizardEntry = (entry) =>
    entry?.uiKind === 'issue-management' ||
    entry?.uiKind === 'version-control-wizard';

  const sectionEntries = sortSectionEntries(
    Object.keys(configuration).map((sectionKey, index) => {
      const section = configuration[sectionKey];
      const uiSchema = resolveSectionUiSchema(sectionKey, section);
      const fullTitle =
        uiSchema.title || section?.Properties?.Name || sectionKey;

      return {
        key: sectionKey,
        index,
        order: uiSchema.order,
        uiKind: uiSchema.kind,
        section,
        title: cleanTitle(fullTitle),
        fullTitle,
        icon: getSectionIcon(
          fullTitle,
          uiSchema.iconName || section?.Properties?.Icon
        ),
        stats: getSectionStats(section, uiSchema.kind),
      };
    })
  );

  const selectedSectionEntry =
    sectionEntries.find((entry) => entry.key === selectedSectionKey) ||
    sectionEntries[0];

  const selectedSection = selectedSectionEntry?.section;
  const sectionSteps = selectedSectionEntry
    ? getSectionSteps(selectedSectionEntry)
    : [];
  const isIssueManagementWizard =
    selectedSectionEntry?.uiKind === 'issue-management';
  const isVersionControlStyledWizard =
    selectedSectionEntry?.uiKind === 'version-control-wizard';
  const isGuidedWizardSection =
    isIssueManagementWizard || isVersionControlStyledWizard;
  const isAuthProvidersSection =
    selectedSectionEntry?.uiKind === 'auth-subsections';
  const authProviderSteps = isAuthProvidersSection
    ? sectionSteps.filter((step) => step.sectionType === 'subsection')
    : [];
  const selectedAuthProviderIndex = isAuthProvidersSection
    ? Math.min(
        (() => {
          const storedIndex = sectionStepIndex[selectedSectionEntry?.key];

          if (
            Number.isInteger(storedIndex) &&
            storedIndex >= 0 &&
            storedIndex < authProviderSteps.length
          ) {
            return storedIndex;
          }

          const firstEnabledIndex = authProviderSteps.findIndex(
            (step) => authMode[step.authKey]
          );

          return firstEnabledIndex >= 0 ? firstEnabledIndex : 0;
        })(),
        Math.max(authProviderSteps.length - 1, 0)
      )
    : 0;
  const selectedAuthProviderStep = isAuthProvidersSection
    ? authProviderSteps[selectedAuthProviderIndex] || null
    : null;
  const isAuthProviderConfigView = isAuthProvidersSection
    ? Boolean(authProviderConfigView[selectedSectionEntry?.key])
    : false;
  const isSectionEditMode = isGuidedWizardSection
    ? Boolean(sectionEditMode[selectedSectionEntry?.key])
    : true;
  const isSystemSplitSection =
    selectedSectionEntry?.uiKind === 'generic-flat' &&
    normalizeText(
      selectedSectionEntry?.fullTitle || selectedSectionEntry?.title || ''
    ) === 'system';
  const isRolloutPackageSplitSection =
    selectedSectionEntry?.uiKind === 'rollout-package-split';
  const isEnvironmentVersionSection = isRolloutPackageSplitSection;
  const usesSplitLayout =
    isSystemSplitSection ||
    isEnvironmentVersionSection ||
    isAuthProvidersSection ||
    isRolloutPackageSplitSection;
  const sectionIntroText = isAuthProvidersSection
    ? 'Enable the providers you need and configure each one independently.'
    : isGuidedWizardSection && !isSectionEditMode
      ? 'Review the current configuration in view mode. Use Edit Configuration to update these settings step by step.'
    : isEnvironmentVersionSection
      ? 'Select an environment first, then configure major and minor versions in order.'
    : isSystemSplitSection || isRolloutPackageSplitSection
      ? 'Configure everything in one place and review the summary before saving.'
      : 'Complete the steps below to finish this setup.';
  const currentVersionControlCategory =
    versionControlCategorySelections[selectedSectionEntry?.key] || '';
  const isStyledConnectionWizard =
    isIssueManagementWizard || isVersionControlStyledWizard;
  const currentProviderValue = getProviderValueForSection(selectedSection);
  const isProviderSelected = Boolean(String(currentProviderValue || '').trim());
  const activeStepIndex = Math.min(
    sectionStepIndex[selectedSectionEntry?.key] || 0,
    Math.max(sectionSteps.length - 1, 0)
  );
  const activeStep = sectionSteps[activeStepIndex];
  const versionControlDetailsStepIndex =
    selectedSectionEntry?.uiKind === 'version-control-wizard'
      ? sectionSteps.findIndex((step) => step.stepRole === 'details')
      : -1;
  const versionControlReviewStepIndex =
    selectedSectionEntry?.uiKind === 'version-control-wizard'
      ? sectionSteps.findIndex((step) => step.sectionType === 'review')
      : -1;
  const recommendedSectionKey =
    sectionEntries.find((entry) =>
      ['database', 'auth', 'system'].some((keyword) =>
        entry.fullTitle.toLowerCase().includes(keyword)
      )
    )?.key || sectionEntries[0]?.key;
  const isFirstTimeState =
    sectionEntries.length > 0 &&
    !sectionEntries.some(
      (entry) => entry.stats.totalFields > 0 && entry.stats.completedFields > 0
    );
  const getStepCompletionState = (step) =>
    step.sectionType === 'subsection'
      ? step.enabled &&
        step.fields.length > 0 &&
        step.completedCount >= step.fields.length
      : step.fields.length > 0 && step.completedCount >= step.fields.length;
  const getStepPipelineState = (step, index) => {
    const isComplete = getStepCompletionState(step);
    const isActive = index === activeStepIndex;
    const isLiveConnectionStep =
      selectedSectionEntry?.uiKind === 'issue-management' &&
      (step?.stepRole === 'provider' || step?.stepRole === 'connection');
    const isOptionalVersionControlStep =
      selectedSectionEntry?.uiKind === 'version-control-wizard' &&
      step?.optional;

    if (isActive) {
      return {
        key: 'active',
        label: isLiveConnectionStep ? 'Connecting...' : 'In progress',
        progressLabel:
          step.fields?.length > 0
            ? `${Math.round((step.completedCount / step.fields.length) * 100)}%`
            : 'Live',
      };
    }

    if (isComplete) {
      return {
        key: 'completed',
        label: isLiveConnectionStep ? 'Connected' : 'Complete',
        progressLabel: '100%',
      };
    }

    return {
      key: 'pending',
      label: isOptionalVersionControlStep ? 'Optional' : 'Waiting',
      progressLabel: isOptionalVersionControlStep ? 'Skip' : '--',
    };
  };
  const isStepAccessible = (stepIndex) => {
    if (selectedSectionEntry?.uiKind === 'version-control-wizard') {
      const selectionState = getVersionControlSelectionState(selectedSection);
      const detailsIndex =
        versionControlDetailsStepIndex >= 0
          ? versionControlDetailsStepIndex
          : Math.max(sectionSteps.length - 2, 0);
      const reviewIndex =
        versionControlReviewStepIndex >= 0
          ? versionControlReviewStepIndex
          : Math.max(sectionSteps.length - 1, 0);
      let maxUnlockedIndex = 0;

      if (selectionState.providerValue) {
        maxUnlockedIndex = detailsIndex;
      }

      if (activeStepIndex >= detailsIndex) {
        maxUnlockedIndex = Math.max(maxUnlockedIndex, reviewIndex);
      }

      return stepIndex <= maxUnlockedIndex;
    }

    if (!isStyledConnectionWizard) {
      return true;
    }

    const providerStepIndex = sectionSteps.findIndex(
      (step) => step.stepRole === 'provider'
    );

    if (providerStepIndex === -1 || stepIndex <= providerStepIndex) {
      return true;
    }

    return isProviderSelected;
  };
  const canContinue =
    activeStepIndex < sectionSteps.length - 1
      ? selectedSectionEntry?.uiKind === 'version-control-wizard'
        ? (() => {
            const selectionState =
              getVersionControlSelectionState(selectedSection);

            if (activeStep?.stepRole === 'provider') {
              return Boolean(selectionState.providerValue);
            }

            return true;
          })()
        : activeStep?.stepRole === 'provider' && isStyledConnectionWizard
          ? isProviderSelected
          : true
      : true;
  const getPreviousStepIndex = () => {
    if (selectedSectionEntry?.uiKind === 'version-control-wizard') {
      const selectionState = getVersionControlSelectionState(selectedSection);
      const providerStepIndex = sectionSteps.findIndex(
        (step) => step.stepRole === 'provider'
      );
      const providerTypeStepIndex = sectionSteps.findIndex(
        (step) => step.stepRole === 'provider-type'
      );
      const apiVersionStepIndex = sectionSteps.findIndex(
        (step) => step.stepRole === 'api-version'
      );

      if (activeStep?.stepRole === 'details') {
        if (selectionState.versionValue && apiVersionStepIndex >= 0) {
          return apiVersionStepIndex;
        }

        if (selectionState.providerTypeValue && providerTypeStepIndex >= 0) {
          return providerTypeStepIndex;
        }

        return providerStepIndex >= 0
          ? providerStepIndex
          : Math.max(activeStepIndex - 1, 0);
      }
    }

    return Math.max(activeStepIndex - 1, 0);
  };

  const setCurrentSectionStep = (nextIndex) => {
    if (!selectedSectionEntry) {
      return;
    }

    if (!isStepAccessible(nextIndex)) {
      return;
    }

    setSectionStepIndex((prevState) => ({
      ...prevState,
      [selectedSectionEntry.key]: nextIndex,
    }));
  };

  const focusAuthProviderConfig = (providerIndex) => {
    setCurrentSectionStep(providerIndex);
  };

  const openAuthProviderConfig = (providerIndex) => {
    if (!selectedSectionEntry?.key) {
      return;
    }

    setCurrentSectionStep(providerIndex);
    setAuthProviderConfigView((prevState) => ({
      ...prevState,
      [selectedSectionEntry.key]: true,
    }));
  };

  const closeAuthProviderConfig = () => {
    if (!selectedSectionEntry?.key) {
      return;
    }

    setAuthProviderConfigView((prevState) => ({
      ...prevState,
      [selectedSectionEntry.key]: false,
    }));
  };

  const renderAuthProvidersDashboard = () => {
    if (!authProviderSteps.length) {
      return (
        <div className="px-2 pt-2">
          <div
            className={mergeClasses(
              FIELD_PANEL_CLASS,
              'grid gap-3 p-4 sm:grid-cols-[auto_1fr]'
            )}
          >
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icons.ShieldCheck size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                No authentication providers available
              </h3>
              <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                Add authentication providers to configure sign-in for this
                application.
              </p>
            </div>
          </div>
        </div>
      );
    }

    const selectedMeta = selectedAuthProviderStep
      ? getAuthProviderMeta(
          selectedAuthProviderStep.authKey,
          selectedAuthProviderStep.title
        )
      : null;

    if (isAuthProviderConfigView && selectedAuthProviderStep) {
      return (
        <div className="space-y-6 px-2 pt-2">
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <AuthProviderLogo
                  authKey={selectedAuthProviderStep.authKey}
                  title={selectedAuthProviderStep.title}
                  className="h-12 w-12"
                />
                <div>
                  <h3 className="text-[1.1rem] font-semibold text-foreground">
                    {selectedMeta?.name || selectedAuthProviderStep.title}
                  </h3>
                  <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                    {selectedMeta?.description ||
                      'Manage this authentication provider configuration.'}
                  </p>
                </div>
              </div>

              <ActionButton onClick={closeAuthProviderConfig}>
                <Icons.ArrowLeft size={16} />
                Back to providers
              </ActionButton>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card/30 p-4">
              <span
                className={mergeClasses(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium',
                  getAuthProviderStatusMeta(
                    Boolean(authMode[selectedAuthProviderStep.authKey])
                  ).className
                )}
              >
                {Boolean(authMode[selectedAuthProviderStep.authKey]) ? (
                  <Icons.Check size={12} />
                ) : (
                  <Icons.Circle size={12} />
                )}
                {Boolean(authMode[selectedAuthProviderStep.authKey])
                  ? 'Enabled'
                  : 'Not configured'}
              </span>
              <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-3 py-2">
                <span className="text-[12px] font-medium text-muted-foreground">
                  Enable
                </span>
                <ToggleSwitch
                  checked={Boolean(authMode[selectedAuthProviderStep.authKey])}
                  onChange={(nextValue) =>
                    toggleAuthMethod(
                      selectedAuthProviderStep.authKey,
                      nextValue
                    )
                  }
                />
              </div>
            </div>

            {!authMode[selectedAuthProviderStep.authKey] ? (
              <div className="rounded-lg border border-border/60 bg-muted/25 px-4 py-3 text-[13px] leading-5 text-muted-foreground">
                This provider is currently disabled. You can configure it now
                and enable it when ready.
              </div>
            ) : null}

            {renderFieldGroup(selectedAuthProviderStep.fields, {
              ...selectedAuthProviderStep,
              unboxedFields: true,
            })}

            <div className="sticky bottom-3 z-10 pt-3">
              <div className="flex flex-wrap items-center justify-end gap-2">
                <ActionButton variant="primary" onClick={saveConfig}>
                  Save Configuration
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6 px-2 pt-2">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {authProviderSteps.map((step, index) => {
            const meta = getAuthProviderMeta(step.authKey, step.title);
            const isEnabled = Boolean(authMode[step.authKey]);
            const isSelected =
              selectedAuthProviderStep?.authKey === step.authKey;
            const statusMeta = getAuthProviderStatusMeta(isEnabled);
            const StatusIcon = statusMeta.icon;

            return (
              <div
                key={step.key}
                onClick={() => focusAuthProviderConfig(index)}
                className={mergeClasses(
                  'relative flex min-h-[270px] flex-col rounded-xl border bg-background/90 p-5 transition duration-200',
                  isEnabled
                    ? 'border-primary/60 bg-primary/[0.03] shadow-[0_16px_38px_-28px_rgba(124,110,247,0.55)]'
                    : 'border-border/60 hover:border-primary/25 hover:bg-background',
                  isSelected && !isEnabled
                    ? 'shadow-[0_16px_38px_-28px_rgba(15,23,42,0.4)] ring-1 ring-primary/25'
                    : ''
                )}
              >
                <div className="space-y-4">
                  <AuthProviderLogo
                    authKey={step.authKey}
                    title={step.title}
                    className="h-14 w-14"
                  />
                  <div className="space-y-1.5">
                    <h4 className="text-[15px] font-semibold text-foreground">
                      {meta.name}
                    </h4>
                    <p className="text-[13px] leading-6 text-muted-foreground">
                      {meta.description}
                    </p>
                  </div>
                  <span
                    className={mergeClasses(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium',
                      statusMeta.className
                    )}
                  >
                    <StatusIcon size={12} />
                    {statusMeta.label}
                  </span>
                </div>

                <div className="mt-auto border-t border-border/60 pt-4">
                  <div
                    className="flex items-center justify-between gap-3"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span className="text-[13px] font-medium text-muted-foreground">
                      Enable
                    </span>
                    <ToggleSwitch
                      checked={isEnabled}
                      onChange={(nextValue) =>
                        toggleAuthMethod(step.authKey, nextValue)
                      }
                    />
                  </div>

                  <div onClick={(event) => event.stopPropagation()}>
                    <ActionButton
                      className="mt-4"
                      onClick={() => openAuthProviderConfig(index)}
                    >
                      <Icons.Settings size={14} />
                      Configure
                    </ActionButton>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSystemSplitLayout = () => {
    const fields = getSectionVisibleFields(selectedSection);
    const osField = fields.find(({ key, field }) =>
      isOsPlatformField(key, field)
    );
    const downloadField = fields.find(({ key, field }) =>
      isDownloadPlatformField(key, field)
    );
    const dateField = fields.find(({ key, field }) =>
      isDateFormatField(key, field)
    );
    const releaseVersionField = fields.find(({ key, field }) =>
      isReleaseVersionField(key, field)
    );
    const groupRolloutsField = fields.find(({ key, field }) =>
      isGroupRolloutsField(key, field)
    );
    const remainingFields = fields.filter(
      ({ key, field }) =>
        !isOsPlatformField(key, field) &&
        !isDownloadPlatformField(key, field) &&
        !isDateFormatField(key, field) &&
        !isReleaseVersionField(key, field) &&
        !isGroupRolloutsField(key, field)
    );

    const renderSystemChoiceGrid = (fieldEntry) => {
      if (!fieldEntry) {
        return null;
      }

      const options = getChoiceOptions(fieldEntry.field);
      const selectedValue = envObj[fieldEntry.key] || options[0] || '';

      return (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {options.map((option) => {
            const meta = getSystemChoiceCardMeta(
              fieldEntry.field?.name || fieldEntry.key,
              option
            );
            const isSelected = selectedValue === option;

            return (
              <button
                key={option}
                type="button"
                onClick={() => setFieldValue(fieldEntry.key, option)}
                className={mergeClasses(
                  'group relative flex min-h-[108px] items-start gap-3.5 rounded-[18px] border bg-background/[0.88] px-4 py-3.5 text-left shadow-[0_10px_24px_-24px_rgba(15,23,42,0.65)] transition-all duration-200 focus:outline-none focus:ring-[3px] focus:ring-ring/18',
                  isSelected
                    ? 'border-primary/35 bg-primary/[0.035] shadow-[0_14px_30px_-26px_rgba(124,110,247,0.38)]'
                    : 'border-border/55 hover:border-primary/18 hover:bg-background/[0.94] hover:shadow-[0_16px_32px_-28px_rgba(15,23,42,0.72)]'
                )}
              >
                <span
                  className={mergeClasses(
                    'mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
                    meta.tileClassName
                  )}
                >
                  <SystemChoiceCardIcon type={meta.iconType} />
                </span>
                <span className="min-w-0 flex-1 space-y-1 pr-7">
                  <span className="block text-[14px] font-medium leading-5 text-foreground">
                    {meta.label}
                  </span>
                  <span className="block text-[12px] leading-5 text-muted-foreground/90">
                    {meta.description}
                  </span>
                </span>
                <span
                  className={mergeClasses(
                    'absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full border shadow-[0_6px_18px_-14px_rgba(15,23,42,0.55)] transition-all duration-200',
                    isSelected
                      ? 'border-primary/30 bg-primary/14 text-primary'
                      : 'border-border/55 bg-background/80 text-transparent group-hover:border-primary/15'
                  )}
                >
                  <Icons.Check
                    size={11}
                    className={mergeClasses(
                      'transition-all duration-200',
                      isSelected
                        ? 'scale-100 opacity-100'
                        : 'scale-75 opacity-0'
                    )}
                  />
                </span>
              </button>
            );
          })}
        </div>
      );
    };

    const getReleaseToggleOptions = () => {
      if (!releaseVersionField) {
        return [];
      }

      if (releaseVersionField.field?.type === 'checkbox') {
        return [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ];
      }

      const options = getChoiceOptions(releaseVersionField.field);

      return options.map((option, index) => ({
        value: option,
        label: index === 0 ? 'Yes' : 'No',
      }));
    };

    const releaseToggleOptions = getReleaseToggleOptions();
    const currentReleaseValue = releaseVersionField
      ? (envObj[releaseVersionField.key] ?? '')
      : '';
    const currentDateFormatValue = dateField
      ? String(envObj[dateField.key] ?? '').trim()
      : '';
    const dateFormatOptions = dateField
      ? getDateFormatPresetOptions(dateField.field)
      : [];
    const normalizedReleaseValue = normalizeText(
      releaseVersionField
        ? releaseVersionField.field?.type === 'checkbox'
          ? currentReleaseValue || 'false'
          : currentReleaseValue || releaseToggleOptions[0]?.value || ''
        : ''
    );
    const isReleaseVersionEnabled = releaseVersionField
      ? normalizedReleaseValue !== 'false' && normalizedReleaseValue !== 'no'
      : false;
    const releaseVersionReviewValue = isReleaseVersionEnabled ? 'Yes' : 'No';
    const groupRolloutsOptions = groupRolloutsField
      ? groupRolloutsField.field?.type === 'checkbox'
        ? ['true', 'false']
        : getChoiceOptions(groupRolloutsField.field)
      : [];
    const currentGroupRolloutsValue = groupRolloutsField
      ? String(
          envObj[groupRolloutsField.key] ??
            groupRolloutsOptions[1] ??
            groupRolloutsOptions[0] ??
            'false'
        )
      : 'false';
    const normalizedGroupRolloutsValue = normalizeText(
      currentGroupRolloutsValue
    );
    const isGroupRolloutsEnabled = ['true', '1', 'yes', 'y'].includes(
      normalizedGroupRolloutsValue
    );
    const groupRolloutsEnabledValue =
      groupRolloutsOptions.find((option) =>
        ['true', '1', 'yes', 'y', 'enabled', 'on'].includes(
          normalizeText(option)
        )
      ) ||
      groupRolloutsOptions[0] ||
      'true';
    const groupRolloutsDisabledValue =
      groupRolloutsOptions.find((option) =>
        ['false', '0', 'no', 'n', 'disabled', 'off'].includes(
          normalizeText(option)
        )
      ) ||
      groupRolloutsOptions[1] ||
      'false';

    return (
      <div className="space-y-5 px-2 pt-2">
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            {osField ? (
              <div className="space-y-2.5">
                <span className={LABEL_CLASS}>OS Platform</span>
                {renderSystemChoiceGrid(osField)}
              </div>
            ) : null}

            {downloadField ? (
              <>
                <div className="h-px bg-border/60" />
                <div className="space-y-2.5">
                  <span className={LABEL_CLASS}>Download Platform</span>
                  {renderSystemChoiceGrid(downloadField)}
                </div>
              </>
            ) : null}
            <div className="h-px bg-border/60" />
            {dateField || releaseVersionField ? (
              <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.9fr)] xl:grid-cols-[minmax(0,1.2fr)_320px]">
                {releaseVersionField ? (
                  <div className="min-w-0 w-full self-start space-y-5 rounded-[22px] border border-border/55 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.015))] p-5 shadow-[0_20px_40px_-32px_rgba(15,23,42,0.85)] backdrop-blur-sm">
                    <div className="flex items-start gap-3.5">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-[16px] border border-primary/15 bg-primary/10 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                        <Icons.BadgeCheck size={16} />
                      </span>
                      <div className="min-w-0 space-y-1.5 pt-0.5">
                        <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/95">
                          Release Version
                        </label>
                        <p className="max-w-[28rem] text-[12px] leading-5 text-muted-foreground">
                          Control whether release labels include a version.
                        </p>
                      </div>
                    </div>

                    <div className="inline-flex w-full rounded-[16px] border border-border/55 bg-[#151214]/80 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                      <div className="grid w-full grid-cols-2 gap-1">
                        {releaseToggleOptions.map((option, index) => {
                          const optionValue = normalizeText(option.value);
                          const isSelected = currentReleaseValue
                            ? normalizedReleaseValue === optionValue
                            : index === 0;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                setFieldValue(
                                  releaseVersionField.key,
                                  option.value
                                )
                              }
                              className={mergeClasses(
                                'flex h-9 min-h-9 items-center justify-center rounded-[12px] px-4 text-[12px] font-semibold transition-all duration-200',
                                isSelected
                                  ? 'bg-[linear-gradient(135deg,rgba(124,110,247,0.98),rgba(104,81,255,0.92))] text-primary-foreground shadow-[0_0_0_1px_rgba(167,139,250,0.18),0_10px_24px_-14px_rgba(124,110,247,0.95)]'
                                  : 'bg-transparent text-[#a1a1aa] hover:bg-white/[0.03] hover:text-foreground'
                              )}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}

                {dateField ? (
                  <div className="min-w-0 w-full h-full space-y-4 rounded-2xl border border-border/60 bg-card/[0.22] p-5 shadow-[0_14px_30px_-24px_rgba(15,23,42,0.65)]">
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary">
                        <Icons.CalendarDays size={18} />
                      </span>
                      <div className="space-y-1">
                        <label
                          htmlFor={dateField.key}
                          className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/90"
                        >
                          Date Format
                        </label>
                        <p className="text-[12px] leading-5 text-muted-foreground">
                          Choose a preset from the dropdown or type a custom date
                          format.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <Icons.CalendarRange size={15} />
                        </span>
                        <TextInput
                          id={dateField.key}
                          name={dateField.key}
                          list={`${dateField.key}-options`}
                          value={currentDateFormatValue}
                          onChange={handleInput}
                          placeholder="e.g. YYYY-MM-DD"
                          className="h-12 rounded-2xl border-border/60 bg-background/80 pl-12 pr-11 text-[13px] font-semibold shadow-none"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-xl bg-background text-muted-foreground">
                          <Icons.ChevronsUpDown size={15} />
                        </span>
                        <datalist id={`${dateField.key}-options`}>
                          {dateFormatOptions.map((option) => (
                            <option key={option} value={option} />
                          ))}
                        </datalist>
                      </div>
                      <p className="text-[12px] leading-5 text-muted-foreground">
                        Supported examples: MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {groupRolloutsField ? (
              <>
                <div className="h-px bg-border/60" />
                <div className="rounded-[22px] border border-border/60 bg-card/[0.22] p-5 shadow-[0_14px_30px_-24px_rgba(15,23,42,0.65)]">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/12 text-primary">
                        <Icons.LayoutList size={18} />
                      </span>
                      <div className="space-y-1">
                        <label
                          htmlFor={groupRolloutsField.key}
                          className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/90"
                        >
                          Group Rollouts
                        </label>
                        <p className="text-[12px] leading-5 text-muted-foreground">
                          Enable or disable grouping of rollouts.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-start rounded-full border border-border/60 bg-background/70 px-3 py-2">
                      <span className="text-[12px] font-medium text-muted-foreground">
                        {isGroupRolloutsEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <ToggleSwitch
                        checked={isGroupRolloutsEnabled}
                        onChange={(nextValue) =>
                          setFieldValue(
                            groupRolloutsField.key,
                            nextValue
                              ? groupRolloutsEnabledValue
                              : groupRolloutsDisabledValue
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {remainingFields.length ? (
              <>
                <div className="h-px bg-border/60" />
                {renderDefaultFieldGroup(remainingFields, {
                  stepRole: 'details',
                })}
              </>
            ) : null}

          </div>

          <div className="xl:sticky xl:top-4 xl:self-start">
            <div className="rounded-xl border border-border/60 bg-card/30 p-5">
              <div className="flex items-center gap-2 text-[1rem] font-semibold text-foreground">
                <Icons.ClipboardCheck size={16} className="text-primary" />
                <span>Review & save</span>
              </div>

              <div className="mt-4 space-y-0">
                <div className="flex items-center justify-between gap-3 border-b border-border/60 py-3 text-[13px]">
                  <span className="text-muted-foreground">OS Platform</span>
                  <span className="font-medium text-foreground">
                    {osField
                      ? getPlatformOptionLabel(
                          envObj[osField.key] ||
                            getChoiceOptions(osField.field)[0] ||
                            ''
                        )
                      : 'Not set'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 border-b border-border/60 py-3 text-[13px]">
                  <span className="text-muted-foreground">Download</span>
                  <span className="font-medium text-foreground">
                    {downloadField
                      ? getPlatformOptionLabel(
                          envObj[downloadField.key] ||
                            getChoiceOptions(downloadField.field)[0] ||
                            ''
                        )
                      : 'Not set'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 border-b border-border/60 py-3 text-[13px]">
                  <span className="text-muted-foreground">Date format</span>
                  <span className="font-medium text-foreground">
                    {dateField
                      ? envObj[dateField.key] ||
                        getChoiceOptions(dateField.field)[0] ||
                        'Not set'
                      : 'Not set'}
                  </span>
                </div>
                <div
                  className={mergeClasses(
                    'flex items-center justify-between gap-3 py-3 text-[13px]',
                    groupRolloutsField ? 'border-b border-border/60' : ''
                  )}
                >
                  <span className="text-muted-foreground">Release version</span>
                  <span className="font-medium text-foreground">
                    {releaseVersionField
                      ? releaseVersionReviewValue
                      : 'Not set'}
                  </span>
                </div>
                {groupRolloutsField ? (
                  <div className="flex items-center justify-between gap-3 py-3 text-[13px]">
                    <span className="text-muted-foreground">
                      Group Rollouts
                    </span>
                    <span className="font-medium text-foreground">
                      {isGroupRolloutsEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                ) : null}
              </div>

              <ActionButton
                variant="primary"
                className="mt-5 w-full justify-center"
                onClick={saveConfig}
              >
                Save Configuration
                <Icons.ArrowUpRight size={15} />
              </ActionButton>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRolloutPackageSplitLayout = () => {
    const fields = getSectionVisibleFields(selectedSection);
    const rolloutNameField = fields.find(({ key, field }) =>
      isRolloutNameField(key, field)
    );
    const yamlField = fields.find(({ key, field }) =>
      isYamlOrderField(key, field)
    );
    const remainingFields = fields.filter(
      ({ key, field }) =>
        !isRolloutNameField(key, field) && !isYamlOrderField(key, field)
    );
    const reviewFields = [rolloutNameField, yamlField, ...remainingFields]
      .filter(Boolean)
      .filter(({ key, field }) => !isPasswordLikeField(key, field));

    const renderSplitFieldCard = (fieldEntry, options = {}) => {
      if (!fieldEntry) {
        return null;
      }

      const { key, field } = fieldEntry;
      const useFramedCard = options.framed !== false;

      return (
        <div
          className={
            useFramedCard
              ? 'space-y-3 rounded-[28px] p-5 sm:p-6'
              : 'space-y-2'
          }
        >
          <label htmlFor={key} className={LABEL_CLASS}>
            {getFieldLabel(key, field)}
          </label>
          {renderElement(field, key)}
          {getFieldHelperText(key, field) ? (
            <div className="pt-0.5 text-[12px] leading-5 text-muted-foreground">
              {getFieldHelperText(key, field)}
            </div>
          ) : null}
        </div>
      );
    };

    return (
      <div className="space-y-7 px-3 pt-3">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px] 2xl:grid-cols-[minmax(0,1fr)_400px]">
          <div className="space-y-6">
            {rolloutNameField || yamlField ? (
              <div className="grid grid-cols-1 items-start gap-6">
                {rolloutNameField
                  ? renderSplitFieldCard(rolloutNameField)
                  : null}
                {yamlField
                  ? renderSplitFieldCard(yamlField)
                  : null}
              </div>
            ) : null}

            {remainingFields.length ? (
              <>
                {rolloutNameField || yamlField ? (
                  <div className="h-px bg-border/60" />
                ) : null}
                {renderDefaultFieldGroup(remainingFields, {
                  stepRole: 'details',
                })}
              </>
            ) : null}
          </div>

          <div className="xl:sticky xl:top-4 xl:self-start">
            <div className="rounded-[28px] p-6">
              <div className="flex items-center gap-2 text-[1rem] font-semibold text-foreground">
                <Icons.ClipboardCheck size={16} className="text-primary" />
                <span>Review & save</span>
              </div>

              <div className="mt-4 space-y-0">
                {reviewFields.map(({ key, field }, index) => (
                  <div
                    key={key}
                    className={mergeClasses(
                      'flex flex-col gap-2 py-4 text-[13px] sm:flex-row sm:items-start sm:justify-between sm:gap-4',
                      index < reviewFields.length - 1
                        ? 'border-b border-border/35'
                        : ''
                    )}
                  >
                    <span className="min-w-[132px] text-muted-foreground">
                      {getFieldLabel(key, field)}
                    </span>
                    <span className="flex-1 whitespace-normal break-words text-left font-medium leading-6 text-foreground sm:text-right">
                      {formatReviewValue(key, field)}
                    </span>
                  </div>
                ))}
              </div>

              <ActionButton
                variant="primary"
                className="mt-5 w-full justify-center"
                onClick={saveConfig}
              >
                Save Configuration
                <Icons.ArrowUpRight size={15} />
              </ActionButton>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGuidedSectionDefaultView = () => {
    const reviewStep =
      sectionSteps.find((step) => step.sectionType === 'review') || null;
    const reviewItems = isIssueManagementWizard
      ? buildIssueManagementReviewItems(reviewStep?.fields || [])
      : buildVersionControlReviewItems(reviewStep?.fields || []);
    const canTestCurrentConnection =
      canTestSectionConnection(selectedSectionEntry);

    return (
      <motion.div
        key={`${selectedSectionEntry?.key || 'section'}-view-mode`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="space-y-5 px-1 pt-1"
      >
        <div className="flex max-w-[820px] flex-wrap items-center justify-end gap-2">
          {canTestCurrentConnection ? (
            <ActionButton
              variant="secondary"
              className="min-h-10 rounded-xl px-4"
              onClick={() => handleTestConnection(selectedSectionEntry)}
              disabled={isTestingConnection}
            >
              <Icons.PlugZap size={15} />
              {isTestingConnection ? 'Testing...' : 'Test Connection'}
            </ActionButton>
          ) : null}
          <ActionButton
            variant="primary"
            className="min-h-10 rounded-xl px-4"
            onClick={() => restartSectionFlow(selectedSectionEntry.key)}
          >
            <Icons.PencilLine size={15} />
            Edit Configuration
          </ActionButton>
        </div>

        <div className="max-w-[820px] overflow-hidden rounded-xl border border-border/60 bg-background/45">
          <div>
            {reviewItems.map((item, index) => {
              return (
                <div
                  key={item.key}
                  className={mergeClasses(
                    'flex items-center justify-between gap-4 px-4 py-3.5',
                    index < reviewItems.length - 1
                      ? 'border-b border-border/50'
                      : ''
                  )}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {item.label}
                  </span>
                  <div className="flex justify-end">
                    {item.providerBrand ? (
                      <span className="text-right text-[13px] font-medium text-foreground/78">
                        <span>{item.value}</span>
                      </span>
                    ) : (
                      <span
                        className={mergeClasses(
                          'text-right text-[13px] font-medium text-foreground',
                          item.mono ? 'font-mono text-primary' : ''
                        )}
                      >
                        {item.badge ? (
                          <span className="inline-flex rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                            {item.value}
                          </span>
                        ) : (
                          item.value
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  };

  const handleSectionSidebarSelect = (sectionKey) => {
    if (sectionKey === selectedSectionKey) {
      return;
    }

    const nextEntry = sectionEntries.find((entry) => entry.key === sectionKey);

    if (selectedSectionKey) {
      setAuthProviderConfigView((prevState) => ({
        ...prevState,
        [selectedSectionKey]: false,
      }));
    }

    setSectionEditMode((prevState) => ({
      ...prevState,
      [sectionKey]: !isGuidedWizardEntry(nextEntry),
    }));
    setSelectedSectionKey(sectionKey);
  };

  const openSectionWizard = (sectionKey) => {
    const nextEntry = sectionEntries.find((entry) => entry.key === sectionKey);

    setAuthProviderConfigView((prevState) => ({
      ...prevState,
      [sectionKey]: false,
    }));
    setSectionEditMode((prevState) => ({
      ...prevState,
      [sectionKey]: !isGuidedWizardEntry(nextEntry),
    }));
    setSelectedSectionKey(sectionKey);
    setIsWizardView(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const restartSectionFlow = (sectionKey) => {
    setAuthProviderConfigView((prevState) => ({
      ...prevState,
      [sectionKey]: false,
    }));
    setSectionEditMode((prevState) => ({
      ...prevState,
      [sectionKey]: true,
    }));
    setSelectedSectionKey(sectionKey);
    setSectionStepIndex((prevState) => ({
      ...prevState,
      [sectionKey]: 0,
    }));
    setIsWizardView(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeSectionWizard = () => {
    if (selectedSectionKey) {
      setAuthProviderConfigView((prevState) => ({
        ...prevState,
        [selectedSectionKey]: false,
      }));
      setSectionEditMode((prevState) => ({
        ...prevState,
        [selectedSectionKey]: false,
      }));
    }
    setIsWizardView(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    fetchConfiguration();
  }, []);

  React.useEffect(() => {
    if (!sectionEntries.length) {
      return;
    }

    if (!selectedSectionKey || !configuration[selectedSectionKey]) {
      setSelectedSectionKey(sectionEntries[0].key);
    }
  }, [configuration, selectedSectionKey, sectionEntries]);

  React.useEffect(() => {
    if (
      !isStyledConnectionWizard ||
      !selectedSection ||
      !currentProviderValue
    ) {
      return;
    }

    const snapshot = getIssueManagementDraftSnapshot(selectedSection);

    setIssueProviderDrafts((prevState) => {
      const previousSnapshot =
        prevState[
          getProviderDraftStorageKey(
            selectedSectionEntry?.key,
            currentProviderValue
          )
        ] || {};
      const keys = Object.keys(snapshot);
      const isSame =
        keys.length === Object.keys(previousSnapshot).length &&
        keys.every((key) => previousSnapshot[key] === snapshot[key]);

      if (isSame) {
        return prevState;
      }

      return {
        ...prevState,
        [getProviderDraftStorageKey(
          selectedSectionEntry?.key,
          currentProviderValue
        )]: snapshot,
      };
    });
  }, [
    currentProviderValue,
    envObj,
    isStyledConnectionWizard,
    selectedSection,
    selectedSectionEntry?.key,
  ]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <React.Suspense fallback={<Loader />}>
        <div className="tw setting-page relative min-w-0 space-y-4">
          {loading && sectionEntries.length > 0 ? <Loader /> : null}

          {loading && !sectionEntries.length ? (
            <SettingsSkeleton />
          ) : (
            <>
              {!isWizardView ? (
                <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {sectionEntries.map((entry) => {
                    const IconComponent = entry.icon;
                    const sectionDisplay = getSectionDisplayState(entry);
                    const statusMeta = sectionDisplay.statusMeta;
                    const isRecommended = entry.key === recommendedSectionKey;
                    const StatusIcon = statusMeta.icon;
                    const configuredSummary =
                      sectionDisplay.totalCount > 0
                        ? `${sectionDisplay.completedCount} of ${sectionDisplay.totalCount} configured`
                        : 'No required fields';

                    return (
                      <article
                        key={entry.key}
                        className={mergeClasses(
                          'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border/50 bg-card/35 px-5 py-5 shadow-[0_18px_48px_-34px_rgba(15,23,42,0.38)] transition duration-200 hover:border-primary/20 hover:bg-card/45',
                          isRecommended && isFirstTimeState
                            ? 'border-dashed'
                            : ''
                        )}
                      >
                        <div className="pointer-events-none absolute right-5 top-0">
                          <span
                            className={mergeClasses(
                              'inline-flex items-center gap-1.5 rounded-b-xl border border-t-0 px-3 py-1 text-[11px] font-semibold',
                              getSectionOverviewStatusClassName(statusMeta.key)
                            )}
                          >
                            <StatusIcon size={13} />
                            {statusMeta.label}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => openSectionWizard(entry.key)}
                          className="flex flex-1 appearance-none flex-col gap-4 rounded-xl border-0 bg-transparent p-0 pt-3 text-left shadow-none outline-none"
                        >
                          <div className="grid grid-cols-[auto_1fr] gap-4">
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-[14px] border border-primary/20 bg-primary/10 text-primary transition duration-300 group-hover:-translate-y-0.5">
                              <IconComponent size={20} />
                            </span>
                            <div className="min-w-0">
                              <h2 className="truncate text-[1.12rem] font-semibold leading-tight tracking-[-0.02em] text-foreground">
                                {entry.title}
                              </h2>
                              <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                                {getSectionOverviewDescription(entry)}
                              </p>
                              {isRecommended && isFirstTimeState ? (
                                <span className="mt-2 inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">
                                  Recommended
                                </span>
                              ) : null}
                            </div>
                          </div>

                          <div className="mt-auto space-y-2">
                            <div className="flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
                              <span className="font-medium">
                                {configuredSummary}
                              </span>
                              <span className="font-semibold">
                                {sectionDisplay.progress}%
                              </span>
                            </div>

                            <div className="h-1 w-full overflow-hidden rounded-full bg-muted/70">
                              <span
                                className="block h-full rounded-full bg-gradient-to-r from-primary via-primary to-violet-300 transition-[width] duration-300"
                                style={{ width: `${sectionDisplay.progress}%` }}
                              />
                            </div>
                          </div>
                        </button>

                        <div className="mt-3 flex items-center gap-2 border-t border-border/35 pt-3">
                          <button
                            type="button"
                            className={SETTINGS_OVERVIEW_VIEW_ACTION_CLASS}
                            onClick={() => openSectionWizard(entry.key)}
                          >
                            <Icons.Eye size={14} />
                            View
                          </button>
                          <button
                            type="button"
                            className={SETTINGS_OVERVIEW_EDIT_ACTION_CLASS}
                            onClick={() => restartSectionFlow(entry.key)}
                          >
                            <Icons.PencilLine size={14} />
                            Edit
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </section>
              ) : null}

              {isWizardView && selectedSectionEntry ? (
                <section className="p-4">
                  <div
                    className={mergeClasses(
                      'space-y-4',
                      sectionEntries.length > 1
                        ? sectionSidebarCollapsed
                          ? 'lg:grid lg:grid-cols-[84px_minmax(0,1fr)] lg:gap-4 lg:space-y-0'
                          : 'lg:grid lg:grid-cols-[264px_minmax(0,1fr)] lg:gap-4 lg:space-y-0'
                        : ''
                    )}
                  >
                    <div
                      className={mergeClasses(
                        INNER_PANEL_CLASS,
                        'space-y-4 p-4',
                        sectionEntries.length > 1 ? 'lg:col-start-2' : ''
                      )}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <selectedSectionEntry.icon size={22} />
                          </span>
                          <div>
                            <h2 className="text-lg font-semibold text-foreground">
                              {selectedSectionEntry.title}
                            </h2>
                            <p className="text-[13px] leading-5 text-muted-foreground">
                              {sectionIntroText}
                            </p>
                          </div>
                        </div>

                        <ActionButton onClick={closeSectionWizard}>
                          <Icons.ArrowLeft size={16} />
                          Back
                        </ActionButton>
                      </div>

                      {!usesSplitLayout &&
                      (!isGuidedWizardSection || isSectionEditMode) ? (
                        <div className="overflow-x-auto pb-1">
                          <div className="flex min-w-[900px] items-start px-1 pt-1">
                            {sectionSteps.map((step, index) => {
                              const pipelineState = getStepPipelineState(
                                step,
                                index
                              );
                              const isActive = pipelineState.key === 'active';
                              const isComplete =
                                pipelineState.key === 'completed';
                              const isAccessible = isStepAccessible(index);
                              const nextStep = sectionSteps[index + 1];
                              const nextPipelineState = nextStep
                                ? getStepPipelineState(nextStep, index + 1)
                                : null;
                              const hasCompletedConnector =
                                index < sectionSteps.length - 1 && isComplete;
                              const hasActiveConnector =
                                index < sectionSteps.length - 1 &&
                                !hasCompletedConnector &&
                                (isActive ||
                                  nextPipelineState?.key === 'active');

                              return (
                                <React.Fragment key={step.key}>
                                  <div className="relative min-w-[170px] flex-1">
                                    {index < sectionSteps.length - 1 ? (
                                      <div className="pointer-events-none absolute left-[calc(50%+40px)] top-[28px] w-[calc(100%-80px)]">
                                        <div className="relative h-[2px] w-full overflow-hidden rounded-full bg-border/60">
                                          {hasCompletedConnector ? (
                                            <motion.span
                                              initial={{
                                                width: 0,
                                                opacity: 0.4,
                                              }}
                                              animate={{
                                                width: '100%',
                                                opacity: 1,
                                              }}
                                              transition={{
                                                duration: 0.45,
                                                ease: 'easeOut',
                                              }}
                                              className="absolute inset-y-0 left-0 rounded-full bg-emerald-400 dark:bg-emerald-500"
                                            />
                                          ) : null}

                                          {hasActiveConnector ? (
                                            <>
                                              <span className="absolute inset-0 rounded-full bg-[#C46437]/12" />
                                              <motion.span
                                                className="absolute inset-y-0 left-[-35%] w-[35%] rounded-full bg-gradient-to-r from-transparent via-[#C46437] to-transparent opacity-95"
                                                animate={{ x: ['0%', '390%'] }}
                                                transition={{
                                                  repeat: Infinity,
                                                  duration: 1.5,
                                                  ease: 'linear',
                                                }}
                                              />
                                              <motion.span
                                                className="absolute inset-y-0 left-[-12%] w-[12%] rounded-full bg-[#C46437]/70 blur-[1px]"
                                                animate={{ x: ['0%', '930%'] }}
                                                transition={{
                                                  repeat: Infinity,
                                                  duration: 1.5,
                                                  ease: 'linear',
                                                }}
                                              />
                                            </>
                                          ) : null}

                                          {!hasCompletedConnector &&
                                          !hasActiveConnector ? (
                                            <span
                                              className="absolute inset-0 opacity-60"
                                              style={{
                                                backgroundImage:
                                                  'repeating-linear-gradient(to right, rgba(148,163,184,0.55) 0 8px, transparent 8px 14px)',
                                              }}
                                            />
                                          ) : null}
                                        </div>
                                      </div>
                                    ) : null}

                                    <button
                                      type="button"
                                      aria-disabled={!isAccessible}
                                      onClick={() => {
                                        if (isAccessible) {
                                          setCurrentSectionStep(index);
                                        }
                                      }}
                                      className={mergeClasses(
                                        'relative z-10 mx-auto flex w-[170px] flex-col items-center gap-3 rounded-xl border border-transparent bg-transparent px-3 py-3 text-center shadow-none outline-none transition-all focus-visible:ring-[3px] focus-visible:ring-ring/20',
                                        isAccessible
                                          ? 'cursor-pointer hover:border-[rgba(196,100,55,0.35)] hover:bg-[#C46437]/[0.03]'
                                          : 'cursor-not-allowed opacity-60',
                                        !isActive && !isComplete
                                          ? 'opacity-75'
                                          : ''
                                      )}
                                    >
                                      <span className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center">
                                        {isActive ? (
                                          <motion.span
                                            className="absolute inset-[-4px] rounded-full border border-[rgba(196,100,55,0.35)]"
                                            animate={{ rotate: 360 }}
                                            transition={{
                                              repeat: Infinity,
                                              duration: 4,
                                              ease: 'linear',
                                            }}
                                          />
                                        ) : null}

                                        <motion.span
                                          initial={false}
                                          animate={
                                            isActive
                                              ? {
                                                  scale: [1, 1.08, 1],
                                                  boxShadow: [
                                                    '0 0 0 0 rgba(196,100,55,0.18)',
                                                    '0 0 0 8px rgba(196,100,55,0.08)',
                                                    '0 0 0 0 rgba(196,100,55,0.18)',
                                                  ],
                                                }
                                              : isComplete
                                                ? { scale: [1, 1.12, 1] }
                                                : { scale: 1 }
                                          }
                                          transition={
                                            isActive
                                              ? {
                                                  repeat: Infinity,
                                                  duration: 2.2,
                                                  ease: 'easeInOut',
                                                }
                                              : {
                                                  duration: 0.45,
                                                  type: 'spring',
                                                  stiffness: 260,
                                                  damping: 18,
                                                }
                                          }
                                          className={mergeClasses(
                                            'inline-flex h-10 w-10 items-center justify-center rounded-full border text-center text-[13px] leading-none font-medium transition-colors',
                                            isActive
                                              ? 'border-[rgba(196,100,55,0.35)] bg-[#C46437]/12 text-[#F0B28C]'
                                              : isComplete
                                                ? 'border-emerald-300 bg-emerald-50 text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-500/12 dark:text-emerald-300'
                                                : 'border-dashed border-border bg-transparent text-muted-foreground'
                                          )}
                                        >
                                          {isComplete ? (
                                            <motion.span
                                              initial={{
                                                scale: 0.65,
                                                opacity: 0,
                                              }}
                                              animate={{ scale: 1, opacity: 1 }}
                                              transition={{
                                                duration: 0.28,
                                                type: 'spring',
                                                stiffness: 320,
                                                damping: 20,
                                              }}
                                            >
                                              <Icons.Check size={15} />
                                            </motion.span>
                                          ) : (
                                            index + 1
                                          )}
                                        </motion.span>
                                        {isActive ? (
                                          <span className="absolute bottom-0 -right-2 inline-flex h-4 w-4 translate-y-1/2 items-center justify-center rounded-full border border-[rgba(196,100,55,0.35)] bg-[#1D1C1B] text-[#F0B28C] shadow-[0_0_10px_rgba(196,100,55,0.18)]">
                                            <Icons.AlertTriangle size={8} />
                                          </span>
                                        ) : null}
                                      </span>

                                      <span className="flex min-w-0 flex-col items-center gap-0.5">
                                        <span
                                          className={mergeClasses(
                                            'text-[10px] uppercase tracking-[0.08em] transition-colors',
                                            isActive
                                              ? 'text-[#F0B28C]'
                                              : isComplete
                                                ? 'text-emerald-600/80 dark:text-emerald-300/80'
                                                : 'text-muted-foreground/80'
                                          )}
                                        >
                                          Step {index + 1}
                                        </span>
                                        <span
                                          className={mergeClasses(
                                            'max-w-[18ch] text-[14px] leading-5 transition-colors',
                                            isActive
                                              ? 'font-semibold text-[#F0B28C]'
                                              : isComplete
                                                ? 'font-medium text-foreground'
                                                : 'font-medium text-muted-foreground'
                                          )}
                                        >
                                          {step.title}
                                        </span>
                                      </span>
                                    </button>
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      ) : isAuthProvidersSection &&
                        !isAuthProviderConfigView ? (
                        <div className="rounded-lg border border-border/60 bg-background/35 px-4 py-4 text-[13px] leading-5 text-muted-foreground">
                          Authentication providers are configured independently.
                          Enable only what you need and jump straight into any
                          provider to edit its settings.
                        </div>
                      ) : null}
                    </div>

                    <div
                      className={mergeClasses(
                        'space-y-4',
                        sectionEntries.length > 1 ? 'lg:contents' : ''
                      )}
                    >
                      {sectionEntries.length > 1 ? (
                        <aside
                          className={mergeClasses(
                            'space-y-2 rounded-lg border border-border/50 bg-card/20 p-2',
                            'lg:col-start-1 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-4 lg:self-start',
                            sectionSidebarCollapsed
                              ? 'lg:w-[84px]'
                              : 'lg:w-[264px]'
                          )}
                        >
                          <div
                            className={mergeClasses(
                              'flex items-center',
                              sectionSidebarCollapsed
                                ? 'justify-center'
                                : 'justify-between gap-3'
                            )}
                          >
                            {!sectionSidebarCollapsed ? (
                              <div>
                                <h4 className="text-[13px] font-semibold text-foreground">
                                  Configuration sections
                                </h4>
                                <p className="text-[11px] text-muted-foreground">
                                  Switch between the main setting cards here.
                                </p>
                              </div>
                            ) : null}

                            <button
                              type="button"
                              onClick={() =>
                                setSectionSidebarCollapsed(
                                  (current) => !current
                                )
                              }
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 bg-background/80 text-muted-foreground transition hover:border-primary/25 hover:text-foreground"
                              aria-label={
                                sectionSidebarCollapsed
                                  ? 'Expand sidebar'
                                  : 'Collapse sidebar'
                              }
                            >
                              {sectionSidebarCollapsed ? (
                                <Icons.PanelLeftOpen size={16} />
                              ) : (
                                <Icons.PanelLeftClose size={16} />
                              )}
                            </button>
                          </div>

                          <div className="space-y-3">
                            {sectionEntries.map((entry) => {
                              const IconComponent = entry.icon;
                              const sectionDisplay = getSectionDisplayState(entry);
                              const statusMeta = sectionDisplay.statusMeta;
                              const isActiveSection =
                                entry.key === selectedSectionKey;

                              return (
                                <button
                                  key={entry.key}
                                  type="button"
                                  onClick={() =>
                                    handleSectionSidebarSelect(entry.key)
                                  }
                                  className={mergeClasses(
                                    'w-full rounded-lg border text-left transition duration-200',
                                    sectionSidebarCollapsed
                                      ? 'flex min-h-[64px] items-center justify-center px-2 py-3'
                                      : 'space-y-2 px-3 py-3',
                                    isActiveSection
                                      ? 'border-primary/35 bg-primary/[0.06]'
                                      : 'border-border/50 bg-card/20 hover:border-primary/20 hover:bg-card/30'
                                  )}
                                >
                                  {sectionSidebarCollapsed ? (
                                    <span
                                      className={mergeClasses(
                                        'inline-flex h-10 w-10 items-center justify-center rounded-lg',
                                        isActiveSection
                                          ? 'bg-primary/12 text-primary shadow-[0_0_0_1px_rgba(124,110,247,0.2)]'
                                          : 'bg-primary/10 text-primary'
                                      )}
                                    >
                                      <IconComponent size={20} />
                                    </span>
                                  ) : (
                                    <>
                                      <div className="flex items-start justify-between gap-3">
                                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                          <IconComponent size={20} />
                                        </span>
                                        <StatusBadge status={statusMeta} />
                                      </div>

                                      <div className="space-y-2">
                                        <div className="text-[15px] font-semibold text-foreground">
                                          {entry.title}
                                        </div>

                                        <div className="flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
                                          <span>{sectionDisplay.summaryLabel}</span>
                                          <span>{sectionDisplay.progress}%</span>
                                        </div>

                                        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                                          <span
                                            className="block h-full rounded-full bg-primary transition-[width] duration-300"
                                            style={{
                                              width: `${sectionDisplay.progress}%`,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </aside>
                      ) : null}

                      <div
                        className={mergeClasses(
                          'min-w-0 space-y-4',
                          sectionEntries.length > 1 ? 'lg:col-start-2' : ''
                        )}
                      >
                        {isGuidedWizardSection && !isSectionEditMode ? (
                          renderGuidedSectionDefaultView()
                        ) : isEnvironmentVersionSection ? (
                          renderEnvironmentVersionHierarchyLayout()
                        ) : isSystemSplitSection ? (
                          renderSystemSplitLayout()
                        ) : isRolloutPackageSplitSection ? (
                          renderRolloutPackageSplitLayout()
                        ) : isAuthProvidersSection ? (
                          renderAuthProvidersDashboard()
                        ) : activeStep ? (
                          <motion.div
                            key={`${selectedSectionEntry.key}-${activeStep.key}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.22, ease: 'easeOut' }}
                            className="space-y-4 px-2 pt-2 transition-opacity duration-200"
                          >
                            <div className="flex flex-wrap items-start gap-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                  <activeStep.icon size={18} />
                                </span>
                                <div>
                                  <h3 className="text-[1.1rem] font-semibold text-foreground">
                                    {activeStep.displayTitle ||
                                      activeStep.title}
                                  </h3>
                                  {activeStep.displayDescription ||
                                  activeStep.description ? (
                                    <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                                      {activeStep.displayDescription ||
                                        activeStep.description}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </div>

                            <div className="space-y-3">
                              {isIssueManagementWizard &&
                              activeStep.stepRole === 'provider' ? (
                                <p className="text-[13px] leading-5 text-muted-foreground">
                                  {isProviderSelected
                                    ? `${currentProviderValue} selected. Continue to the next step.`
                                    : 'Choose a provider to continue.'}
                                </p>
                              ) : null}
                            </div>

                            {activeStep.sectionType === 'subsection' &&
                            activeStep.title !== 'Review & Save' ? (
                              <div
                                className={mergeClasses(
                                  FIELD_PANEL_CLASS,
                                  'flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between'
                                )}
                              >
                                <div>
                                  <span className="block text-[15px] font-semibold text-foreground">
                                    Enable {activeStep.title}
                                  </span>
                                </div>
                                <ToggleSwitch
                                  checked={Boolean(
                                    authMode[activeStep.authKey]
                                  )}
                                  onChange={(nextValue) =>
                                    toggleAuthMethod(
                                      activeStep.authKey,
                                      nextValue
                                    )
                                  }
                                />
                              </div>
                            ) : null}

                            {activeStep.sectionType === 'subsection' &&
                            activeStep.title !== 'Review & Save' &&
                            !authMode[activeStep.authKey] ? (
                              <div
                                className={mergeClasses(
                                  FIELD_PANEL_CLASS,
                                  'grid gap-3 p-4 sm:grid-cols-[auto_1fr]'
                                )}
                              >
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                  <Icons.Lock size={18} />
                                </div>
                                <div>
                                  <h3 className="text-base font-semibold text-foreground">
                                    {activeStep.title} is currently off
                                  </h3>
                                  <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                                    Turn it on to configure this step.
                                  </p>
                                </div>
                              </div>
                            ) : (
                              renderFieldGroup(activeStep.fields, activeStep)
                            )}

                            <div className="sticky bottom-3 z-10 pt-3">
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                <ActionButton
                                  disabled={activeStepIndex === 0}
                                  onClick={() =>
                                    setCurrentSectionStep(
                                      getPreviousStepIndex()
                                    )
                                  }
                                >
                                  Back
                                </ActionButton>
                                {activeStepIndex === sectionSteps.length - 1 ? (
                                  <>
                                    {canTestSectionConnection(
                                      selectedSectionEntry
                                    ) ? (
                                      <ActionButton
                                        onClick={() =>
                                          handleTestConnection(
                                            selectedSectionEntry
                                          )
                                        }
                                        disabled={isTestingConnection}
                                      >
                                        <Icons.PlugZap size={15} />
                                        {isTestingConnection
                                          ? 'Testing...'
                                          : 'Test Connection'}
                                      </ActionButton>
                                    ) : null}
                                    <ActionButton
                                      variant="primary"
                                      onClick={saveConfig}
                                    >
                                      {isStyledConnectionWizard
                                        ? 'Save Configuration'
                                        : 'Save'}
                                    </ActionButton>
                                  </>
                                ) : (
                                  <ActionButton
                                    variant="primary"
                                    disabled={!canContinue}
                                    onClick={() =>
                                      setCurrentSectionStep(activeStepIndex + 1)
                                    }
                                  >
                                    Continue
                                  </ActionButton>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}
            </>
          )}
        </div>

        <Dialog
          open={deleteEnvironmentDialog.open}
          onOpenChange={(open) => {
            setDeleteEnvironmentDialog((current) => ({
              ...current,
              open,
            }));
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete environment</DialogTitle>
            </DialogHeader>

            <p className="text-[13px] leading-6 text-muted-foreground">
              Remove{' '}
              <span className="font-semibold text-foreground">
                {deleteEnvironmentDialog.environmentName || 'this environment'}
              </span>{' '}
              from the rollout package version list? This will also remove its
              saved major and minor versions from the current settings view.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <ActionButton
                type="button"
                variant="secondary"
                onClick={() =>
                  setDeleteEnvironmentDialog({
                    open: false,
                    sectionKey: '',
                    environmentId: '',
                    environmentName: '',
                  })
                }
              >
                Cancel
              </ActionButton>
              <ActionButton
                type="button"
                className="border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleConfirmEnvironmentDelete}
              >
                Delete environment
              </ActionButton>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={feedbackDialog.open}
          onOpenChange={(open) => {
            setFeedbackDialog((current) => ({
              ...current,
              open,
            }));
          }}
        >
          <DialogContent
            className={mergeClasses(
              'max-w-lg gap-0 overflow-hidden rounded-2xl bg-background p-0 shadow-2xl',
              feedbackDialog.tone === 'success'
                ? 'border border-emerald-500/20'
                : 'border border-rose-500/20'
            )}
          >
            <DialogHeader
              className={mergeClasses(
                'border-b border-border/60 px-5 py-4',
                feedbackDialog.tone === 'success'
                  ? 'bg-emerald-500/6'
                  : 'bg-rose-500/6'
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={mergeClasses(
                    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                    feedbackDialog.tone === 'success'
                      ? 'bg-emerald-500/12 text-emerald-600 dark:text-emerald-300'
                      : 'bg-rose-500/12 text-rose-600 dark:text-rose-300'
                  )}
                >
                  {feedbackDialog.tone === 'success' ? (
                    <Icons.CheckCircle2 size={18} />
                  ) : (
                    <Icons.AlertTriangle size={18} />
                  )}
                </span>
                <div className="space-y-1">
                  <DialogTitle className="text-base font-semibold text-foreground">
                    {feedbackDialog.title ||
                      (feedbackDialog.tone === 'success'
                        ? 'Success'
                        : 'Something went wrong')}
                  </DialogTitle>
                  <p className="text-[13px] leading-5 text-muted-foreground">
                    {feedbackDialog.tone === 'success'
                      ? 'Your configuration update completed successfully.'
                      : 'Review the error details below and update the configuration before trying again.'}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 px-5 py-5">
              <div
                className={mergeClasses(
                  'rounded-xl px-4 py-3',
                  feedbackDialog.tone === 'success'
                    ? 'border border-emerald-500/15 bg-emerald-500/6'
                    : 'border border-rose-500/15 bg-rose-500/6'
                )}
              >
                <p className="whitespace-pre-wrap break-words text-[13px] leading-6 text-foreground">
                  {feedbackDialog.message ||
                    (feedbackDialog.tone === 'success'
                      ? 'The action completed successfully.'
                      : 'An unexpected error occurred.')}
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <ActionButton
                  variant="primary"
                  onClick={() =>
                    setFeedbackDialog((current) => ({
                      ...current,
                      open: false,
                    }))
                  }
                >
                  Close
                </ActionButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default Setting;
